import { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState, Suspense, type ComponentType } from "react"
import { createPortal } from "react-dom"
import { useFullscreenHost } from "@vc/viewmodels/screen/useFullscreenHost"
import { useFullscreen, toggleElementFullscreen } from "@vc/viewmodels/screen/useFullscreen"
import { lazyWithReload } from "@/shared/utils/lazy-with-reload"
import { ANIMATION_DURATION } from "@vc/constants/animations"
import { cn } from "@vc/lib/utils"
import { UIStateProvider } from "@vc/contexts/UIStateContext"
import { VideoInputProvider } from "@vc/contexts/VideoInputContext"

import { ControlBar } from "@vc/views/controlBar/ControlBar"
import { MicControl, CameraControl } from "@vc/views/controlBar/components"
import { ShortcutFeedbackOverlay } from "@vc/views/vcLayout/components/overlays/ShortcutFeedbackOverlay"
import { VideoGrid } from "@vc/views/vcLayout/components/video/VideoGrid"
import { RailTiles } from "@vc/views/vcLayout/components/video/RailTiles"
import { ZoomPanVideo } from "@vc/views/vcLayout/components/video/ZoomPanVideo"
import { vclog } from "../hooks/vc-log"
import { zoomReducer, FIT_STATE, type ZoomMetrics } from "../services/screen-share-zoom"
import { useTrackStream } from "../hooks/useTrackStream"
import { seatActiveSpeaker, emptySeating, type Seating } from "../services/grid-order"
import { VideoTile } from "@vc/views/vcLayout/components/video/VideoTile"
import { ScreenShareLayout } from "@vc/views/vcLayout/components/ScreenShareLayout/ScreenShareLayout"
import { MobileDualStream } from "@vc/views/vcLayout/components/MobileDualStream"
import { CaptionsOverlay } from "@vc/views/vcLayout/components/overlays/CaptionsOverlay"
import { useLiveTranscriptionCapture } from "../hooks/useLiveTranscriptionCapture"
import { useLiveCaptions, type LiveCaptionEntry } from "../hooks/useLiveCaptions"
import { RecordingStatusBar } from "@vc/views/vcLayout/components/status/RecordingStatusBar"
import { BroadcastIndicator } from "@vc/views/vcLayout/components/overlays/BroadcastIndicator"
import { RoomNameBadge } from "@vc/views/vcLayout/components/overlays/RoomNameBadge"
import { ReconnectingBanner } from "../components/ReconnectingBanner"
import { useReconnectBanner } from "../hooks/useReconnectBanner"
import { useScreenWakeLock } from "../hooks/useScreenWakeLock"
import { SidePanelTabs } from "@vc/views/panels/SidePanelTabs"
import { MobileOverlayPanel } from "@/clientWeb/components/ui/mobile-overlay-panel"
import { useThrottledResize } from "@vc/hooks/useThrottledResize"
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice"
import { X, ZoomIn, ZoomOut, Copy, Share2, Check, ChevronLeft, ChevronRight, Minimize2 } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  useChat,
  useMeetingDialogs,
  useMeetingLayout,
  useBroadcasting,
  useKeyboardShortcuts,
  usePolls,
  useBreakoutRooms,
  useScreenShare,
  useReactions,
  useBranding,
  useMediaShare,
  usePictureInPicture,
  type PipParticipant,
  COMMON_SHORTCUTS,
  type ShortcutConfig,
} from "@vc/viewmodels"
import type { MeetingInfo, Participant, ParticipantRole } from "@vc/models"
import { hasPermission } from "@vc/services/permissions"
import { useBreakoutSync } from "../hooks/useBreakoutSync"
import { BreakoutJoinDialog } from "@vc/views/controlBar/components/breakout/BreakoutJoinDialog"
import { useMeetingPollsStore } from "../store/meeting-polls.store"
import type { ChatAttachmentKind, ChatChannel, ChatType } from "@/features/chat"
import { uploadChatAttachment, canUploadAttachments } from "@/features/chat"
import { useWhiteboard } from "../hooks/useWhiteboard"
import { useSharedMedia, shareableUrl } from "../hooks/useSharedMedia"
import { useStageContent } from "../hooks/useStageContent"
import { mediaStageKey } from "../services/stage-items"
import { FULLSCREEN_KEY_ATTR } from "../services/fullscreen-focus"
import { StageContentBar } from "../components/StageContentBar"
import { deleteUploadedShare, downloadSharedFile } from "../services/share-upload"
import { documentLink, keepLinkFresh } from "../services/scheduled-file-link"
import { freshJoins } from "../services/stage-sync"
import { moderatorRejoined } from "../services/presenter-reask"
import {
  breakoutCandidates,
  isShareLobbyRow,
  peopleCount,
  pipSpeaker,
  presenterRequestWho,
  withoutShareOnly,
} from "../services/share-only-roster"

/** Inert channel handles for preview renders that have no socket. */
const noopSendEvent = () => {}
const noopSubscribe = () => () => {}
import { LiveStreamingControls } from "@vc/views/controlBar/components/liveStreaming/LiveStreamingControls"
/** Panel-shaped placeholder, sized to the slot the panel is about to fill. */
function PanelFallback() {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3 p-4 animate-pulse" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-10 rounded-lg bg-white/10 dark:bg-white/5" />
      ))}
    </div>
  )
}

/**
 * Give a lazy panel a Suspense boundary of its own.
 *
 * A lazy component suspends the first time it renders, and React shows the
 * NEAREST boundary — which was `SafeSuspense` around the entire meeting, with
 * `fallback={null}`. Opening a panel therefore replaced the video grid, the
 * control bar and everything else with nothing until the chunk arrived: a full
 * white page, once per panel, smooth on every open after that.
 *
 * Wrapping here rather than at the twenty-odd render sites means each panel
 * carries its own boundary wherever it is used, in either layout branch. The
 * meeting stays on screen and the panel slot alone shows a placeholder, however
 * big the chunk or slow the network. `usePrefetchPanels` below then makes the
 * fetch usually finish before anyone clicks, so the placeholder rarely appears
 * at all — but it is no longer what stands between the user and their meeting.
 */
function withPanelSuspense<P extends object>(Lazy: ComponentType<P>): ComponentType<P> {
  return function SuspendedPanel(props: P) {
    return (
      <Suspense fallback={<PanelFallback />}>
        <Lazy {...props} />
      </Suspense>
    )
  }
}

// Lazy load heavy panels
// The shared, framed board — not the old local canvas, which every participant
// had a private copy of. Same slot in every layout branch.
const WhiteboardPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels/SharedWhiteboard").then(m => ({ default: m.SharedWhiteboard }))))
import { DocumentPresentationLayout } from "@vc/views/vcLayout/components/video/DocumentPresentationLayout"
import { VideoShareLayout } from "@vc/views/vcLayout/components/video/VideoShareLayout"
import { Button } from "@/clientWeb/components/ui/button"
const BrandingPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels/BrandingPanel").then(m => ({ default: m.BrandingPanel }))))
const SurveysPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels/SurveysPanel").then(m => ({ default: m.SurveysPanel }))))
import type { Survey } from "@vc/views/panels/SurveysPanel"
const WebinarUsersPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels/WebinarUsersPanel").then(m => ({ default: m.WebinarUsersPanel }))))
const PollsPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels").then(m => ({ default: m.PollsPanel }))))
const VideoSharePanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels").then(m => ({ default: m.VideoSharePanel }))))
const DocumentSharePanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels").then(m => ({ default: m.DocumentSharePanel }))))
const TranscriptionPanel = withPanelSuspense(lazyWithReload(() => import("@vc/views/panels/TranscriptionPanel").then(m => ({ default: m.TranscriptionPanel }))))
import "@vc/views/controlBar/components/reactions/reactionStyles"
import { ShareDialog } from "@/clientWeb/components/ui/ShareDialog"
import { useToast } from "@vc/hooks/useToast"
import { MobileStageLayout } from "@vc/views/vcLayout/components/MobileStageLayout"
import { useParticipantAlerts } from '../hooks/useParticipantAlerts'
import { useMeetingEventAlerts } from '../hooks/useMeetingEventAlerts'
import { meetingAlert } from '../services/meeting-alert'
import { HostRequestDialog } from '@vc/views/dialogs/HostRequestDialog'
import { nextPipSpeaker } from '../services/pip-speaker'
import * as renderedTiles from '../services/rendered-tiles'
import { layerKeyForTile } from '../services/layer-preference'
import { forcedActionChanged, newRecorders, type ForcedMediaState } from '../services/meeting-notices'
import { rosterMedia } from '../services/roster-media'
import { onBackgroundStatus } from '../services/virtual-background'
import { ToastContainer } from "@/clientWeb/components/ui/toast"
import { toast, setToastSink, type ToastSink } from "@/clientWeb/components/ui/toast-utils"
import { useMeetingNoticesStore } from '../store/meeting-notices.store'
import { MeetingNoticeStack } from '../components/MeetingNoticeStack'
import { applyPreferredSpeaker, notifySpeakerRoutingFailure } from "@/features/devices/services/audio-output.service"
import { forgetBlockedAudio, playWithAutoplayRecovery } from "../services/audio-autoplay"
import { JoinRequestsWidget } from "@vc/views/vcLayout/components"
import { useLobby, type LobbyState } from "../hooks/useLobby"
import { useRoomRoles, type RoomRoles } from "../hooks/useRoomRoles"
import { useModeration, type Moderation, type PresenterRequestDetails } from "../hooks/useModeration"
import { useShareAskerDepartures } from "../hooks/useShareAskerDepartures"

/** Stable empty list so the lobby-derived memos don't churn every render. */
const EMPTY_LOBBY_REQUESTS: LobbyState["requests"] = []
import type { SharedVideo } from "@vc/views/panels/VideoSharePanel"
import type { SharedDocument } from "@vc/views/panels/DocumentSharePanel"
import { ErrorBoundary } from "@/clientWeb/components/ui/error-boundary"
import { GridPagination } from "../components/overlays/GridPagination"
import { MeetingScreenDialogs } from "../components/MeetingScreenDialogs"
import { useMeetingPreferencesStore } from "../store/meeting-preferences.store"
import {
  DEFAULT_PARTICIPANT_VOLUME,
  useParticipantVolumeStore,
} from "../store/participant-volume.store"
import { useMeetingSessionStore } from "../store/meeting-session.store"
import { useMeetingConnection, MAX_ADDITIONAL_VIDEO_INPUTS, type LocalVideoInput } from "../hooks/useMeetingConnection"
import { useLocalRecording } from "../hooks/useLocalRecording"
import { useCloudRecording } from "../hooks/useCloudRecording"
import { useRecordingNotice } from "../hooks/useRecordingNotice"
import type { CompositorTile } from "../recording/compositor"
import { useHostPermissionsStore } from "../store/host-permissions.store"
import { usePresenterRequestsStore } from "../store/presenter-requests.store"
import { useAnnotationRequestsStore } from "../store/annotation-requests.store"
import { ScreenShareRequestDialog } from "@vc/views/dialogs"
import { ACCESS_REQUEST_COPY } from "@vc/views/dialogs/ScreenShareRequestDialog"
import { MyRequestChip } from "../components/MyRequestChip"
import { playNotificationSound as playRequestSound } from "@vc/utils/notificationSound"
import type { PermissionRequest } from "@vc/views/vcLayout/components/overlays/JoinRequestsWidget"
import type { PresenterRequestSubject } from "@vc/views/dialogs"
import {
  useMeetingHostControlsStore,
  selectMeetingHostControls,
  type MeetingHostControls,
} from "../store/meeting-host-controls.store"
import { useDeviceGrantStore } from "../store/device-grant.store"
import { canPressDevice } from "../services/device-gate"
import { useMeetingSurveysStore } from "../store/meeting-surveys.store"
import { useMeetingsStore } from "@/features/meetings/store/meetings.store"
import { playNotificationSound } from "@/features/settings"
import { useWebinarStore } from "@/features/webinar/store/webinar.store"
import { TranscriptionConsentBanner } from "../components/banners/TranscriptionConsentBanner"
import { WebinarStartBanner } from "../components/banners/WebinarStartBanner"
import { useDevicesStore } from '@/features/devices'
import { meetingsApi } from '@/features/meetings/services/meetings.api'
import { useLayoutSync } from '../hooks/useLayoutSync'
import { canManageLobby } from '../services/lobby-permission'
import { pollsToPrompt, reconcileQueue, takeNextPrompt } from '../services/poll-prompt-queue'
import { promptedPollIds, rememberPrompted } from '../services/poll-prompt-memory'
import { useMeetingLayoutStore } from '../store/meeting-layout.store'
import { pinForAllBody, readPinSync, unpinForAllBody } from '../services/pin-sync'
import {
  decideWhiteboardClick,
  markRevocationExplained,
  noPresenterHistory,
  observePresenter,
} from '../services/whiteboard-gate'
import { findMeetingByUrlId } from "@/features/meetings/utils/recurrence"
import { useEmbedConnectionEvents } from '@/features/embed/useEmbedConnectionEvents'

/**
 * The distinct chunks the lazy panels above split into.
 *
 * Each of those panels suspends the FIRST time it renders, and the only
 * boundary around them is `SafeSuspense` below, whose fallback is `null` — so
 * opening Whiteboard, Polls, Surveys, Transcription and the rest replaced the
 * WHOLE meeting with nothing until the chunk arrived. That is the white screen
 * before the panel appears, and it happens once per panel: the second open is
 * smooth because the module is already in memory.
 *
 * Fetching them while the browser is idle puts every panel in that
 * already-in-memory state before anyone clicks, so the first open behaves like
 * the second. Wrapping twenty render sites in their own boundaries would be the
 * structural fix; this removes the suspension itself, which is what the user
 * actually sees.
 */
const PANEL_CHUNKS: Array<() => Promise<unknown>> = [
  () => import("@vc/views/panels"),
  () => import("@vc/views/panels/SharedWhiteboard"),
  () => import("@vc/views/panels/BrandingPanel"),
  () => import("@vc/views/panels/SurveysPanel"),
  () => import("@vc/views/panels/WebinarUsersPanel"),
  () => import("@vc/views/panels/TranscriptionPanel"),
]

/** Warm the panel chunks once the meeting has settled. Failures are ignored —
 *  the real import on click keeps `lazyWithReload`'s stale-chunk recovery. */
function usePrefetchPanels() {
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (id: number) => void
    }
    const warm = () => {
      for (const load of PANEL_CHUNKS) void load().catch(() => {})
    }
    const id = w.requestIdleCallback ? w.requestIdleCallback(warm) : window.setTimeout(warm, 1500)
    return () => {
      if (w.cancelIdleCallback) w.cancelIdleCallback(id)
      else window.clearTimeout(id)
    }
  }, [])
}

/** Slot identity of the main stage in sidebar view — the same slot whoever holds it. */
const stageTileKey = () => "stage"

function SafeSuspense({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

/** A share this client started that has not reached the room. */
type LocalPresenting = {
  kind: 'document' | 'video'
  id: string
  name: string
  url: string
  /** videos only — a file the player streams, or an embed it frames. */
  videoKind?: 'file' | 'embed'
}

/**
 * One thing on the stage, whether the room put it there or this client did.
 *
 * `id` carries the `remote-` prefix for anything the room knows about, which
 * is what `document-presenting` strips to match a panel row, and what tells
 * the video player whether to warn that a share never left this machine.
 */
type StageMedia = {
  kind: 'document' | 'video'
  id: string
  name: string
  url: string
  videoKind?: 'file' | 'embed'
  /** So a late joiner is seeked to where the room actually is, not to zero. */
  startedAt?: number
  /** The room's playback was stopped where it stands. */
  paused?: boolean
  /** peerId of whoever is presenting it. */
  by: string | null
  /**
   * The room has never heard of this one — a `blob:` share that could not be
   * uploaded, or the frames before the server echoes a start back. Not
   * inferable from `by`, which is this client's peerId in that case too.
   */
  local?: boolean
}

const REMOTE_MEDIA_PREFIX = 'remote-'

/** How long after a share starts before auto-PiP watches for the user's return. */
const AUTO_PIP_SHARE_SETTLE_MS = 1000

const MeetingScreenInner = memo(function MeetingScreenInner(props: {
  localParticipant: Participant
  participants: Participant[]
  /**
   * Extra camera tiles (own + remote), each `sourceKind: 'additional'`. They are
   * spliced into the layout list only — never into `participants`, which drives
   * the People panel, counts, breakouts and moderation.
   */
  additionalSources?: Participant[]
  /** Dominant speaker's peerId from the SFU, or null before anyone has spoken. */
  activeSpeakerId?: string | null
  /** Live roles from the signaling server. Absent in standalone/preview renders. */
  roles?: RoomRoles
  /** Moderator actions on the wire. Absent in standalone/preview renders. */
  moderation?: Moderation
  /** Room lock + waiting-room queue. Absent in standalone/preview renders. */
  lobby?: LobbyState
  meetingInfo: MeetingInfo
  onLeaveMeeting: (reason?: "left" | "ended" | "removed") => void
  remoteScreen: { peerId: string; name: string; stream: MediaStream } | null
  /** Every remote peer sharing right now, in join order. */
  remoteScreens?: { peerId: string; name: string; stream: MediaStream }[]
  /** Hands the local stop-capture function up, so a moderator's
   *  "stop screenshare" can tear down capture and not just the producers. */
  /** The inner screen's stop; it returns whether a share was actually running. */
  registerStopScreenShare?: (fn: () => boolean) => void
  publishScreenShare: (stream: MediaStream) => Promise<void>
  unpublishScreenShare: () => void
  /** A local share is running that the room is not receiving yet. */
  screenSharePending?: () => boolean
  /** Mic + every remote peer, mixed into the local recording's audio track. */
  recordingAudioStreams?: MediaStream[]
  /** Announce local recording to the room so nobody is recorded silently. */
  onRecordingStateChange?: (isRecording: boolean) => void
  /** A remote participant is recording — show the indicator here too. */
  someoneElseIsRecording?: boolean
  /** Tell the room a cloud recording started or stopped, so late joiners and
   *  everyone else's REC indicator stays right without polling alone. */
  onCloudRecordingChanged?: (active: boolean) => void
  /** Bumped whenever another participant announces a cloud recording
   *  start/stop, so this session knows to re-check the server's status. */
  cloudRecordingSignal?: number
  /** A cloud recording start was announced over the channel and not yet
   *  followed by a stop. Lets a guest who cannot poll the status API still
   *  see the REC indicator. */
  cloudAnnounced?: boolean
  /** Name of whoever announced the running cloud recording, for the toast. */
  cloudStartedBy?: string | null
  /** Tells the outer component whether THIS session controls the cloud
   *  recording that is currently running, so it can re-announce it to
   *  late joiners the same way a local recording is re-announced. */
  onOwnCloudRecordingChange?: (active: boolean) => void
  /** Extra cameras this client is publishing — drives the Add Video Input dialog. */
  localVideoInputs?: LocalVideoInput[]
  publishVideoInput?: (deviceId: string, label: string) => Promise<void>
  unpublishVideoInput?: (sourceId: string) => void
  /**
   * Room-channel handles for in-meeting chat. Absent in standalone/preview
   * renders, where chat is inert rather than pretending to send.
   */
  chatChannel?: ChatChannel | null
  /**
   * The camera stream actually being published — composited when a virtual
   * background is on. Handed to the local tile in that case so the sender sees
   * what the room sees; without it the tile renders MediaStreamContext's
   * separate raw capture and the background is invisible to its owner.
   */
  localStream?: MediaStream | null
  /** Live, multi-peer, server-sourced captions — see useLiveCaptions. */
  liveCaptions?: LiveCaptionEntry[]
  /**
   * The SFU peerId, straight from the connection.
   *
   * Chat identity must not be read off `localParticipant`: that prop is
   * returned verbatim when a caller supplies one, so it can carry an app-level
   * id and no `sfuPeerId` — and any id that isn't the one the server stamps as
   * `from` makes your own messages look like someone else's, counting them as
   * unread and filing your half of a private thread under the wrong key.
   */
  localPeerId?: string | null
}) {
  const t = useTranslation();
  const { meetingInfo, onLeaveMeeting, remoteScreen, remoteScreens = [], registerStopScreenShare, publishScreenShare, unpublishScreenShare, activeSpeakerId, lobby, roles, moderation } = props
  // IDs that the host removed from the meeting in this session.
  const [removedParticipantIds, setRemovedParticipantIds] = useState<Set<string>>(
    () => new Set<string>(),
  )
  // Hidden only until they have actually gone. Removal ends a visit, not the
  // person's access — "Try to Rejoin" brings them back — and an id kept here
  // for good hid the returning participant from this moderator alone: no tile,
  // no row, not counted, while everyone could still hear them.
  useEffect(() => {
    setRemovedParticipantIds((prev) => {
      if (prev.size === 0) return prev
      const present = new Set(props.participants.map((p) => p.id))
      const next = new Set([...prev].filter((id) => present.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [props.participants])

  // No local overrides on top of the roster. Roles were the last thing set
  // here, and an override never expired: the moderator who set one went on
  // seeing it after someone else changed the role or the person rejoined.
  // Roles come from the server's `roles:updated`, which arrives within a
  // round trip of the change.
  const micOn = useMeetingSessionStore((s) => s.micOn)
  const camOn = useMeetingSessionStore((s) => s.camOn)
  const isMyVideoPaused = useMeetingSessionStore((s) => s.isMyVideoPaused)
  const areIncomingVideosPaused = useMeetingSessionStore((s) => s.areIncomingVideosPaused)
  const captionsOn = useMeetingSessionStore((s) => s.captionsOn)
  const handRaised = useMeetingSessionStore((s) => s.handRaised)
  // Headcount — you plus every remote participant still in the room. Read by
  // the layout choice, the join-share popover, "pin for all" and polls.
  //
  // People only: a screen-share session has no tile to lay out, no vote and
  // nobody watching, so it is not counted here. It is listed and counted in
  // the People panel, which reads the roster itself (decision 5).
  //
  // Derived in the same render as the roster, never mirrored into state. It
  // used to be copied from the roster by an effect, which ran after the render
  // that added someone — so for one painted frame the layout was chosen with
  // yesterday's count. With Sidebar or Pinned selected that frame drew the
  // Auto grid, then switched views and remounted every tile.
  const participantCount = useMemo(
    () => peopleCount(props.participants, removedParticipantIds),
    [props.participants, removedParticipantIds],
  )
  const setHasOtherParticipants = useMeetingSessionStore((s) => s.setHasOtherParticipants)
  useEffect(() => {
    setHasOtherParticipants(participantCount > 1)
  }, [participantCount, setHasOtherParticipants])
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null)
  const setFullscreenOn = useMeetingSessionStore((s) => s.setFullscreenOn)
  const fullscreenOn = useMeetingSessionStore((s) => s.fullscreenOn)
  const selectedBackground = useMeetingPreferencesStore((s) => s.meetingBackground)
  // Two genuinely different recordings, not two modes of one.
  //
  // Cloud: the mixer MCU composites the room server-side, writes a WebM,
  // uploads it to S3 and it appears on the recordings page. Survives this tab
  // closing. Host only.
  //
  // Local: composites the grid in a canvas here, mixes real audio, and streams
  // to the user's own disk. Available to anyone, needs no meeting id.
  const recording = useCloudRecording(props.meetingInfo.meetingCode || null)
  const cloudRecording = recording
  const localRecording = useLocalRecording(props.onRecordingStateChange)
  // The hook returns a fresh object every render, so effects that only need
  // to CALL refresh() read it off this ref rather than depending on
  // `recording` itself, which would otherwise re-fire them on every render.
  const recordingRef = useRef(recording)
  recordingRef.current = recording

  // Host-controls permissions — read in the feature layer and passed to the
  // (presentational) control bar and chat panel as props. Individual primitive
  // selectors so referential equality keeps renders stable.
  // A host controls the ATTENDEES, never themselves. Without this exemption
  // closing "Microphone access" greyed out the host's own mic button — the one
  // person the restriction was not aimed at — while the keyboard shortcut for
  // the same action stayed live, because only that path exempted moderators.
  // The server applies the same rule to the two gates it enforces.
  const isRoomModerator = roles?.isModerator ?? false
  /**
   * Live streaming is the HOST's, not every moderator's.
   *
   * Deliberately stricter than isRoomModerator, which is also true for a
   * cohost. The backend allows only the meeting's host to start a stream and
   * answers anyone else with 403, so gating on isModerator would show a cohost
   * a button that always fails. It also matches who the requirement is about:
   * the stream ends when the HOST leaves.
   */
  const isRoomHost = roles?.myRole === 'host'
  const hostGate = (allowed: boolean) => isRoomModerator || allowed

  /**
   * Joined through the meeting's view-only link.
   *
   * Sits OUTSIDE `hostGate`, unlike every other restriction here. The host
   * exemption exists because a moderator should not be able to lock themselves
   * out of their own room — but this is not a restriction the host placed on
   * the room, it is which door the person came through, and a moderator who
   * opens the view-only link is a view-only attendee for as long as they hold
   * that credential. The signaling server takes the same view: it reads the
   * room token, not the role.
   */
  const viewOnly = useMeetingSessionStore((s) => s.viewOnly)

  /**
   * Read the host flags UNCONDITIONALLY, then gate on `viewOnly`.
   *
   * These were written as `!viewOnly && hostGate(useMeetingHostControlsStore(...))`,
   * and `&&` short-circuits — so when `viewOnly` was true the hook was never
   * called at all. Three hooks appearing and disappearing with a piece of state
   * is exactly what React cannot tolerate: the render after `viewOnly` flips
   * sees a different hook count and throws "rendered fewer hooks than expected",
   * taking the whole meeting screen down.
   *
   * Calling them always costs nothing — a store read — and `hostGate` still
   * only runs when the user is not view-only, so the behaviour is unchanged.
   */
  const hostMicrophone = useMeetingHostControlsStore((s) => s.useMicrophone)
  const hostVideo = useMeetingHostControlsStore((s) => s.useVideo)
  const hostScreenShare = useMeetingHostControlsStore((s) => s.shareScreen)
  // A device the host personally let through (an accepted Request Audio or
  // Request Video) stays usable while the room switch is off, and turning a
  // device off is never blocked: see `canPressDevice`.
  const micGranted = useDeviceGrantStore((s) => s.audio)
  const camGranted = useDeviceGrantStore((s) => s.video)
  const hostCanUseMicrophone = canPressDevice({
    viewOnly, isModerator: isRoomModerator, roomAllows: hostMicrophone, granted: micGranted, isOn: micOn,
  })
  const hostCanUseVideo = canPressDevice({
    viewOnly, isModerator: isRoomModerator, roomAllows: hostVideo, granted: camGranted, isOn: camOn,
  })
  // A grant lasts until the host changes the room switch: switching it off
  // mutes everyone (the host's client sends that), switching it on frees
  // everyone, and either way nobody keeps a personal exception.
  const clearGrant = useDeviceGrantStore((s) => s.revoke)
  useEffect(() => { clearGrant('audio') }, [hostMicrophone, clearGrant])
  useEffect(() => { clearGrant('video') }, [hostVideo, clearGrant])
  const hostAllowsScreenShare = !viewOnly && hostGate(hostScreenShare)
  const hostCanUseEmojis = hostGate(useMeetingHostControlsStore((s) => s.useEmojis))
  // NOT gated by `hostGate`, unlike its neighbours. "Chat Feature" turns the
  // chat panel off for the ROOM — it is a feature switch, not a restriction
  // placed on attendees — so a host who switches it off loses the chat button
  // too. Exempting moderators here meant the host kept a chat nobody else had,
  // which is both confusing and the opposite of what the switch says.
  //
  // `sendChat`/`privateChat` keep their exemption: those restrict who may
  // speak, and a moderator who closed group chat still needs to address the
  // room. The server draws the same line — `chat_allowed?/2` exempts
  // moderators, and nothing server-side consults `allow_chat` at all.
  const hostAllowChat = useMeetingHostControlsStore((s) => s.allowChat)
  const hostSendChat = hostGate(useMeetingHostControlsStore((s) => s.sendChat))
  const hostPrivateChat = hostGate(useMeetingHostControlsStore((s) => s.privateChat))
  const hostSendFiles = hostGate(useMeetingHostControlsStore((s) => s.sendFiles))
  // Sharing needs BOTH the host's room-wide toggle and this participant holding
  // presenter. Host and co-host are presenters implicitly; an attendee must be
  // granted it, so by default only hosts can share a screen, whiteboard,
  // document or video. Defaults open when `roles` is absent (preview renders).
  const hostCanShareScreen = hostAllowsScreenShare && (roles?.canPresent ?? true)
  // The share button is ALWAYS rendered now, blocked when it cannot be used, so
  // it behaves like mic and camera. Two different reasons lead there and they
  // are answered differently — one is a switch someone flipped, the other is a
  // capability you can ask for — so the reason has to survive as far as the
  // click handler.
  const screenShareBlockedByHost = !hostAllowsScreenShare
  const screenShareNeedsPresenter = hostAllowsScreenShare && !(roles?.canPresent ?? true)
  // Document and video share need presenter for the same reason a screen does —
  // they put content in front of the whole room. Both were opening their panel
  // to anyone who clicked, so an attendee could take over the stage without
  // holding presenter at all. Neither has a host-level toggle of its own, so
  // presenter is the only gate, and the ask is the same one screen share makes.
  const shareNeedsPresenter = !(roles?.canPresent ?? true)
  const hostCanRaiseHand = hostGate(useHostPermissionsStore((s) => s.allowHandRaise))

  // ── Source-of-truth lookup for this session ────────────────────────────
  // The same `/meeting/:meetingId` route hosts both real meetings and webinars,
  // so the kind (and its settings) are determined by which feature store has
  // the id. Hard-coding these earlier caused banners and the end-call dropdown
  // to misbehave regardless of context.
  const meetingsList = useMeetingsStore((s) => s.meetings)
  const webinarList = useWebinarStore((s) => s.webinars)
  // Either identifier can be in the URL: the dashboard shares the readable
  // 9-digit meetingId while internal navigation carries the record uuid, and
  // the backend resolves both. Matching on `id` alone meant a join by PIN found
  // no record — so no attachments, no transcription flag, no title.
  const meetingRecord = useMemo(
    () =>
      // An exact id first; a series code is shared by all of its occurrences.
      findMeetingByUrlId(meetingsList, meetingInfo.meetingCode),
    [meetingsList, meetingInfo.meetingCode],
  )
  const webinarRecord = useMemo(
    () => webinarList.find((w) => w.id === meetingInfo.meetingCode),
    [webinarList, meetingInfo.meetingCode],
  )
  const isWebinarSession = !!webinarRecord
  const transcriptionEnabledForSession = !!meetingRecord?.settings?.transcriptionEnabled
  // Cloud recording is gated on the host's "Cloud recording" advanced option
  // captured at schedule time. Webinar sessions always allow it (no schedule
  // step in this mock). The flag drives whether the menu item is rendered.
  // Two independent conditions, both required. The host's schedule-time
  // "Cloud recording" option decides whether the feature is offered at all;
  // the hook decides whether this session actually has a meeting to record
  // against. Offering the menu item without the second would render a control
  // that always errors. Regular participants are left out for the same reason:
  // recording is a host/co-host control, and their click only meets a refusal.
  const cloudRecordingAvailable =
    isRoomModerator &&
    (isWebinarSession || !!meetingRecord?.settings?.autoRecord) &&
    cloudRecording.isAvailable
  // Whether THIS user can control the cloud recording that is running, not
  // merely whether one is running. A moderator on the meeting that is not
  // recorded-by-them (someone else started it, or they lost moderator mid
  // meeting) gets the "someone else is recording" chip, not Start/Stop.
  const canControlCloud = cloudRecordingAvailable
  const ownCloudRecording = recording.isRecording && canControlCloud
  // A guest (or anyone whose status fetch is failing) has no `recording.isRecording`
  // of their own, only the channel announcement: fold that in so they still see
  // the recording is running. Control still needs the server's own confirmation,
  // so `ownCloudRecording` is left alone.
  const cloudRunning = recording.isRecording || (props.cloudAnnounced ?? false)
  const othersCloudRecording = cloudRunning && !canControlCloud
  // The ~10s mixer drain happens after this user's own Stop click, not
  // anyone else's: otherwise every Stop control in the room would spin for
  // a click only one person made.
  const cloudStopping = recording.isPending && ownCloudRecording

  // Both lobby surfaces — the "Lobby" pill over the grid and the Participants
  // panel's lobby section — read the same live list. They used to hold separate
  // hardcoded arrays, so admitting only spliced local state and the two views
  // could disagree.
  const lobbyRequests = lobby?.requests ?? EMPTY_LOBBY_REQUESTS
  // A Screen Share page knocking is tagged as one, with the account it came
  // from: the name on it is whatever was typed (contract §8).
  const joinRequests = useMemo(
    () => lobbyRequests.map((r) => ({
      id: r.id,
      name: r.name,
      title: isShareLobbyRow(r)
        ? [t("shareOnly.tag"), r.email].filter(Boolean).join(" · ")
        : t("participantsPanel.wantsToJoin"),
    })),
    [lobbyRequests, t],
  )
  const lobbyUsers = useMemo(
    () => lobbyRequests.map((r) => (isShareLobbyRow(r)
      ? { id: r.id, name: r.name, shareOnly: true, email: r.email || null }
      : { id: r.id, name: r.name })),
    [lobbyRequests],
  )

  // The control bar reads the lock from the session store, but the server owns
  // it. Mirror server truth in so the chip, the menu label, and any other
  // consumer agree — including when another host flips it.
  const setRoomLocked = useMeetingSessionStore((s) => s.setRoomLocked)
  const lobbyLocked = lobby?.locked
  useEffect(() => {
    if (typeof lobbyLocked === 'boolean') setRoomLocked(lobbyLocked)
  }, [lobbyLocked, setRoomLocked])

  const handleToggleRoomLock = useCallback(() => {
    if (!lobby) return
    lobby.setLocked(!lobby.locked)
  }, [lobby])

  // Admission is a server decision — these call the host-only endpoints and let
  // the `lobby:updated` broadcast settle the list for every host in the room.
  // Request = ask the participant to enable their own media. Distinct from the
  // stop actions, which act on them directly.
  const handleRequestAudio = useCallback(
    (id: string) => moderation?.requestAudio(id),
    [moderation],
  )
  const handleRequestVideo = useCallback(
    (id: string) => moderation?.requestVideo(id),
    [moderation],
  )

  const handleAdmitFromLobby = useCallback((id: string) => lobby?.admit(id), [lobby])
  const handleRejectFromLobby = useCallback((id: string) => lobby?.deny(id), [lobby])
  const handleAdmitAllFromLobby = useCallback(() => lobby?.admitAll(), [lobby])

  const broadcasting = useBroadcasting(props.meetingInfo.meetingCode || undefined)

  /**
   * Say when a live stream fails. Nothing did.
   *
   * The store has recorded the reason all along — a rejected stream key, a
   * platform with no ingest configured, an unreachable mixer — and no component
   * read it. `onStartStream` acted only on success (`if (ok) close the dialog`),
   * so a failed Go Live left the host looking at the same form with no idea
   * whether it had been sent, let alone what was wrong with it.
   *
   * Driven off the error itself rather than off the start call, so the two other
   * ways a stream dies silently are covered by the same line: a `stop` that the
   * server refused, and a heartbeat reporting the lease lapsed or the platform
   * dropped the key mid-stream — that last one is the only warning a host gets
   * that they are no longer live.
   *
   * The ref is what stops one failure toasting on every unrelated re-render, and
   * lets the SAME message speak again after a later attempt clears it: `start`
   * sets `error: null` on the way in, so a second identical failure is a
   * genuinely new event.
   */
  const lastBroadcastErrorRef = useRef<string | null>(null)
  useEffect(() => {
    const message = broadcasting.error
    if (!message) {
      lastBroadcastErrorRef.current = null
      return
    }
    if (message === lastBroadcastErrorRef.current) return
    lastBroadcastErrorRef.current = message
    toast.error({ title: t('common.error'), description: message })
  }, [broadcasting.error, t])

  const [meetingStartTime] = useState(() => new Date())
  /**
   * A presentation this client started that the ROOM does not have.
   *
   * Everything the room knows about lives in `sharedMedia.stack`, which is the
   * same list on every client — so what is on the stage is derived from that
   * rather than mirrored into local state. Two cases are not in it, and this
   * covers both: a share whose URL cannot leave the tab (a `blob:` upload that
   * never made it to the server), and the frames between pressing Present and
   * the server echoing the start back.
   *
   * One slot, not a list: this client can only press Present on one thing at a
   * time, and the moment the room echoes it back the stack owns it.
   */
  const [localPresenting, setLocalPresenting] = useState<LocalPresenting | null>(null)
  const autoGridTiles = useMemo(() =>
    Array.from({ length: 4 }).map(() => Math.random() > 0.7), []
  )

  const tiledGridTiles = useMemo(() =>
    Array.from({ length: 18 }).map(() => Math.random() > 0.7), []
  )

  const setSettingsTab = useMeetingPreferencesStore((s) => s.setSettingsTab)
  const [isControlBarVisible, setIsControlBarVisible] = useState(true)
  const permanentBottomBar = useMeetingPreferencesStore((s) => s.permanentBottomBar)
  const autoHideNames = useMeetingPreferencesStore((s) => s.autoHideNames)
  const sideDrawerOverContent = useMeetingPreferencesStore((s) => s.sideDrawerOverContent)
  const hiddenSelfVideo = useMeetingPreferencesStore((s) => s.hiddenSelfVideo)
  const setHiddenSelfVideo = useMeetingPreferencesStore((s) => s.setHiddenSelfVideo)
  const inMeetingNotifications = useMeetingPreferencesStore((s) => s.inMeetingNotifications)
  const inMeetingNotificationSounds = useMeetingPreferencesStore(
    (s) => s.inMeetingNotificationSounds,
  )
  const autoPictureInPicture = useMeetingPreferencesStore((s) => s.autoPictureInPicture)
  const hideChatNotifications = useMeetingPreferencesStore((s) => s.hideChatNotifications)
  // Phones and landscape use the same auto-hide as desktop. While the bar is
  // hidden, a tap on the meeting only brings it back: the catcher below takes
  // that tap and keeps it until the finger lifts, so it never also lands on a
  // tile or its menu underneath.
  const [holdTapCatcher, setHoldTapCatcher] = useState(false)
  // Banner state. Each one tracks "has the user dismissed it?" — the actual
  // *visibility* is derived from the dismissal flag AND the session context
  // when rendering (so a user toggling transcription off the dialog still
  // hides this banner instantly, etc.).
  const [transcriptionBannerDismissed, setTranscriptionBannerDismissed] = useState(false)
  const [webinarBannerDismissed, setWebinarBannerDismissed] = useState(false)
  const [isWebinarLive, setIsWebinarLive] = useState(false)
  const [{ zoomLevel: screenZoomLevel, isAtNaturalSize: screenAtNaturalSize }, dispatchScreenZoom] =
    useReducer(zoomReducer, FIT_STATE)
  const [screenZoomMetrics, setScreenZoomMetrics] = useState<ZoomMetrics | null>(null)
  // The desktop share stage's container, which is what its Fullscreen button
  // fullscreens. It used to fullscreen the <video> itself, and every browser
  // draws its own player controls (pause included) over a fullscreen <video>,
  // while our zoom buttons stayed behind on the page.
  const screenStageRef = useRef<HTMLDivElement>(null)
  const { isFullscreen: screenStageFullscreen, toggle: toggleScreenStageFullscreen } =
    useFullscreen(screenStageRef)
  const [screenSharePiP, setScreenSharePiP] = useState(false)
  // Separate layout-width state from visual open state.
  // panelLayoutOpen snaps TRUE immediately (1 reflow on open), snaps FALSE after 290ms on close.
  // This eliminates continuous layout reflow during the 450ms width animation (CLS root cause).
  const [panelLayoutOpen, setPanelLayoutOpen] = useState(false)
  const panelCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const {
    layoutMode, setLayoutMode, setGridPage, safeGridPage,
    rightRailPage, setRightRailPage,
    setPinnedPage, safePinnedPage, pinnedPageCount,
    pinnedIds, maxTiles, tilesPerPage, tileDensity,
    hideTilesWithoutVideo,
    handlePin, maxTilesPerPage, maxTilesPerPinnedPage,
  } = useMeetingLayout(participantCount)

  const {
    screenOn, screenStream, screenVideoRef, toggleScreenShare: toggleScreenShareRaw, stopScreenShare
  } = useScreenShare(setLayoutMode)

  // Starting a share also opens auto-PiP (see "Auto PiP" below): the click is
  // the one moment the page may open it itself, and a whole-screen or window
  // share never hides this tab, so the browser's own trigger would not fire.
  // Through a ref because that code is declared further down.
  const pipOnShareStartRef = useRef<(started: Promise<boolean>) => void>(() => {})
  const toggleScreenShare = useCallback(() => {
    const starting = !screenOn
    // The picker is requested first, synchronously, inside this same click;
    // the PiP window is asked for straight after it.
    const started = toggleScreenShareRaw()
    if (starting) pipOnShareStartRef.current(started)
  }, [screenOn, toggleScreenShareRaw])

  // Publish the local screen track(s) to the SFU while sharing; tear down on stop.
  // Refs keep the effect's only dependency `screenStream`, so a callback identity
  // change (e.g. on reconnect) doesn't spuriously re-publish.
  const publishScreenRef = useRef(publishScreenShare)
  publishScreenRef.current = publishScreenShare
  // `stopScreenShare` also restores the layout from before the share, so a
  // forced stop puts the presenter back on it instead of a dead stage.
  const screenOnRef = useRef(screenOn)
  screenOnRef.current = screenOn
  useEffect(() => {
    registerStopScreenShare?.(() => {
      const wasSharing = screenOnRef.current
      stopScreenShare()
      return wasSharing
    })
  }, [registerStopScreenShare, stopScreenShare])

  const unpublishScreenRef = useRef(unpublishScreenShare)
  unpublishScreenRef.current = unpublishScreenShare
  const screenSharePendingRef = useRef(props.screenSharePending)
  screenSharePendingRef.current = props.screenSharePending
  useEffect(() => {
    if (!screenStream) return
    // Never rejects for a missing transport: the publisher waits for one and
    // publishes then, and again after a session rebuild.
    publishScreenRef.current(screenStream).catch((err) => {
      vclog('producer', 'screen share publish failed', { error: (err as Error)?.message })
    })
    // The presenter sees their own capture whatever happens, so without this
    // they could present to nobody and never know. Said once, if the room is
    // still not receiving the share a few seconds in.
    const check = window.setTimeout(() => {
      if (screenSharePendingRef.current?.()) {
        toast.warning({
          title: "Others can't see your screen yet",
          description: 'Reconnecting to the meeting — your screen will appear for everyone as soon as it is back.',
        })
      }
    }, 8000)
    return () => {
      window.clearTimeout(check)
      unpublishScreenRef.current()
    }
  }, [screenStream])

  // Unified "presentation" view — whoever is currently sharing (local OR remote).
  // The existing screen-share layout binds whatever stream we attach to
  // `screenVideoRef`, so remote shares reuse the same UI.
  // One list across every live share, local included — your own screen is one
  // of the things being presented, so excluding it meant "you + one other"
  // offered nothing to page between.
  const shares = useMemo(() => {
    const out: { key: string; name: string; stream: MediaStream; isLocal: boolean }[] = []
    if (screenOn && screenStream) {
      out.push({ key: 'local', name: `${meetingInfo.userName} (You)`, stream: screenStream, isLocal: true })
    }
    for (const r of remoteScreens) {
      out.push({ key: r.peerId, name: r.name, stream: r.stream, isLocal: false })
    }
    return out
  }, [screenOn, screenStream, remoteScreens, meetingInfo.userName])

  // Keyed, not indexed: a share ending reshuffles the list, and an index would
  // slide the viewer onto someone else's screen unprompted.
  const [selectedShareKey, setSelectedShareKey] = useState<string | null>(null)
  const activeShareIndex = useMemo(() => {
    if (!shares.length) return -1
    const at = shares.findIndex((sh) => sh.key === selectedShareKey)
    return at === -1 ? 0 : at
  }, [shares, selectedShareKey])
  const activeShare = activeShareIndex >= 0 ? shares[activeShareIndex] : null

  // A different share landing on the stage (or paging back to a previous
  // one) must not keep the last one's zoom — see zoomReducer's shareChanged
  // case. `activeShare.key` is the one identity that is stable across a
  // share's whole lifetime and changes for nothing else.
  useEffect(() => {
    dispatchScreenZoom({ type: 'shareChanged' })
  }, [activeShare?.key])

  // The presenter resized their window/display mid-share: follow it so
  // "100%" stays exactly 100%, but only while already pinned there.
  useEffect(() => {
    if (!screenZoomMetrics || !screenAtNaturalSize) return
    dispatchScreenZoom({ type: 'metricsUpdated', naturalZoom: screenZoomMetrics.naturalZoom })
  }, [screenZoomMetrics, screenAtNaturalSize])

  /**
   * The stage's selection, reachable from here.
   *
   * `stage` is built further down — it needs `shares`, which is built just
   * above — so the arrows cannot call it directly. Assigned there, read here.
   */
  const stageSelectRef = useRef<((key: string) => void) | null>(null)

  const stepShare = useCallback((delta: number) => {
    if (shares.length < 2) return
    const next = (activeShareIndex + delta + shares.length) % shares.length
    const key = shares[next].key
    // Through the stage, not around it. Setting `selectedShareKey` alone was
    // undone on the very next render: the stage -> switcher sync effect below
    // copies `stage.active.key` back over any value that disagrees with it, and
    // the stage had not been told. So the arrows flipped and snapped straight
    // back, which is only visible once a SECOND person shares — the one case
    // the arrows exist for.
    stageSelectRef.current?.(key)
    // Set locally as well rather than waiting for the sync effect, so the press
    // lands in the same frame. The two agree by construction now.
    setSelectedShareKey(key)
  }, [shares, activeShareIndex])

  // `isLocalPresenter` still means "I am sharing" — other logic (PiP, publish
  // effects) depends on that reading — while the stage follows the selection.
  const isLocalPresenter = screenOn && !!screenStream
  const presentationStream = activeShare?.stream ?? screenStream ?? remoteScreen?.stream ?? null
  const isPresenting = shares.length > 0
  const presenterName = activeShare?.name ?? (isLocalPresenter
    ? `${meetingInfo.userName} (You)`
    : (remoteScreen?.name ?? ''))

  // meetingCode is what the polls API resolves (PIN or uuid, either works), and
  // the room channel is where poll:* broadcasts arrive.
  const { polls, pollVotes, promptPollId, setActivePollId } = usePolls({
    meetingId: meetingInfo.meetingCode,
    channel: props.chatChannel ?? null,
  })

  const {
    setBreakoutRoomsOpen,
    setBreakoutRoomsAssignmentOpen,
    activeBreakoutRooms,
    currentBreakoutRoomId, setCurrentBreakoutRoomId,
    breakoutRoomsPanelOpen, setBreakoutRoomsPanelOpen,
    breakoutActive, assignment: breakoutAssignment,
    myAssignedRoomId, invitePromptOpen, setInvitePromptOpen, switching: breakoutSwitching,
    meetingRoster, roomCapacity,
  } = useBreakoutRooms()

  /**
   * The name of the room this client is actually in.
   *
   * Null until breakout rooms exist: until then there is one room, and naming
   * it tells nobody anything. Afterwards every room looks like every other one,
   * so the main room has to be named too — "Main Room" is an answer, and being
   * in it is not the absence of a room.
   */
  const currentRoomName = useMemo(() => {
    if (!breakoutActive) return null
    if (!currentBreakoutRoomId) return t("controlBar.mainRoom")
    return (
      activeBreakoutRooms.find((r) => r.id === currentBreakoutRoomId)?.name ??
      t("controlBar.mainRoom")
    )
  }, [breakoutActive, currentBreakoutRoomId, activeBreakoutRooms, t])

  /**
   * Where a moderator's broadcast is shown.
   *
   * A ref because the toast hook is set up further down this component and the
   * breakout wiring is here; the alternative is reordering two unrelated blocks
   * so one can see the other.
   */
  const breakoutBroadcastRef = useRef<((message: string, from: string) => void) | null>(null)

  // Breakout rooms, joined up: channel events for the room's picture of itself,
  // an API call for actually moving into one. See useBreakoutSync.
  const {
    joinRoom: joinBreakoutRoom,
    leaveRoom: leaveBreakoutRoom,
    openRooms: openBreakoutRooms,
    moveParticipant: moveBreakoutParticipant,
    closeRooms: closeBreakoutRooms,
    broadcastMessage: broadcastBreakoutMessage,
  } = useBreakoutSync({
    onBroadcast: (message, from) => breakoutBroadcastRef.current?.(message, from),
    // Said out loud, because the alternative is a button that appears to do
    // nothing.
    onRoomFull: (roomName: string) =>
      breakoutBroadcastRef.current?.(`${roomName} is full.`, 'Breakout rooms'),
    // Same reason: a command the room refuses is a thing that did not happen,
    // and a moderator staring at a button that changed nothing has no other
    // way to find out.
    onCommandFailed: (message: string) =>
      breakoutBroadcastRef.current?.(message, 'Breakout rooms'),
    onClosed: () => meetingAlert(t('meetingScreen.alertBreakoutClosed'), { personal: true }),
    subscribe: props.chatChannel?.subscribe ?? null,
    sendEvent: props.chatChannel?.sendEvent ?? null,
    request: props.chatChannel?.request ?? null,
    ready: !!props.chatChannel,
    selfId: props.localPeerId ?? null,
    // Names come from the roster; the wire carries ids, because names change.
    nameOf: (id) =>
      props.participants.find((p) => p.id === id)?.name ?? 'Participant',
    meetingId: meetingInfo.meetingCode || null,
  })

  const surveys = useMeetingSurveysStore((s) => s.surveys)
  const createSurvey = useMeetingSurveysStore((s) => s.createSurvey)
  const removeSurvey = useMeetingSurveysStore((s) => s.removeSurvey)
  const updateSurvey = useMeetingSurveysStore((s) => s.updateSurvey)
  const handleEditSurvey = useCallback(
    (survey: Survey) => updateSurvey(survey.id, survey),
    [updateSurvey],
  )

  const {
    logos, backgrounds, overlays,
    videoClips, audioClips,
    addLogo: handleAddLogo, removeLogo: handleDeleteLogo,
    addBackground: handleAddBackground, removeBackground: handleDeleteBackground,
    toggleOverlay: handleToggleOverlay,
    addVideoClip: handleAddVideoClip, removeVideoClip: handleDeleteVideoClip,
    addAudioClip: handleAddAudioClip, removeAudioClip: handleDeleteAudioClip
  } = useBranding()

  const {
    sharedVideos, setSharedVideos, sharedDocuments, setSharedDocuments,
    setRoomId: setMediaShareRoomId
  } = useMediaShare()



  const {
    reactionFor, triggerReaction
  } = useReactions()

  const {
    setSettingsOpen,
    setShortcutsOpen,
    setHostControlsOpen,
    setAddVideoInputOpen,
    whiteboardOpen, setWhiteboardOpen,
    pollsPanelOpen, setPollsPanelOpen,
    setCreatePollOpen,
    setCreateSurveyOpen,
    pollVotingOpen, setPollVotingOpen,
    setPollResultsOpen,
    surveysPanelOpen, setSurveysPanelOpen,
    brandingPanelOpen, setBrandingPanelOpen,
    videoSharePanelOpen, setVideoSharePanelOpen,
    documentSharePanelOpen, setDocumentSharePanelOpen,
    transcriptionPanelOpen, setTranscriptionPanelOpen,
    setBackgroundEffectsOpen,
    liveStreamingOpen, setLiveStreamingOpen,
    setEngagementControlsOpen,
    setEndWebinarDialogOpen,
    setAdjustViewOpen,
    chatOpen, setChatOpen,
    participantsOpen, setParticipantsOpen,
    webinarUsersOpen, setWebinarUsersOpen,
    filesOpen, setFilesOpen,
    meetingInfoPanelOpen, setMeetingInfoPanelOpen,
  } = useMeetingDialogs()

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState("")
  const [showJoinSharePopover, setShowJoinSharePopover] = useState(true)
  const [isJoinSharePopoverVisible, setIsJoinSharePopoverVisible] = useState(false)
  const [joinShareCopied, setJoinShareCopied] = useState(false)

  const meetingShareUrl = useMemo(() => {
    if (typeof window === "undefined") return ""
    // Direct link — lands on the meeting prejoin; a signed-out visitor is
    // admitted as a guest on the "Join" click (VcApp guest flow). No role flag:
    // the route decides from the session, so the link survives being pasted on.
    return `${window.location.origin}/meeting/${meetingInfo.meetingCode}`
  }, [meetingInfo.meetingCode])

  // Selected private chat state (for opening chat from participant panel)
  const [selectedPrivateChat, setSelectedPrivateChat] = useState<string | null>(null)

  // Toast notifications. The two callbacks below honour the in-meeting
  // settings flags — the toast/sound surface is opt-out, so a `false` flag
  // results in a silent no-op rather than a queued-then-discarded toast.
  // Playing the chime alongside each toast lets a single "Notification
  // sounds" toggle govern audible feedback regardless of where the toast
  // originated from.
  const { toasts, showToast, dismissToast, clearToasts } = useToast()

  // Breakout broadcasts land as a toast like any other notice. Deliberately not
  // behind the chat-notification toggle: this is the moderator addressing the
  // room, not chatter.
  breakoutBroadcastRef.current = (message, from) => showToast(message, from || 'Moderator')

  // Chat-originated toast: callers route inbound chat messages through here
  // so the dedicated "hide chat notifications" flag can mute them without
  // killing every other notification surface.
  const handleShowChatToast = useCallback(
    (message: string, senderName: string) => {
      if (!inMeetingNotifications || hideChatNotifications) return
      showToast(message, senderName)
      if (inMeetingNotificationSounds) playNotificationSound('chat')
    },
    [
      showToast,
      inMeetingNotifications,
      hideChatNotifications,
      inMeetingNotificationSounds,
    ],
  )

  const handleOpenShare = useCallback((url: string) => {
    setShareUrl(url)
    setShareDialogOpen(true)
  }, [])

  const handleCopyJoinShareLink = useCallback(async () => {
    if (!meetingShareUrl) return
    try {
      await navigator.clipboard.writeText(meetingShareUrl)
      setJoinShareCopied(true)
      window.setTimeout(() => setJoinShareCopied(false), 1400)
    } catch {
      // Ignore clipboard failure
    }
  }, [meetingShareUrl])

  const handleCloseJoinSharePopover = useCallback(() => {
    setIsJoinSharePopoverVisible(false)
    const timer = window.setTimeout(() => {
      setShowJoinSharePopover(false)
    }, 500)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!showJoinSharePopover) {
      setIsJoinSharePopoverVisible(false)
    }
  }, [showJoinSharePopover])

  useEffect(() => {
    if (!showJoinSharePopover) return
    setIsJoinSharePopoverVisible(false)
    const timer = window.setTimeout(() => {
      setIsJoinSharePopoverVisible(true)
    }, 220)
    return () => window.clearTimeout(timer)
  }, [showJoinSharePopover])

  const rootRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const rightRailEl = useRef<HTMLDivElement | null>(null)
  const [rightRailElState, setRightRailElState] = useState<HTMLDivElement | null>(null)
  const setRightRailEl = useCallback((node: HTMLDivElement | null) => {
    rightRailEl.current = node
    setRightRailElState(node)
  }, [])
  const uiAudioCtxRef = useRef<AudioContext | null>(null)
  const [rightRailWidth, setRightRailWidth] = useState(320)
  const [, setIsHoveringScreen] = useState(false)
  const contentWRef = useRef(0)
  const railDragRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const [rightRailH, setRightRailH] = useState(0)

  // Responsive device detection - combines device type + breakpoints + orientation
  const responsiveDevice = useResponsiveDevice()
  const { shouldUseMobileUI, isLandscape } = responsiveDevice

  // Use shouldUseMobileUI instead of isMobileOrLandscape
  // This ensures mobile devices ALWAYS use mobile UI (portrait or landscape)
  const isMobileOrLandscape = shouldUseMobileUI

  // No automatic full screen on phones. There used to be one here: an effect
  // that asked for it 500 ms after mount. `useMobileFeatures` returns a new
  // object every render, so the effect re-armed after EVERY render — and a
  // browser grants full screen only within a few seconds of a tap. The request
  // failed silently at join, then succeeded off the back of the next unrelated
  // tap: mute the mic on Android Chrome and the meeting went full screen, which
  // also hides the control bar (`fullscreenOn` below). iPhone browsers cannot
  // full-screen a page at all, so only Android ever saw it. Full screen stays
  // one deliberate tap away, in the More menu.

  const playUiBeep = useCallback((type: "add" | "remove") => {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctx) return

      if (!uiAudioCtxRef.current) uiAudioCtxRef.current = new Ctx()
      const ctx = uiAudioCtxRef.current
      if (!ctx) return

      const now = ctx.currentTime
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = "sine"
      osc.frequency.setValueAtTime(type === "add" ? 880 : 520, now)

      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12)

      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(now)
      osc.stop(now + 0.13)
    } catch {
      // ignore
    }
  }, [])


  // Shared throttled resize for right rail height
  useThrottledResize(
    rightRailElState,
    (_w, h) => { if (h) setRightRailH(h) },
    16,
  )

  // Shared throttled resize for content width
  useThrottledResize(
    contentRef,
    (w) => {
      if (!w) return
      contentWRef.current = w
      setRightRailWidth((prev) => {
        if (prev !== 320) return prev
        return Math.round(Math.min(520, Math.max(260, w * 0.3)))
      })
    },
    100,
  )

  // Point a video element at the presented stream.
  //
  // A callback ref rather than an effect alone, because the element is not one
  // element: the stage renders the presented `<video>` from more than
  // one branch, and switching branches unmounts one and mounts another. An
  // effect keyed on the stream does not re-run for that — the stream did not
  // change, only the element did — so the new one came up with no `srcObject`
  // and played black. Attaching on mount means whichever element is currently
  // on screen always has the stream.
  const attachPresentationVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      screenVideoRef.current = el
      // Fullscreening the presentation pauses every remote camera: the key is
      // empty because no camera is on show. See fullscreen-focus.
      el?.setAttribute(FULLSCREEN_KEY_ATTR, '')
      if (!el || !presentationStream) return
      if (el.srcObject !== presentationStream) el.srcObject = presentationStream
      // Mute our OWN screen back to ourselves (no echo); a remote presenter's
      // screen/tab audio should be audible.
      el.muted = isLocalPresenter
      // And audible from the speaker you chose. This element was the one piece
      // of the meeting still playing to the system default: voices are routed
      // per peer and UI sounds through the output helper, but a shared tab's
      // audio came out of the laptop while everything else was in your headset.
      if (!el.muted) applyPreferredSpeaker(el)
      if (el.muted) el.play().catch(() => { })
      else playWithAutoplayRecovery(el, 'the shared screen')
    },
    [presentationStream, isLocalPresenter, screenVideoRef],
  )

  // Still needed for the case the ref callback cannot see: the element stays
  // mounted and the STREAM changes under it (switching between two peers'
  // screens), where the ref does not fire again.
  useEffect(() => {
    if (!presentationStream || !screenVideoRef.current) return
    const el = screenVideoRef.current
    if (el.srcObject !== presentationStream) el.srcObject = presentationStream
    el.muted = isLocalPresenter
    if (!el.muted) applyPreferredSpeaker(el)
    if (el.muted) el.play().catch(() => { })
    else playWithAutoplayRecovery(el, 'the shared screen')
  }, [presentationStream, isLocalPresenter, screenVideoRef])

  // Re-route the presentation when the OUTPUT changes, not only when the stream
  // does. Both attach points above run on a new stream or a new element; picking
  // a different speaker is neither, so without this a shared tab keeps playing
  // out of the device you just moved away from — the same half-working state
  // (voices here, everything else there) the output helper exists to prevent.
  const presentationSpeakerId = useDevicesStore((s) => s.selectedSpeakerId)
  useEffect(() => {
    const el = screenVideoRef.current
    if (!el || el.muted) return
    applyPreferredSpeaker(el)
  }, [presentationSpeakerId, presentationStream, screenVideoRef])

  // Screen sharing functions are now in useScreenShare hook (toggleScreenShare)

  useEffect(() => {
    const onFsChange = () => {
      const isFs = document.fullscreenElement != null
      setFullscreenOn(isFs)
      if (isFs) {
        // Park focus on the meeting root, HERE rather than at the call site.
        //
        // Full screen is usually entered from the More menu, and Radix returns
        // focus to the menu's trigger when the menu closes — which happens
        // after `requestFullscreen()` resolves, so a focus call there is
        // immediately undone. The trigger then sits inside the control bar that
        // full screen collapses, and it handles its own keys: Space reopened
        // the menu instead of engaging push-to-talk.
        //
        // An iframe (a shared document or an embedded player) is worse: it
        // keeps every keystroke, and the shortcuts listen on `window`, so this
        // document hears nothing at all and the keyboard looks dead.
        requestAnimationFrame(() => {
          const active = document.activeElement
          if (active instanceof HTMLIFrameElement) active.blur()
          if (active !== rootRef.current) rootRef.current?.focus?.({ preventScroll: true })
        })
      }
      if (!isFs) {
        setAdjustViewOpen(false)
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'))
        })
      }
    }
    document.addEventListener("fullscreenchange", onFsChange)
    // Also handle webkit fullscreen for Safari
    document.addEventListener("webkitfullscreenchange", onFsChange)
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange)
      document.removeEventListener("webkitfullscreenchange", onFsChange)
    }
  }, [])

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        return
      }
      const el = document.documentElement
      if (!el?.requestFullscreen) return
      await el.requestFullscreen()
      // Take focus off whatever had it before going fullscreen.
      //
      // If that was an embedded player or shared document, its iframe keeps
      // every keystroke — the shortcuts listen on `window`, so this document
      // never hears them and the keyboard looks dead. Entering from the menu
      // leaves focus on the menu's trigger, which is inside the bar we hide.
      const active = document.activeElement
      if (active instanceof HTMLIFrameElement) active.blur()
      rootRef.current?.focus?.({ preventScroll: true })
    } catch {
      // Ignore; fullscreen may fail depending on browser/user gesture.
    }
  }, [])

  // Keyboard shortcuts configuration
  // True only while push-to-talk is holding the mic open, so the release turns
  // off exactly what it turned on.
  const pushToTalkRef = useRef(false)

  // Clicking a control the host has switched off. The button stays clickable
  // precisely so this can run — a dead button reads as a broken app, and the
  // user has no way to know a person decided this.
  // My own ask to present. `myOutcome` is set from two places, because approval
  // and refusal arrive by different routes: a roles rebroadcast granting
  // presenter, and `moderate:deny_presenter`.
  const [presenterRequestOpen, setPresenterRequestOpen] = useState(false)
  // What the pending ask is FOR, so approval resumes that action rather than
  // always starting a screen share.
  const [presenterRequestFor, setPresenterRequestFor] = useState<PresenterRequestSubject>('screenShare')
  const myPresenterOutcome = usePresenterRequestsStore((s) => s.myOutcome)
  const presenterIncoming = usePresenterRequestsStore((s) => s.incoming)
  const annotationIncoming = useAnnotationRequestsStore((s) => s.incoming)
  const setMyPresenterOutcome = usePresenterRequestsStore((s) => s.setMyOutcome)

  // Approval has no event of its own — the grant lands as a roles update, so
  // gaining the capability while a request is open IS the answer.
  // Keyed on having ASKED, not on the dialog being open. Closing the popup is
  // not withdrawing the request — the moderator still has it — so a verdict
  // that lands afterwards has to reach the person who asked. Keying this on
  // `presenterRequestOpen` meant anyone who dismissed the dialog was never told
  // they had been approved.
  const [presenterAsked, setPresenterAsked] = useState(false)
  useEffect(() => {
    if (!presenterAsked) return
    if (roles?.canPresent) {
      setMyPresenterOutcome('approved')
      setPresenterAsked(false)
    }
  }, [presenterAsked, roles?.canPresent, setMyPresenterOutcome])

  // A refusal ends the ask too, or a later unrelated grant would be read as an
  // answer to a question already declined.
  useEffect(() => {
    if (myPresenterOutcome === 'rejected') setPresenterAsked(false)
  }, [myPresenterOutcome])

  // Re-ask a host or co-host who reconnects while the request is still open.
  //
  // The ask only ever lived in the moderators' open pages (see
  // presenter-requests.store), so a host who reloaded before answering it lost
  // the ask while the asker kept showing "Request sent" with nobody left to
  // grant it. Sending it again once a moderator's presence comes back fresh
  // brings the popup back for them, never on a timer; the store already drops
  // a repeat for anyone who never left.
  const presenterRequestForRef = useRef(presenterRequestFor)
  presenterRequestForRef.current = presenterRequestFor
  const rolesRef = useRef(roles)
  rolesRef.current = roles
  const moderationRef = useRef(moderation)
  moderationRef.current = moderation
  // Fresh joiners not yet known as host or co-host. Their role travels in a
  // separate roles update that can land after the presence join, and checking
  // only at the join would then see an attendee and never ask again.
  const awaitingRoleRef = useRef(new Set<string>())
  useEffect(() => {
    awaitingRoleRef.current.clear()
    if (!presenterAsked) return
    const channel = props.chatChannel
    if (!channel) return
    return channel.subscribe('presence_diff', (payload) => {
      const diff = (payload ?? {}) as Parameters<typeof freshJoins>[0]
      const roleOf = (id: string) => rolesRef.current?.roleOf(id) ?? 'attendee'
      const joins = freshJoins(diff)
      if (moderatorRejoined(joins, roleOf, props.localPeerId ?? null)) {
        moderationRef.current?.requestPresenter(presenterRequestForRef.current)
        return
      }
      for (const id of joins) awaitingRoleRef.current.add(id)
    })
  }, [presenterAsked, props.chatChannel, props.localPeerId])
  // The next roles update settles them: ask again if one turned out to be a
  // moderator, and forget the rest either way.
  useEffect(() => {
    const waiting = awaitingRoleRef.current
    if (!presenterAsked || waiting.size === 0) return
    const ids = [...waiting]
    waiting.clear()
    const roleOf = (id: string) => roles?.roleOf(id) ?? 'attendee'
    if (moderatorRejoined(ids, roleOf, props.localPeerId ?? null)) {
      moderationRef.current?.requestPresenter(presenterRequestForRef.current)
    }
  }, [roles, presenterAsked, props.localPeerId])

  // A request lasts as long as the connection that made it. Leaving, reloading
  // or rejoining ends it, and the person asks again from the meeting they are
  // in now: the moderators drop it (below), and the asker's own "request
  // sent" ends with the connection it was sent on.
  useEffect(() => {
    if (!props.chatChannel) setPresenterAsked(false)
  }, [props.chatChannel])
  useEffect(() => {
    const channel = props.chatChannel
    if (!channel) return
    return channel.subscribe('presence_diff', (payload) => {
      const diff = (payload ?? {}) as Parameters<typeof freshJoins>[0]
      // Back on a new connection (reload, rejoin), or gone with nothing coming
      // back in the same diff. A mic, camera or hand change is a leave and a
      // join of the same connection, and drops nothing.
      const ended = new Set(freshJoins(diff))
      for (const id of Object.keys(diff.leaves ?? {})) if (!diff.joins?.[id]) ended.add(id)
      if (ended.size === 0) return
      const presenterAsks = usePresenterRequestsStore.getState()
      const annotationAsks = useAnnotationRequestsStore.getState()
      for (const id of ended) {
        presenterAsks.remove(id)
        annotationAsks.remove(id)
      }
    })
  }, [props.chatChannel])

  // Approval resumes the thing that was asked for. Always starting a screen
  // share would answer a document request with the wrong action.
  const startApprovedShare = () => {
    if (presenterRequestFor === 'document') {
      setDocumentSharePanelOpen(true)
    } else if (presenterRequestFor === 'video') {
      setVideoSharePanelOpen(true)
    } else if (presenterRequestFor === 'whiteboard') {
      // NOT `whiteboard.start()` directly. Between the host approving and this
      // button being pressed the permission can be taken away again, and
      // `start()` answers that by returning `'not_presenter'` and doing nothing.
      handleOpenWhiteboard()
    } else {
      toggleScreenShare()
    }
  }
  // Turned down while the dialog is closed: let them ask again. The answer
  // effect below already says so, and a second toast here doubled it.
  useEffect(() => {
    if (myPresenterOutcome !== 'rejected' || presenterRequestOpen) return
    setMyPresenterOutcome(null)
  }, [myPresenterOutcome, presenterRequestOpen, setMyPresenterOutcome])
  // While an ask waits, the share entries show it and do nothing.
  const pendingRequestLabel = presenterAsked ? t(ACCESS_REQUEST_COPY[presenterRequestFor].waiting) : undefined

  // Requests to present or annotate, for the requests pill (moderators only).
  const permissionRequests = useMemo<PermissionRequest[]>(() => {
    if (!isRoomModerator) return []
    const words = { unknown: t('panels.unknown'), tag: t('shareOnly.tag') }
    return [
      ...presenterIncoming.map((r) => {
        const { who, email } = presenterRequestWho(r, words)
        return { id: r.peerId, name: who, kind: r.subject ?? 'screenShare', email }
      }),
      ...annotationIncoming.map((r) => ({ id: r.peerId, name: r.name || words.unknown, kind: 'annotation' as const })),
    ]
  }, [isRoomModerator, presenterIncoming, annotationIncoming, t])
  // Approving is the grant itself; the roles rebroadcast tells everyone.
  const approvePermissionRequest = useCallback((r: PermissionRequest) => {
    if (r.kind === 'annotation') {
      moderation?.setAnnotator(r.id, true)
      useAnnotationRequestsStore.getState().remove(r.id)
    } else {
      moderation?.setPresenter(r.id, true)
      usePresenterRequestsStore.getState().remove(r.id)
    }
  }, [moderation])
  const rejectPermissionRequest = useCallback((r: PermissionRequest) => {
    if (r.kind === 'annotation') {
      moderation?.denyAnnotator(r.id)
      useAnnotationRequestsStore.getState().remove(r.id)
    } else {
      moderation?.denyPresenter(r.id)
      usePresenterRequestsStore.getState().remove(r.id)
    }
  }, [moderation])
  // A new one plays the request sound once; the pill pulses but stays closed.
  const permissionCountRef = useRef(0)
  useEffect(() => {
    const grew = permissionRequests.length > permissionCountRef.current
    permissionCountRef.current = permissionRequests.length
    if (!grew) return
    const prefs = useMeetingPreferencesStore.getState()
    if (prefs.inMeetingNotifications && prefs.inMeetingNotificationSounds) playRequestSound()
  }, [permissionRequests.length])

  // The same rebroadcast is how OTHER moderators learn the question is settled.
  // Without this, a co-host who did not answer keeps a dialog open for someone
  // who is already presenting.
  const presenterIds = roles?.presenters
  useEffect(() => {
    if (!presenterIds?.length) return
    const { incoming, remove } = usePresenterRequestsStore.getState()
    for (const request of incoming) {
      if (presenterIds.includes(request.peerId)) remove(request.peerId)
    }
  }, [presenterIds])

  // The same for annotation asks another moderator approved.
  const annotatorIds = roles?.annotators
  useEffect(() => {
    if (!annotatorIds?.length) return
    const { incoming, remove } = useAnnotationRequestsStore.getState()
    for (const request of incoming) {
      if (annotatorIds.includes(request.peerId)) remove(request.peerId)
    }
  }, [annotatorIds])

  // Tell the asker the moment the answer lands. Previously these only fired
  // from the dialog's buttons, so an answer that arrived while they were not
  // looking at it passed silently.
  useEffect(() => {
    if (!myPresenterOutcome) return
    if (myPresenterOutcome === 'approved') {
      toast.success({ title: t('dialogs.presenterRequestApproved') })
    } else {
      toast.info({ title: t('dialogs.presenterRequestRejected') })
    }
  }, [myPresenterOutcome, t])

  const handleHostBlocked = useCallback(
    (what: 'microphone' | 'camera' | 'screenShare') => {
      // Screen share has two ways to be blocked. Only one of them is the host,
      // and telling someone the host disabled sharing when they simply lack
      // presenter would send them chasing the wrong person.
      if (what === 'screenShare' && screenShareNeedsPresenter) {
        if (presenterAsked) return
        setPresenterRequestFor('screenShare')
        setPresenterRequestOpen(true)
        return
      }
      const title =
        what === 'microphone'
          ? t('controlBar.microphoneDisabledByHost')
          : what === 'camera'
            ? t('controlBar.cameraDisabledByHost')
            : t('controlBar.screenShareDisabledByHost')
      toast.info({ title })
    },
    [t, screenShareNeedsPresenter],
  )

  const shortcuts = useMemo<ShortcutConfig[]>(() => {
    // Host controls gate the buttons; they have to gate the keyboard too.
    // Without this a host could disable cameras for attendees, watch the button
    // grey out, and an attendee could still turn theirs on with Ctrl+E.
    // Moderators are exempt, the same rule the control bar follows.
    const exempt = roles?.isModerator ?? false
    const allowed = (gate: boolean) => exempt || gate

    return [
    {
      ...COMMON_SHORTCUTS.mute,
      action: () => {
        // Same answer as clicking the button — a shortcut that silently does
        // nothing is the same dead end, just harder to notice.
        if (!allowed(hostCanUseMicrophone)) return handleHostBlocked('microphone')
        useMeetingSessionStore.getState().toggleMic()
      },
    },
    {
      // Advertised in the shortcuts dialog since it was written, and never
      // wired to anything. `moderation` is called directly rather than through
      // `handleMuteAll`, which is defined further down this component — reading
      // it here would be a use-before-declaration on first render.
      ...COMMON_SHORTCUTS.muteAll,
      action: () => {
        // The channel rejects `moderate:*` from non-moderators anyway; this
        // just avoids firing a request we know will be refused.
        if (roles?.isModerator) moderation?.muteAll()
      },
    },
    {
      ...COMMON_SHORTCUTS.video,
      action: () => {
        if (!allowed(hostCanUseVideo)) return handleHostBlocked('camera')
        useMeetingSessionStore.getState().toggleCam()
      },
    },
    {
      ...COMMON_SHORTCUTS.screen,
      action: () => {
        // `hostCanShareScreen` already folds in the presenter capability, so
        // only answer when the HOST is the reason — a non-presenter is not
        // being blocked by a switch and telling them so would be wrong.
        // Both reasons are answerable now, so the shortcut behaves exactly
        // like the button rather than dead-ending on one of them.
        if (!allowed(hostCanShareScreen)) return handleHostBlocked('screenShare')
        toggleScreenShare()
      },
    },
    {
      ...COMMON_SHORTCUTS.hand,
      action: () => {
        if (!allowed(hostCanRaiseHand)) return
        useMeetingSessionStore.getState().toggleHand()
      },
    },
    {
      ...COMMON_SHORTCUTS.chat,
      action: () => {
        // Deliberately not `allowed(...)`: the chat panel is off for the whole
        // room, moderators included, so the shortcut must not reopen for them
        // what the button no longer offers.
        if (!hostAllowChat) return
        setChatOpen((v) => !v)
      },
    },
    {
      ...COMMON_SHORTCUTS.participants,
      action: () => setParticipantsOpen((v) => !v),
    },
    {
      ...COMMON_SHORTCUTS.captions,
      action: () => {
        // Captions exist only where the meeting runs transcription, and the
        // button is not offered elsewhere, so the shortcut must not turn on an
        // overlay that nothing can ever fill.
        if (!transcriptionEnabledForSession) return
        useMeetingSessionStore.getState().toggleCaptions()
      },
    },
    {
      ...COMMON_SHORTCUTS.fullscreen,
      action: () => toggleFullscreen().catch(() => { }),
    },
    {
      // Leaves straight away, exactly as the Leave menu item does — there is no
      // confirmation step in the product to route through. `onLeaveMeeting` is
      // the prop; `handleLeaveMeeting` wraps it further down this component and
      // reading that here would be a use-before-declaration.
      ...COMMON_SHORTCUTS.leave,
      action: () => onLeaveMeeting(),
    },
    {
      // Hold Space to talk, release to go back to muted.
      //
      // Only engages when the mic is already muted, and only un-mutes what it
      // itself muted: holding Space while live must not mute you on release.
      // The hook fires `onRelease` on window blur too, so alt-tabbing mid-hold
      // cannot leave the mic open in another window.
      ...COMMON_SHORTCUTS.pushToTalk,
      action: () => {
        if (!allowed(hostCanUseMicrophone)) return
        const session = useMeetingSessionStore.getState()
        if (session.micOn) return
        pushToTalkRef.current = true
        session.setMicOn(true)
      },
      onRelease: () => {
        if (!pushToTalkRef.current) return
        pushToTalkRef.current = false
        useMeetingSessionStore.getState().setMicOn(false)
      },
    },
    {
      ...COMMON_SHORTCUTS.help,
      action: () => setShortcutsOpen((v) => !v),
    },
    // Layout shortcuts
    {
      key: "1",
      ctrl: true,
      description: "Switch to dynamic layout",
      action: () => setLayoutMode("auto"),
    },
    {
      key: "2",
      ctrl: true,
      description: "Switch to tiled layout",
      action: () => setLayoutMode("tiled"),
    },
    {
      key: "3",
      ctrl: true,
      description: "Switch to spotlight layout",
      action: () => setLayoutMode("spotlight"),
    },
    {
      key: "4",
      ctrl: true,
      description: "Switch to sidebar layout",
      action: () => setLayoutMode("sidebar"),
    },
    ]
  }, [
    toggleScreenShare, toggleFullscreen, setLayoutMode, roles, moderation,
    hostCanUseMicrophone, hostCanUseVideo, hostCanShareScreen, hostCanRaiseHand,
    hostAllowChat, onLeaveMeeting, transcriptionEnabledForSession,
  ])

  // Initialize keyboard shortcuts
  useKeyboardShortcuts(shortcuts)

  // CRITICAL OPTIMIZATION: Memoize participants to prevent grid re-renders
  //
  // The roster is whoever the connection reports, and nobody else. This used to
  // fall back to a generated list of stand-in names whenever `roomCredentials`
  // was absent, on the theory that the only way to be without credentials was
  // to be running with no server. That is not the only way: leaving a meeting
  // clears the credentials, so anyone who was removed by the host and then
  // pressed "Try to rejoin" landed in a room populated entirely by invented
  // people. Names that convincing are worse than an empty grid — there is no
  // way to tell, from inside the meeting, that the room is fiction.
  //
  // An empty roster while connecting is the honest state and renders fine.
  const allParticipants = useMemo<Participant[]>(
    () => {
      const local: Participant = {
        ...props.localParticipant,
        muted: !micOn,
        videoOn: camOn && !isMyVideoPaused,
        cameraOn: camOn,
        handRaised,
        // Inherited from `props.localParticipant` (see the note there) instead of
        // being re-derived from `micOn`, which would reinstate the always-on
        // speaking state this screen's own tile animation keys off.
        // Only when a background is on. An explicit `stream` overrides
        // MediaStreamContext, and without a background there is no reason to
        // change where the tile gets its picture.
        ...(selectedBackground && props.localStream
          ? { stream: props.localStream }
          : {}),
      }
      const merged = local

      const remotes = props.participants
        .filter((p) => !removedParticipantIds.has(p.id))
        .map((p) => {
          const base: Participant = {
            ...p,
            videoOn: !areIncomingVideosPaused && p.videoOn,
            // Kept separate from `videoOn` above, which this client's own
            // "pause incoming videos" preference forces false for everyone.
            // Moderation asks whether their camera is on, not whether the host
            // has chosen to look at it — conflating the two left "stop all
            // videos" reading a room full of live cameras as empty.
            //
            // The peer's own report when it gives one: received video is also
            // off for a tile that is off-screen or starved, and the roster was
            // showing those cameras as off.
            cameraOn: p.cameraOn ?? p.videoOn,
          }
          return base
        })

      return [merged, ...remotes]
    },
    [
      props.localParticipant,
      props.participants,
      micOn,
      camOn,
      handRaised,
      isMyVideoPaused,
      areIncomingVideosPaused,
      removedParticipantIds,
      // Both are read by this memo and were missing, so it never recomputed for
      // either. The composited stream arrives after `produce()` resolves — by
      // which time no other dependency has moved — so the local tile kept a
      // roster built before that stream existed and went on rendering the raw
      // camera from MediaStreamContext.
      props.localStream,
      selectedBackground,
    ],
  )

  /**
   * The roster's ids as one string, memoised.
   *
   * This expression used to live INSIDE a dependency array, and a dependency
   * array is evaluated on every render — so a 250-person room mapped 250 ids
   * and joined them into a string on every single render, purely to decide
   * whether an effect could be skipped.
   */
  const participantIdsKey = useMemo(
    () => allParticipants.map((p) => p.id).join(','),
    [allParticipants],
  )
  // Who is in the room, you included — poll results count everyone here
  // except the poll's creator. Keyed off the id string so a mic toggle, which
  // rebuilds `allParticipants`, does not hand the dialog a new array.
  const presentParticipantIds = useMemo(
    () => (participantIdsKey ? participantIdsKey.split(',') : []),
    [participantIdsKey],
  )

  // Memoize sliced participants array
  const participants = useMemo(
    // No slice: `participantCount` used to be a demo dial (default 7) that
    // doubled as a cap on the real roster, so an eighth participant simply
    // never rendered. It now follows the roster instead of bounding it.
    () => allParticipants,
    [allParticipants]
  )

  const orderedParticipants = useMemo(() => {
    if (!selectedParticipantId) return participants
    const selectedParticipant = participants.find((p) => p.id === selectedParticipantId)
    if (!selectedParticipant) return participants
    const remainingParticipants = participants.filter((p) => p.id !== selectedParticipantId)
    return [selectedParticipant, ...remainingParticipants]
  }, [participants, selectedParticipantId])

  useEffect(() => {
    if (!selectedParticipantId) return
    const exists = participants.some((p) => p.id === selectedParticipantId)
    if (!exists) {
      setSelectedParticipantId(null)
    }
  }, [participants, selectedParticipantId])

  const localParticipant = allParticipants.find((p) => p.isLocal)! // guaranteed to exist

  // Memoize layout participants to prevent unnecessary filtering.
  // - "Hide tiles without video" drops any tile whose camera is off — except a
  //   pinned one. A pin is an explicit "keep this person on my screen", and the
  //   pinned layout's main area holds nothing but pins: dropping a camera-off
  //   pin left that area, and often the whole stage, blank.
  // - "Hidden self video" drops only the local participant's tile so the
  //   user doesn't see their own face but still appears in the panel.
  const hidingTilesWithoutVideo = hideTilesWithoutVideo && !areIncomingVideosPaused
  const layoutParticipants = useMemo(() => {
    // A screen-share session is a screen, not a person: its screen is on the
    // stage like anyone's (remoteScreens), and it has no tile — in any layout,
    // spotlight or pin, nor in a local recording, which draws this list.
    let next = withoutShareOnly(orderedParticipants)
    if (hidingTilesWithoutVideo) {
      next = next.filter((p) => p.videoOn || pinnedIds.includes(p.id))
    }
    // A view-only attendee has no camera and never will for this meeting, so a
    // self tile is a permanent empty square with their initials in it. Dropped
    // for the same reason "hidden self video" drops it — and, like that setting,
    // only from the grid: they stay in the People panel and in the headcount,
    // because they are in the room.
    if (hiddenSelfVideo || viewOnly) {
      next = next.filter((p) => !p.isLocal)
    }

    // Splice each extra camera in right behind its owner's tile, so a person and
    // their second camera read as a group in every layout. This is the ONE place
    // additional sources enter the UI: every layout mode (auto, tiled, spotlight,
    // sidebar, pinned, dual-stream, whiteboard and screen-share rails) derives
    // its tile list from here, so they all pick the tiles up for free.
    const extras = props.additionalSources ?? []
    if (extras.length === 0) return next

    const byOwner = new Map<string, Participant[]>()
    for (const e of extras) {
      const list = byOwner.get(e.ownerId ?? '') ?? []
      list.push(e)
      byOwner.set(e.ownerId ?? '', list)
    }
    const merged: Participant[] = []
    for (const p of next) {
      merged.push(p)
      const own = byOwner.get(p.id)
      if (own) { merged.push(...own); byOwner.delete(p.id) }
    }
    // An owner whose own tile is filtered out (camera off with "hide tiles
    // without video", or hidden self view) still has a live extra camera —
    // append it rather than dropping the stream on the floor.
    for (const orphaned of byOwner.values()) merged.push(...orphaned)
    return merged
  }, [
    hidingTilesWithoutVideo,
    orderedParticipants,
    pinnedIds,
    hiddenSelfVideo,
    viewOnly,
    props.additionalSources,
  ])

  // People "hide tiles without video" is keeping off the screen. With every
  // camera off the grid is empty, and VideoGrid's "No participants" read as
  // "nobody is here" — so the empty stage says "No videos to display" instead.
  // Only the no-video filter counts: a self tile hidden by "hidden self video"
  // (or a view-only attendee's) is hidden on purpose.
  const hiddenNoVideoCount = useMemo(() => {
    if (!hidingTilesWithoutVideo) return 0
    return orderedParticipants.filter(
      (p) =>
        p.sourceKind !== "additional" &&
        !p.videoOn &&
        !pinnedIds.includes(p.id) &&
        !(p.isLocal && (hiddenSelfVideo || viewOnly)),
    ).length
  }, [hidingTilesWithoutVideo, orderedParticipants, pinnedIds, hiddenSelfVideo, viewOnly])

  // The stage is empty only because YOU hid your own tile. "No participants"
  // there reads as a broken call, so the stage says what happened and offers the
  // way back. A view-only attendee has no tile by design, so they never see it.
  const selfHiddenEmpty = useMemo(
    () =>
      hiddenSelfVideo &&
      !viewOnly &&
      hiddenNoVideoCount === 0 &&
      layoutParticipants.length === 0 &&
      orderedParticipants.some((p) => p.isLocal),
    [hiddenSelfVideo, viewOnly, hiddenNoVideoCount, layoutParticipants, orderedParticipants],
  )

  // What a local recording should draw: the tiles the recorder can actually
  // see, in the order they see them. Sourced from the same `layoutParticipants`
  // funnel every layout uses, so the file matches the meeting.
  const recordingTiles = useMemo<CompositorTile[]>(
    () =>
      layoutParticipants.map((p) => ({
        id: p.id,
        name: p.name,
        videoOn: p.videoOn,
        // A virtual background renders as a CSS background with the cutout
        // canvas over it, so the recorder needs the image to draw underneath.
        backgroundUrl: p.isLocal && p.sourceKind !== "additional" ? selectedBackground?.url ?? null : null,
      })),
    [layoutParticipants, selectedBackground],
  )
  // Read by handleStartLocalRecording, which would otherwise close over the
  // tile list from whenever it was last rebuilt.
  const recordingTilesRef = useRef<CompositorTile[]>(recordingTiles)
  recordingTilesRef.current = recordingTiles

  // Keep a running recording in step with the meeting — people joining and
  // leaving, cameras going on and off.
  const localRecordingActive = localRecording.isRecording
  const localRecordingUpdate = localRecording.update

  // Tell the recorder up front when the file won't play in Windows' default
  // player, rather than letting them find out when they open it.
  const recordingWarning = localRecording.playbackWarning
  useEffect(() => {
    if (!recordingWarning) return
    toast.info({ title: 'Recording started', description: recordingWarning })
  }, [recordingWarning])
  useEffect(() => {
    if (!localRecordingActive) return
    localRecordingUpdate({ tiles: recordingTiles, audioStreams: props.recordingAudioStreams ?? [] })
  }, [localRecordingActive, localRecordingUpdate, recordingTiles, props.recordingAudioStreams])

  const breakoutAssignableParticipants = useMemo(
    () =>
      {
        // Everyone in the MEETING, not everyone with a tile.
        //
        // This read `layoutParticipants`, which is the grid's list and drops
        // people the grid has reason to hide — the local user under "hidden
        // self video", anyone without a camera under "hide tiles without
        // video". They are still in the room, so leaving them out of the pool
        // meant they were never assigned and never prompted; a host with
        // self-video hidden could not be put in a room at all. Who may be
        // assigned is a question about the roster and nothing to do with what
        // is currently on screen.
        //
        // People only — you assign a person to a breakout room, not their
        // camera, nor a screen-share session (decision 17).
        const local = breakoutCandidates(allParticipants)
        // Indexed once instead of scanned per lookup. `roleOf` is called for every
        // person in the list and did a `find` over that same list each time, so
        // assembling the pool was O(n²) — ~62,500 comparisons at 250 people,
        // redone whenever the roster changed.
        const byId = new Map(local.map((p) => [p.id, p]))
        const roleOf = (id: string) => {
          const known = byId.get(id)
          return (known?.role === "host" || known?.isLocal ? "host" : "participant") as
            | "host"
            | "participant"
        }

        // Once the rooms are running the server tells us who is in the MAIN
        // room, and that is the list to assign from. The local roster is only
        // ever the room this client is standing in, so a moderator inside a
        // breakout would otherwise see their breakout's occupants as the whole
        // meeting — and anybody who joined after they left would be invisible
        // and impossible to assign.
        if (breakoutActive && (meetingRoster?.length ?? 0) > 0) {
          // The server leaves share sessions out of this roster; an older one
          // did not.
          const roster = withoutShareOnly(meetingRoster)
          const seen = new Set(roster.map((r) => r.id))
          return [
            ...roster.map((r: { id: string; name: string }) => ({
              id: r.id,
              // A name we already know beats the one on the wire: the roster
              // carries whatever the token said, the tile carries what the
              // person is called here.
              name: local.find((p) => p.id === r.id)?.name ?? r.name,
              role: roleOf(r.id),
            })),
            // Anyone this client can see who the main room has not reported —
            // people already inside a breakout with us, who are still in the
            // meeting and still assignable.
            ...local
              .filter((p) => !seen.has(p.id))
              .map((p) => ({ id: p.id, name: p.name, role: roleOf(p.id) })),
          ]
        }

        return local.map((p) => ({
          id: p.id,
          name: p.name,
          role: roleOf(p.id),
        }))
      },
    [allParticipants, breakoutActive, meetingRoster],
  )

  // Auto + tiled put your own tile first. Deliberately a separate list from
  // `layoutParticipants`: spotlight and sidebar use that one to pick a main
  // stage and would change behaviour if self moved to the front. The remaining
  // order is untouched, so a selected participant still leads the rest.
  const selfFirstParticipants = useMemo(() => {
    const selfIndex = layoutParticipants.findIndex((p) => p.isLocal && p.sourceKind !== "additional")
    if (selfIndex <= 0) return layoutParticipants
    const self = layoutParticipants[selfIndex]
    // Your own extra cameras travel with your tile. Filtering on `isLocal` alone
    // dropped them, because an extra camera of yours is a local tile too.
    const myExtras = layoutParticipants.filter((p) => p.sourceKind === "additional" && p.ownerId === self.id)
    const rest = layoutParticipants.filter((p) => p !== self && !myExtras.includes(p))
    return [self, ...myExtras, ...rest]
  }, [layoutParticipants])


  /**
   * Put whoever is speaking on the first page.
   *
   * With fifty people in a room the grid shows the first page and nothing else
   * changes when someone on page three starts talking — you hear a voice with
   * no face anywhere on screen. So the active speaker is MOVED to the front,
   * immediately after your own tile.
   *
   * Moved, not copied, and that is the whole trick. Copying would show that
   * person twice — once promoted and once still sitting in their original slot
   * further back — and would leave the page they came from one tile short.
   * Removing them from the tail is what makes the rest close up, so a speaker
   * promoted from position 45 leaves 43, 44, 46 running on unbroken behind
   * them.
   *
   * Skipped entirely when they are already on the first page: the user can see
   * them, and reordering would shuffle every tile after them to say something
   * the screen already says.
   *
   * `activeSpeakerId` is sticky and min-dwell debounced upstream
   * (useActiveSpeakers, 1.5 s), so this cannot thrash on people talking over
   * each other — this list changes at most once per dwell window.
   */
  /**
   * How many tiles the first page holds — the line between "already on screen"
   * and "needs bringing into view".
   *
   * One number for both grids, and it is the one the user chose in Settings ->
   * "Select number of visible videos on Screen" (plus your own tile). `tiled`
   * slices the roster here with it; `auto` hands it to VideoGrid as `pageSize`.
   *
   * It used to fork: `maxTiles` for tiled and VideoGrid's own DEFAULT_PAGE_SIZE
   * everywhere else, because nothing passed `pageSize` down. That made this a
   * guess about someone else's constant, and a guess in the wrong direction
   * promotes a speaker who was already on screen — the reshuffle the rule
   * exists to avoid.
   *
   * On a phone or tablet VideoGrid draws at most six of those a page, so the
   * same cap applies here (`tilesPerPage`). Seating with the uncapped density
   * there counted seats 6-15 as on screen while they sat on page two, and a
   * speaker in one of them was never brought into view. Desktop is unchanged.
   */
  const firstPageSize = tilesPerPage

  /**
   * Say the page size out loud whenever it changes.
   *
   * This number decides three separate things that are reported as three
   * separate bugs — how many tiles are drawn, whether the active speaker counts
   * as already visible, and which cameras lastN keeps flowing. When someone
   * says "it is only showing me three people" the first thing worth knowing is
   * what the grid thinks the density is, and whether that came from the
   * Settings dropdown or from Auto.
   *
   * Ungated for the same reason as the `[lastN]` and `[activeSpeaker]` lines:
   * the question is asked about a deployed meeting, where `vc:debug` cannot be
   * set after the fact. Fires only on change, so a settled meeting is silent.
   */
  useEffect(() => {
    console.info('[grid] page size', {
      tilesPerPage,
      visibleVideos: tilesPerPage - 1,
      source: tileDensity === 'auto' ? 'Auto' : 'Settings',
      // Differs from tilesPerPage only on a phone or tablet, where it is capped.
      density: maxTiles,
      layoutMode,
      roster: layoutParticipants.length,
      pages: Math.max(1, Math.ceil(layoutParticipants.length / tilesPerPage)),
    })
  }, [maxTiles, tilesPerPage, tileDensity, layoutMode, layoutParticipants.length])

  /**
   * Who sits where, carried across renders.
   *
   * Seating has to persist. Deriving the order fresh from the roster each time
   * can only promote whoever is speaking NOW, so the previous speaker drops
   * back to page four the instant somebody else opens their mouth — and two
   * people on different pages then trade the same seat back and forth for the
   * whole call. Remembering that seat four already belongs to the last speaker
   * is the difference between that and Teams' behaviour.
   *
   * A ref rather than state: every input that can change the seating
   * (`selfFirstParticipants`, `activeSpeakerId`, `firstPageSize`) has already
   * caused this render, so storing it in state would only add a second one.
   * `seatActiveSpeaker` is idempotent — the speaker is on page one by the time
   * it runs again — so a double-invoked render cannot double-swap.
   */
  const seatingRef = useRef<Seating>(emptySeating())

  const gridOrderedParticipants = useMemo(() => {
    const seated = seatActiveSpeaker(
      seatingRef.current,
      selfFirstParticipants,
      activeSpeakerId,
      localParticipant?.id,
      firstPageSize,
    )
    seatingRef.current = seated.seating
    return seated.tiles as Participant[]
  }, [selfFirstParticipants, activeSpeakerId, localParticipant?.id, firstPageSize])

  /**
   * The same seating for the tiles beside presented content — a screen share,
   * the whiteboard, a document or a video — whose rail draws `maxTilesPerPage`
   * a page on a desktop (a phone's strip is below).
   *
   * That rail used `layoutParticipants` in roster order, so a speaker past its
   * first page stayed there while the share played on (BUG-000658). Same rule
   * as the grid: your own tile first, and a speaker already on the first page
   * stays put — self plus five others never moves. Its own seating, because
   * the rail's page is not the grid's (`tilesPerPage` is the Settings density).
   *
   * On a phone the "page" is however many tiles the scrolling strip shows
   * without a swipe, which MobileStageLayout measures — about two and a half
   * in portrait — not six: seats three to six there are off-screen. Never
   * below two, your own tile plus one, or there would be no seat to swap into.
   */
  const [stripVisibleCount, setStripVisibleCount] = useState<number | null>(null)
  const railPageSize = isMobileOrLandscape && stripVisibleCount !== null
    ? Math.max(2, stripVisibleCount)
    : maxTilesPerPage
  const railSeatingRef = useRef<Seating>(emptySeating())
  const stageRailParticipants = useMemo(() => {
    const seated = seatActiveSpeaker(
      railSeatingRef.current,
      selfFirstParticipants,
      activeSpeakerId,
      localParticipant?.id,
      railPageSize,
    )
    railSeatingRef.current = seated.seating
    return seated.tiles as Participant[]
  }, [selfFirstParticipants, activeSpeakerId, localParticipant?.id, railPageSize])

  /**
   * Say so out loud when the speaker cannot be matched to a tile.
   *
   * This is the failure that looks like nothing at all. `seatActiveSpeaker`
   * returns the roster untouched when it cannot find the speaker, which is the
   * right thing to do and completely silent — so an id that never matches
   * (a format the SFU spells differently from Presence, a peer filtered out of
   * the grid) presents as "the active speaker feature does nothing", with no
   * way to tell it apart from "nobody is speaking".
   *
   * Printing the grid's own ids alongside makes a spelling mismatch obvious in
   * one look instead of an afternoon.
   */
  /** Last speaker this effect reported on, so a roster change does not reprint it. */
  const lastMatchLogRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeSpeakerId) return
    // Only when the SPEAKER changes. The dependency list includes the roster,
    // which is rebuilt on every join, leave, mute and camera toggle — so this
    // re-scanned the grid and reprinted an identical line many times a minute
    // in a busy room, and `gridIds` below maps every participant to do it.
    if (lastMatchLogRef.current === activeSpeakerId) return
    lastMatchLogRef.current = activeSpeakerId
    const found = selfFirstParticipants.find(
      (p) => p.id === activeSpeakerId && !p.isLocal && p.sourceKind !== "additional",
    )
    if (found) {
      console.info('[activeSpeaker] matched a tile', {
        peerId: activeSpeakerId,
        name: found.name,
        seat: gridOrderedParticipants.findIndex((p) => p.id === activeSpeakerId),
      })
      return
    }
    console.warn('[activeSpeaker] NOT found in the grid — nothing to promote', {
      peerId: activeSpeakerId,
      isYou: selfFirstParticipants.some((p) => p.id === activeSpeakerId && p.isLocal),
      gridIds: selfFirstParticipants.map((p) => p.id),
    })
  }, [activeSpeakerId, selfFirstParticipants, gridOrderedParticipants])

  /**
   * Why the speaker did or did not move.
   *
   * Promotion has three ways to silently do nothing — nobody is speaking, the
   * speaker is not in this list, or they already hold the seat — and from the
   * outside all three look identical: a voice with no tile at the front. The
   * second is the one that bites, because `activeSpeakerId` is a peerId from
   * the signaling channel while these are `Participant.id`s, and if those ever
   * stop being the same string the feature fails with nothing to see.
   *
   * So say which. Ungated for the same reason as the lastN line: the question
   * gets asked about a deployed meeting, where `vc:debug` cannot be set
   * retroactively. Fires only when the speaker or the resulting order changes.
   */
  /** Last speaker+seat printed, so an unrelated roster change does not reprint it. */
  const lastSeatLogRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeSpeakerId) return
    const seat = gridOrderedParticipants.findIndex((p) => p.id === activeSpeakerId)
    // "Fires only when the speaker or the resulting order changes" is what this
    // log was documented to do, and the dependency list could not express it —
    // the roster changes constantly, so it fired constantly. The signature says
    // it exactly: same speaker in the same seat prints nothing.
    const signature = `${activeSpeakerId}@${seat}`
    if (lastSeatLogRef.current === signature) return
    lastSeatLogRef.current = signature
    const inRoster = selfFirstParticipants.findIndex(
      (p) => p.id === activeSpeakerId && !p.isLocal && p.sourceKind !== "additional",
    )
    console.info('[activeSpeaker] seating', {
      speaker: activeSpeakerId,
      // Where they ended up, and whether that is somewhere you can see. A
      // speaker seated past the page line means the swap did not happen —
      // usually because page one held nothing that may be displaced.
      seat,
      onPageOne: seat >= 0 && seat < firstPageSize,
      // The tell for an id-shape mismatch: a speaker that is never found while
      // the roster is plainly full. Sampled rather than dumped so a 200-person
      // room does not fill the console.
      rosterSample: inRoster < 0 ? selfFirstParticipants.slice(0, 4).map((p) => p.id) : undefined,
      layoutMode,
      // Only these two modes render this list. In any other, seating is
      // computed and then not used — which is a real reason for "it did not
      // move" that has nothing to do with the allocator.
      listInUse: layoutMode === 'auto' || layoutMode === 'tiled',
      // The whole of page one. With a swap only two ids ever differ between
      // consecutive lines, so what changed is readable at a glance — which is
      // the point of the swap in the first place.
      pageOne: gridOrderedParticipants.slice(0, firstPageSize).map((p) => p.id),
    })
  }, [activeSpeakerId, gridOrderedParticipants, selfFirstParticipants, layoutMode, firstPageSize])

  // Grid pagination - now from useMeetingLayout hook (safeGridPage)
  // Paged by `tilesPerPage`, the page VideoGrid draws, so on a phone tiled
  // pages once — here — instead of handing VideoGrid sixteen to page again.
  const totalPages = Math.max(1, Math.ceil(layoutParticipants.length / tilesPerPage))
  const gridStart = safeGridPage * tilesPerPage
  // Memoize grid participants for pagination
  const gridParticipants = useMemo(
    () => gridOrderedParticipants.slice(gridStart, gridStart + tilesPerPage),
    [gridOrderedParticipants, gridStart, tilesPerPage]
  )

  // Pin a participant (up to 6) - now from useMeetingLayout hook (handlePin)

  // Maximize - toggles fullscreen on a specific element (passed from VideoTile).
  // In-page on iPhone, never the native video player — see toggleElementFullscreen.
  const handleMaximize = useCallback((element: HTMLElement) => toggleElementFullscreen(element), [])

  const handleResumeMyVideo = useMeetingSessionStore((s) => s.resumeMyVideo)
  const handleToggleChat = useCallback(() => {
    setChatOpen((v) => !v)
    setParticipantsOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
  }, [])

  const handleOpenParticipants = useCallback(() => {
    setParticipantsOpen(true)
  }, [setParticipantsOpen])

  const handleSelectParticipantFromPanel = useCallback((participantId: string) => {
    setSelectedParticipantId(participantId)
  }, [])

  const handleOpenChatFromTile = useCallback((_participantId: string) => {
    setChatOpen(true)
    setParticipantsOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
  }, [])

  const handleOpenChatWithParticipant = useCallback((participantId: string) => {
    // Open chat panel and switch to private tab with selected participant
    setChatOpen(true)
    setParticipantsOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
    // Set the selected private chat to open that conversation
    setSelectedPrivateChat(participantId)
  }, [])

  const handleToggleParticipants = useCallback(() => {
    setParticipantsOpen((v) => !v)
    setChatOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
    setWebinarUsersOpen(false)
  }, [])
  const handleToggleWebinarUsers = useCallback(() => {
    setWebinarUsersOpen((v) => !v)
    setChatOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
    setParticipantsOpen(false)
  }, [])

  const handleToggleMeetingInfo = useCallback(() => {
    setMeetingInfoPanelOpen((v) => !v)
    setChatOpen(false)
    setParticipantsOpen(false)
    setFilesOpen(false)
    setWebinarUsersOpen(false)
  }, [])
  // Tracks whether THIS click is the one that started the cloud recording
  // currently running, so the "someone started recording" toast below does
  // not fire for the person who just pressed the button.
  const startedCloudByMeRef = useRef(false)
  const onCloudRecordingChanged = props.onCloudRecordingChanged
  const handleStartCloudRecording = useCallback(() => {
    startedCloudByMeRef.current = true
    void recording.start().then((ok) => {
      if (ok) {
        onCloudRecordingChanged?.(true)
      } else {
        startedCloudByMeRef.current = false
      }
    })
  }, [recording, onCloudRecordingChanged])
  const handleStopCloudRecording = useCallback(() => {
    void recording.stop().then((ok) => {
      if (ok) {
        onCloudRecordingChanged?.(false)
        startedCloudByMeRef.current = false
      }
    })
  }, [recording, onCloudRecordingChanged])
  // Tell the outer component whether this session is the one controlling the
  // running cloud recording, so it can re-announce it to late joiners the
  // same way it already does for a local recording.
  const onOwnCloudRecordingChange = props.onOwnCloudRecordingChange
  useEffect(() => {
    onOwnCloudRecordingChange?.(ownCloudRecording)
  }, [ownCloudRecording, onOwnCloudRecordingChange])
  // Another participant announced a cloud start/stop. Re-read the server so
  // this session's own status (and everyone-else chip) catches up without
  // waiting on the 30s poll below.
  useEffect(() => {
    if (!props.cloudRecordingSignal) return
    void recordingRef.current.refresh()
  }, [props.cloudRecordingSignal])
  // A participant who cannot control the cloud recording has no channel
  // event telling them it is still running (only starts/stops are announced),
  // so this covers a recording that outlives the announcement (e.g. this tab
  // reconnecting mid-recording).
  useEffect(() => {
    if (!recording.isRecording || canControlCloud) return
    const id = setInterval(() => { void recordingRef.current.refresh() }, 30000)
    return () => clearInterval(id)
  }, [recording.isRecording, canControlCloud])
  // Tell whoever was not the one to start it. Covers a late joiner (the
  // mount-time status fetch flips isRecording true), a live start relayed by
  // the room channel (the refresh above flips it true), and a guest who only
  // ever sees the channel announcement (cloudAnnounced flips cloudRunning true
  // without isRecording ever being true for them).
  const prevCloudRunningRef = useRef(cloudRunning)
  useEffect(() => {
    const wasRunning = prevCloudRunningRef.current
    prevCloudRunningRef.current = cloudRunning
    if (!wasRunning && cloudRunning && !startedCloudByMeRef.current) {
      toast.info({
        title: 'This meeting is being recorded',
        description: props.cloudStartedBy
          ? `${props.cloudStartedBy} started a cloud recording.`
          : 'A cloud recording has started.',
      })
    }
  }, [cloudRunning, props.cloudStartedBy])
  // A failed cloud start or stop otherwise has no visible trace: the button
  // just reverts and the user is left guessing. clearError runs through the
  // ref so this effect only re-fires on a genuinely new error.
  useEffect(() => {
    if (!recording.error) return
    toast.error({ title: 'Recording problem', description: recording.error })
    recordingRef.current.clearError()
  }, [recording.error])
  const handleStartLocalRecording = useCallback(() => {
    void localRecording.start({
      tiles: recordingTilesRef.current,
      audioStreams: props.recordingAudioStreams ?? [],
    })
     
  }, [localRecording, props.recordingAudioStreams])
  const handleAdjustView = useCallback(() => setAdjustViewOpen(true), [])
  const handleToggleFullscreen = useCallback(() => {
    toggleFullscreen().catch(() => { })
  }, [toggleFullscreen])
  const handleOpenSettings = useCallback((tab: "audio" | "video" | "advanced" = "audio") => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  }, [])
  const handleOpenShortcuts = useCallback(() => {
    setShortcutsOpen(true)
  }, [])
  const handleOpenHostControls = useCallback(() => setHostControlsOpen(true), [])
  const handleOpenLiveStreaming = useCallback(() => setLiveStreamingOpen(true), [])
  const handleEndWebinarForAll = useCallback(() => setEndWebinarDialogOpen(true), [])
  const handleStartWebinar = useCallback(() => {
    setIsWebinarLive(true)
  }, [])
  const handleStopWebinar = useCallback(() => {
    setIsWebinarLive(false)
  }, [])
  const handleLeaveWebinar = useCallback(() => {
    onLeaveMeeting()
  }, [onLeaveMeeting])
  const handleEndMeetingForAll = useCallback(() => {
    // Two halves, and the meeting is only really over with both.
    //
    // The broadcast clears the room: it disconnects everyone currently
    // connected and nothing more. On its own that left the meeting row
    // untouched, so a participant could rejoin the moment they were dropped —
    // into a room the host had just closed and was no longer in. Recording the
    // end is what makes the join gate refuse them.
    //
    // Fire-and-forget: the room is being torn down either way, and blocking
    // the host's exit on an HTTP round-trip would leave them staring at a
    // meeting they had already ended.
    const meetingId = props.meetingInfo.meetingCode
    if (meetingId) void meetingsApi.endForAll(meetingId).catch(() => {})
    // Ending is not leaving: the broadcast disconnects every participant. It
    // comes back to us too, so this direct call is only a fallback for a
    // dropped socket — same reason either way, and the transition guard makes
    // the duplicate a no-op.
    moderation?.endMeetingForAll()
    onLeaveMeeting('ended')
  }, [onLeaveMeeting, moderation, props.meetingInfo.meetingCode])
  const handleLeaveMeeting = useCallback(() => {
    onLeaveMeeting()
  }, [onLeaveMeeting])

  // ── Moderator actions on remote participants ──────────────────────────
  // These go out on the room channel and nothing else: mic/camera/share state
  // is live SFU state (producerPaused/producerResumed), so writing a local
  // override on top of it replaced a fact with a guess that never expired. A
  // stopped participant may turn their own media back on — with `videoOn:false`
  // pinned locally, that never became visible to the moderator who stopped them.
  // The server rejects these from non-moderators regardless of what this client
  // believes.
  const handleMuteParticipant = useCallback(
    (id: string) => moderation?.muteParticipant(id),
    [moderation],
  )
  // A moderator cannot switch someone's mic ON — that is theirs to decide. The
  // row's mic-off button used to only flip this client's copy of `muted`, so it
  // looked like it worked while nothing at all left the browser. It asks now,
  // the same thing the kebab menu's "Request audio" does.
  const handleUnmuteParticipant = useCallback(
    (id: string) => moderation?.requestAudio(id),
    [moderation],
  )
  const handleStopScreenShareFor = useCallback(
    (id: string) => moderation?.stopParticipantScreenShare(id),
    [moderation],
  )
  const handleStopVideo = useCallback(
    (id: string) => moderation?.stopParticipantVideo(id),
    [moderation],
  )
  // Same as unmute: an ask, not a command. This was a pure local override —
  // the camera-off button sent nothing, so the participant's camera stayed off
  // and the tile stayed dark while the icon claimed otherwise.
  const handleStartVideo = useCallback(
    (id: string) => moderation?.requestVideo(id),
    [moderation],
  )
  const handleStopScreen = useCallback(
    (id: string) => moderation?.stopParticipantScreenShare(id),
    [moderation],
  )

  // Ending whatever share is on the stage, whoever owns it.
  //
  // Deliberately keyed off the ACTIVE share and not off `isLocalPresenter`:
  // with several people sharing, that stays true while you page across to
  // somebody else's screen, so it would put a stop button over a screen it
  // does not stop. Your own share you simply end; anyone else's goes out as
  // `moderate:stop_screenshare` — the same event the Participants list and the
  // tile menu already send, which the channel gates on the sender being a
  // moderator. So this is a second door onto an existing power, not a new one,
  // and a non-moderator is offered nothing.
  const canStopActiveShare = activeShare
    ? activeShare.isLocal
      ? isLocalPresenter
      : isRoomModerator
    : false
  const stopActiveShare = useCallback(() => {
    if (!activeShare) return
    if (activeShare.isLocal) stopScreenShare()
    // `key` IS the peerId for a remote share; only the local one is keyed
    // 'local'.
    else handleStopScreen(activeShare.key)
  }, [activeShare, stopScreenShare, handleStopScreen])
  // Tell the room, then hide locally. The local set alone only removed them
  // from this moderator's list — the participant stayed in the meeting, still
  // seeing and heard by everyone else.
  const handleRemoveParticipant = useCallback((id: string) => {
    moderation?.removeParticipant(id)
    setRemovedParticipantIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [moderation])
  // Hand the same moderation actions to the participant tiles.
  //
  // These are the very functions the Participants list uses, so the tile's
  // three-dot menu does the same thing rather than a lookalike. Through context
  // because `VideoTile` renders from twenty-one places and every one of them
  // would otherwise have to pass all nine — which is exactly why none of them
  // passed any, and why every moderation item in that menu quietly did nothing.
  const handleChangeRole = useCallback(
    (id: string, role: ParticipantRole) => {
      // The panel's vocabulary is wider than the room's. "presenter" is a
      // capability grant rather than a role; the rest map onto host/cohost/attendee.
      if (role === 'presenter') {
        moderation?.setPresenter(id, true)
      } else {
        moderation?.setRole(id, role === 'moderator' ? 'cohost' : role)
        // Demoting to attendee revokes the grant too — otherwise "make them an
        // attendee" left them able to share, and there was no way back at all.
        if (role === 'attendee') moderation?.setPresenter(id, false)
      }
      // No local override: roles come from the server's `roles:updated`. An
      // override set here never expired, so this moderator kept seeing the role
      // they had set after someone else changed it or the person rejoined.
    },
    [moderation],
  )
  // A screen-share session's row (contract §8): presenter is the one thing it
  // can hold — never a role, which the server refuses for it — and a removed
  // one can be allowed back.
  const handleSetPresenter = useCallback(
    (id: string, granted: boolean) => moderation?.setPresenter(id, granted),
    [moderation],
  )
  const handleAllowShare = useCallback((id: string) => moderation?.allowShare(id), [moderation])
  const handleSetAnnotator = useCallback(
    (id: string, granted: boolean) => moderation?.setAnnotator(id, granted),
    [moderation],
  )
  const shareBlocked = roles?.shareBlocked

  const setParticipantActions = useMeetingSessionStore((s) => s.setParticipantActions)
  const participantTileActions = useMemo(
    () => ({
      muteParticipant: handleMuteParticipant,
      requestAudio: handleUnmuteParticipant,
      stopVideo: handleStopVideo,
      requestVideo: handleStartVideo,
      stopScreenShare: handleStopScreenShareFor,
      removeParticipant: handleRemoveParticipant,
      changeRole: handleChangeRole,
      setAnnotator: handleSetAnnotator,
    }),
    [
      handleMuteParticipant,
      handleUnmuteParticipant,
      handleStopVideo,
      handleStartVideo,
      handleStopScreenShareFor,
      handleRemoveParticipant,
      handleChangeRole,
      handleSetAnnotator,
    ],
  )
  useEffect(() => {
    setParticipantActions(participantTileActions)
  }, [participantTileActions, setParticipantActions])

  // Bulk moderator actions. The server broadcasts these to everyone but the
  // sender, so the host is never a target — no local exclusion needed, and no
  // bulk override to pin the whole room "off" until reload.
  const handleMuteAll = useCallback(() => moderation?.muteAll(), [moderation])
  // Is there any remote camera to pause?
  //
  // `cameraOn`, not `videoOn` — the same distinction the participants panel
  // draws a few files over. `videoOn` already has this client's own "pause
  // incoming" preference folded into it, so keying off it would say "nobody is
  // on camera" the instant you paused, and the menu item that resumes them
  // would disable itself.
  const anyRemoteCameraOn = useMemo(
    () => allParticipants.some((p) => !p.isLocal && (p.cameraOn ?? p.videoOn)),
    [allParticipants],
  )
  // Is there any remote voice to pause?
  //
  // Plain `muted`, which needs no `cameraOn`-style twin: this client's audio
  // pause never touches it. For a remote peer it is the PRODUCER's state (the
  // SFU's producerPaused push), and it reads true until their audio consumer
  // exists at all — so someone still joining, or with the mic off, is not a
  // voice to pause, and pausing does not make the room read as silent.
  const anyRemoteMicOn = useMemo(
    () => allParticipants.some((p) => !p.isLocal && !p.muted),
    [allParticipants],
  )

  const handleStopAllVideos = useCallback(() => moderation?.stopAllVideos(), [moderation])
  const handleStopAllScreens = useCallback(() => moderation?.stopAllScreenShares(), [moderation])



  const handleOpenBackgroundEffects = useCallback(() => {
    // Nothing to apply an effect to with the camera off, so say so rather than
    // opening a dialog that cannot work.
    //
    // It used to open regardless. The preview is deliberately built from the
    // track already being PUBLISHED — opening a second capture here broke the
    // published mask and could stop the camera the tiles render from — so with
    // the camera off there is no stream, the preview falls back to a bare grey
    // placeholder, and picking a background changes nothing anybody can see.
    // From the user's side that is a menu item that does nothing at all.
    //
    // Told, not silently fixed: switching somebody's camera on because they
    // opened a menu is not a decision this should make for them.
    if (!camOn) {
      toast.warning({ title: t('controlBar.cameraOffForBackgrounds') })
      return
    }
    setBackgroundEffectsOpen(true)
  }, [camOn, t])

  // Poll functions are now in usePolls hook (activePoll, handleCreatePoll, handleVotePoll, handleEndPoll); results are computed by `pollResultFor`

  // Raising a poll is a moderator action — poll.router.ts refuses anyone else
  // with a 403. The control is hidden rather than shown and then rejected: the
  // create dialog does not await the call, so a refusal closed the dialog as if
  // it had worked and silently created nothing.
  const canManagePolls = hasPermission(localParticipant.role, "poll-manage")

  // Polls this participant has answered: the Polls panel offers their results,
  // not another vote.
  const votedPollIds = useMemo(
    () => pollVotes.filter((v) => v.participantId === localParticipant.id).map((v) => v.pollId),
    [pollVotes, localParticipant.id],
  )

  // Publish the moderation permission for the participant tiles.
  //
  // Exactly the expression the Participants list uses, so the tile's three-dot
  // menu and the list cannot disagree about who may do what. A tile is handed
  // the participant it shows and never the one looking at it, so left to itself
  // it could only ask "is this me?" — which is why it was offering Make
  // moderator to every attendee.
  const canModerateParticipants = hasPermission(localParticipant.role, "role-modify")
  const setCanModerate = useMeetingSessionStore((s) => s.setCanModerate)
  useEffect(() => {
    setCanModerate(canModerateParticipants)
  }, [canModerateParticipants, setCanModerate])
  const viewerIsHost = localParticipant.role === "host"
  const setIsRoomHost = useMeetingSessionStore((s) => s.setIsRoomHost)
  useEffect(() => {
    setIsRoomHost(viewerIsHost)
  }, [viewerIsHost, setIsRoomHost])

  // Every live poll this participant still owes an answer to, asked one at a
  // time.
  //
  // This used to prompt ONLY for `poll:created`, so it reached the people
  // already sitting in the meeting and nobody else. Someone who joined after
  // the question was asked got their polls through the catch-up fetch, which
  // never prompted — the poll sat in the Polls panel, and a participant who did
  // not think to open Apps never voted at all.
  //
  // The silence was deliberate, on the reasoning that a question asked before
  // you arrived is an interruption rather than a prompt. Reversed on purpose: a
  // live poll is something the room is waiting on, and a participant who cannot
  // see it cannot answer it.
  //
  // `pollsToPrompt` owns which polls qualify — active, not yours, not already
  // answered, which is also what stops a reload asking twice.
  const [pollQueue, setPollQueue] = useState<string[]>([])

  // Polls this browser has already shown this participant, restored from the
  // last visit. Held in state as well as storage so marking one re-runs the
  // effect below; storage alone would not.
  const pollMeetingId = meetingInfo.meetingCode || null
  const [promptedIds, setPromptedIds] = useState<string[]>(() => promptedPollIds(pollMeetingId))
  useEffect(() => {
    setPromptedIds(promptedPollIds(pollMeetingId))
  }, [pollMeetingId])

  useEffect(() => {
    const promptable = pollsToPrompt({
      polls,
      votes: pollVotes,
      authoredPollIds: useMeetingPollsStore.getState().authoredPollIds,
      promptedPollIds: promptedIds,
      localParticipantId: localParticipant.id,
    })
    // Reconciled rather than replaced: the participant is looking at the head,
    // and recomputing outright would swap the question under the pointer
    // whenever another poll started or a tally moved.
    setPollQueue((prev) => {
      const next = reconcileQueue(prev, promptable)
      return next.length === prev.length && next.every((id, i) => id === prev[i]) ? prev : next
    })
  }, [polls, pollVotes, localParticipant.id, promptedIds])

  // `promptPollId` still exists for the live broadcast, and is consumed here so
  // a re-render cannot replay the dialog. It no longer decides anything on its
  // own — the queue does — but clearing it keeps the store honest.
  useEffect(() => {
    if (promptPollId) useMeetingPollsStore.getState().setPromptPollId(null)
  }, [promptPollId])

  // One at a time. The next question is raised only once the dialog for the
  // previous one has closed, whether it was answered or dismissed — asking two
  // at once would stack dialogs, and answering the top one would reveal a
  // second the participant never saw arrive.
  useEffect(() => {
    if (pollVotingOpen || pollQueue.length === 0) return
    // Nothing is raised until there is a meeting to file it under.
    //
    // `promptedIds` is seeded from storage keyed on this id, so before it
    // resolves the set reads as empty — every poll looks unseen, and a
    // participant returning to a meeting they had already answered was asked
    // again. Worse, the prompt then recorded itself under no meeting, so the
    // second visit repeated it too. Waiting costs a render; not waiting loses
    // everything the participant had already been told.
    if (!pollMeetingId) return

    const { next, queue, shown } = takeNextPrompt(pollQueue, promptedIds)
    if (!next) return
    // Marked as SHOWN here, on open — not on close, and not on the vote.
    //
    // Closing the dialog does not make a poll un-promptable: it is still active
    // and still unanswered, so it qualified again the instant it was dismissed
    // and reopened over the participant. Voting had the same shape for a
    // different reason — the vote is recorded only after a network round trip,
    // while the dialog closes immediately, so for that moment the poll still
    // looked unanswered.
    //
    // Marking on open settles both: shown is shown. Vote, Cancel, X and a slow
    // connection all behave the same, and the poll remains answerable from the
    // Polls panel.
    // Recorded LOCALLY first, and unconditionally.
    //
    // This used to be `setPromptedIds(rememberPrompted(...))`, which made
    // de-duplication depend on what a storage function returned — and it
    // returns an empty list whenever there is no meeting id to file under,
    // which is every guest who joined by link before the meeting record
    // resolves. The poll was then never excluded, so closing the dialog
    // re-qualified it and it opened again, and again. Storage being
    // unavailable at all (private browsing, a quota) had the same effect.
    //
    // Correctness now lives in this component and cannot fail. Persisting is a
    // separate, best-effort step whose only job is to survive a reload.
    setPromptedIds(shown)
    setPollQueue(queue)
    // Persistence is separate and best-effort: its only job is to survive a
    // reload, and nothing above depends on it succeeding.
    rememberPrompted(pollMeetingId, next)
    setActivePollId(next)
    setPollVotingOpen(true)
  }, [pollVotingOpen, pollQueue, promptedIds, pollMeetingId, setActivePollId, setPollVotingOpen])

  // Survey functions are now in useSurveys hook (handleCreateSurvey, handleDeleteSurvey, handleEditSurvey)

  // Branding functions are now in useBranding hook (handleAddLogo, handleDeleteLogo, etc.)

  /**
   * Where the side panel has to be rendered to be seen.
   *
   * When a NESTED element owns the fullscreen layer — a maximised tile, a
   * shared screen, a shared video, a document — the browser paints only that
   * element. The panel is a flex child of the meeting layout, so `Alt+C` and
   * `Alt+P` were opening it somewhere off screen: the state flipped, nothing
   * appeared, and the shortcut looked dead.
   *
   * `document.body` (nothing fullscreen, or the WHOLE PAGE fullscreen, where
   * the normal tree is painted correctly) leaves everything exactly as it was.
   */
  const fullscreenHost = useFullscreenHost()
  const inNestedFullscreen = fullscreenHost !== null && fullscreenHost !== document.body
  /** Fixed-overlay presentation: forced while nested-fullscreen, since the
   *  panel is no longer a child of the flex row that sized it. */
  const panelOverlayMode = sideDrawerOverContent || inNestedFullscreen

  /** Render `node` inside the fullscreen element, or leave it where it is. */
  const renderInFullscreenHost = useCallback(
    (node: React.ReactNode) =>
      inNestedFullscreen && fullscreenHost ? createPortal(node, fullscreenHost) : node,
    [inNestedFullscreen, fullscreenHost],
  )

  /**
   * Mic and camera for a shared video, a document or the whole page in full
   * screen.
   *
   * The first two fullscreen their own element, so the control bar — outside
   * it — is not painted; whole-page full screen collapses the bar outright. In
   * both, turning the mic or camera on or off meant leaving full screen first.
   * Same gates as the control bar's buttons; nothing for a view-only attendee,
   * who has neither there either.
   */
  const fullscreenMediaControls = viewOnly ? null : (
    <>
      <MicControl
        compact
        disabled={!hostCanUseMicrophone}
        onBlocked={() => handleHostBlocked('microphone')}
      />
      <CameraControl
        compact
        disabled={!hostCanUseVideo}
        onBlocked={() => handleHostBlocked('camera')}
      />
    </>
  )

  const sidePanelOpen = useMemo(
    () => chatOpen || participantsOpen || filesOpen || meetingInfoPanelOpen || pollsPanelOpen || surveysPanelOpen || brandingPanelOpen || videoSharePanelOpen || documentSharePanelOpen || webinarUsersOpen || breakoutRoomsPanelOpen || transcriptionPanelOpen,
    [chatOpen, participantsOpen, filesOpen, meetingInfoPanelOpen, pollsPanelOpen, surveysPanelOpen, brandingPanelOpen, videoSharePanelOpen, documentSharePanelOpen, webinarUsersOpen, breakoutRoomsPanelOpen, transcriptionPanelOpen]
  )

  // Side panel: snap layout width state immediately on open and close.
  // Width transition handles visual motion while layout reclaim starts without close lag.
  useEffect(() => {
    if (panelCollapseTimerRef.current) clearTimeout(panelCollapseTimerRef.current)
    if (sidePanelOpen) {
      setPanelLayoutOpen(true)
    } else {
      setPanelLayoutOpen(false)
    }
    return () => {
      if (panelCollapseTimerRef.current) clearTimeout(panelCollapseTimerRef.current)
    }
  }, [sidePanelOpen])

  const sidePanelDefaultTab = meetingInfoPanelOpen ? "info" : chatOpen ? "chat" : participantsOpen ? "people" : filesOpen ? "files" : "chat" // Default to "chat" for unsupported tabs

  // Determine which custom panel to show
  const customPanel = breakoutRoomsPanelOpen
    ? "breakoutRooms"
    : webinarUsersOpen
      ? "webinarUsers"
      : pollsPanelOpen
        ? "polls"
        : surveysPanelOpen
          ? "surveys"
          : brandingPanelOpen
            ? "branding"
            : videoSharePanelOpen
              ? "videoShare"
              : documentSharePanelOpen
                ? "documentShare"
                : transcriptionPanelOpen
                  ? "transcription"
                  : null

  // Real peerId, not "you" and not the roster id. The connection's peerId is
  // authoritative — it is what the server stamps on every message as `from`,
  // so it is the only id that can decide "is this mine".
  const chatLocalId =
    props.localPeerId || props.localParticipant?.sfuPeerId || props.localParticipant?.id || ''
  const chatRoomCredentials = useMeetingSessionStore((s) => s.roomCredentials)
  // `channelTopic` is "room:<id>" — the archive is keyed on the bare room id.
  const chatRoomId = chatRoomCredentials?.channelTopic?.replace(/^room:/, '') ?? null

  // This client's own mic -> transcription-server upload, gated on the
  // meeting's own setting (not on captionsOn, which only controls what this
  // viewer sees) AND on micOn: muting is this participant saying "don't
  // record me", and the SFU producer pause alone doesn't stop this — it's a
  // separate, independent tap on the same track (see
  // live-transcription-capture.ts). Without this gate, muting silenced what
  // other participants heard over the call but kept uploading this mic to
  // Google Speech regardless, which is both a privacy problem and — if this
  // machine's microphone can still acoustically pick up whoever's actually
  // talking nearby — a source of captions wrongly attributed to whoever is
  // "muted". "chat*" above is the room credentials/id generally, not
  // chat-specific — reused here rather than reading the store a second time.
  useLiveTranscriptionCapture(
    props.localStream?.getAudioTracks()[0] ?? null,
    chatRoomId,
    chatLocalId,
    localParticipant?.name,
    transcriptionEnabledForSession && micOn,
  )

  // Bind the persisted share lists to this room. Restores them after a refresh
  // and drops another meeting's files rather than carrying them across.
  useEffect(() => {
    if (chatRoomId) setMediaShareRoomId(chatRoomId)
  }, [chatRoomId, setMediaShareRoomId])

  /**
   * Files attached when the meeting was scheduled appear in the Documents
   * panel, ready to present — that is the point of attaching them.
   *
   * Merged rather than assigned, so anything shared during the meeting stays,
   * and keyed by id so re-running this never duplicates a row. Entries without
   * a URL are skipped: they were never uploaded and would present as nothing.
   */
  useEffect(() => {
    // Wait for the room binding. setMediaShareRoomId clears the share lists
    // when the room changes, so anything seeded before it lands is discarded.
    if (!chatRoomId) return
    const attached = meetingRecord?.attachments ?? []
    if (!attached.length) return
    setSharedDocuments((prev) => {
      const known = new Set(prev.map((d) => d.id))
      const additions = attached
        .filter((a) => a.url && !known.has(a.id))
        .map((a) => ({
          id: a.id,
          name: a.name,
          size: a.size,
          type: a.type,
          url: a.url as string,
          uploadedAt: new Date().toISOString(),
          sharedByName: meetingRecord?.host,
          // A PowerPoint, Word or text file attached when scheduling is
          // presented as the PDF copy made when it was uploaded.
          ...(a.previewUrl ? { previewUrl: a.previewUrl, previewAttachmentId: a.previewId } : {}),
          // Private: opened through a short-lived link, never `url` (VAPT §6.2).
          ...(meetingRecord?.id ? { meetingId: meetingRecord.id } : {}),
        }))
      return additions.length ? [...additions, ...prev] : prev
    })
  }, [chatRoomId, meetingRecord?.attachments, meetingRecord?.host, meetingRecord?.id, setSharedDocuments])

  const chat = useChat(chatLocalId, meetingInfo.userName, {
    channel: props.chatChannel ?? null,
    roomId: chatRoomId,
    roomToken: chatRoomCredentials?.roomToken ?? null,
    // The in-call chat toast. `handleShowChatToast` already applies the
    // Notifications / Hide chat notifications / sound switches; nothing called
    // it for an incoming message, so no chat notification ever appeared.
    onUnreadMessage: (msg) =>
      handleShowChatToast(msg.text || msg.attachment?.name || '', msg.senderName),
  })

  // Send on the channel. `type` still distinguishes the webinar broadcast tab;
  // `to` is the recipient peerId for a private message, null for the room.
  const handleSendChat = useCallback(
    (text: string, _type: ChatType, to?: string | null) => chat.sendMessage(text, to ?? null),
    [chat],
  )

  // Upload first, then send the descriptor over the channel — the file itself
  // never touches the socket. Failures surface: a silently dropped attachment
  // is indistinguishable from a delivered one.
  const chatRoomToken = chatRoomCredentials?.roomToken ?? null

  const handleAttachChat = useCallback(
    async (file: File, kind: ChatAttachmentKind, to?: string | null) => {
      if (!chatRoomId || !chatRoomToken) return
      try {
        const attachment = await uploadChatAttachment(file, kind, chatRoomId, chatRoomToken)
        chat.sendMessage('', to ?? null, attachment)
      } catch (err) {
        toast.error({
          title: 'File not sent',
          description: err instanceof Error ? err.message : 'Upload failed',
        })
      }
    },
    [chat, chatRoomId, chatRoomToken],
  )

  // Uploads now ride the ROOM token, which every participant holds — guests
  // included. This used to ask whether the user was signed in, so a guest's
  // attach button was hidden no matter what the host's File Sharing toggle
  // said. The host control is the only thing that should decide that.
  const canAttachInChat = canUploadAttachments(chatRoomToken) && !!chatRoomId

  // ── Reactions, on the wire ────────────────────────────────────────────
  // `triggerReaction` only ever animated locally, so a reaction was a private
  // event between the user and their own screen. It now goes out on the room
  // channel as well, and inbound ones play the same burst here.
  //
  // Ephemeral by design: nothing is stored and nothing replays on join. A
  // reaction that arrives after the moment has passed is noise.
  const handleSendReaction = useCallback(
    (emoji: string) => {
      // Attributed to us locally; remote peers attribute it from the payload.
      if (chatLocalId) triggerReaction(emoji, chatLocalId)
      props.chatChannel?.sendEvent('reaction:send', { emoji })
    },
    [triggerReaction, chatLocalId, props.chatChannel],
  )

  useEffect(() => {
    const channel = props.chatChannel
    if (!channel) return
    return channel.subscribe('reaction', (payload) => {
      const p = payload as { emoji?: string; peerId?: string }
      // The server sends with broadcast_from, so our own reaction never comes
      // back — the local burst above is the only one we play for ourselves.
      if (typeof p?.emoji === 'string' && p.emoji && p.peerId) {
        triggerReaction(p.emoji, p.peerId)
      }
    })
  }, [props.chatChannel, triggerReaction])

  // ── Raised hand, on the wire ──────────────────────────────────────────
  // `handRaised` is local UI state that the control bar toggles directly, so
  // the hand is mirrored out from here rather than by rewiring the button.
  // Remote hands come back via Presence (see usePresence), which also replays
  // them to anyone joining later — the reason this is presence state and not a
  // one-off event.
  //
  // Skips the first run: sending `hand:lower` on join would be a message about
  // nothing, for every participant, every time.
  const lastSentHandRef = useRef<boolean | null>(null)
  useEffect(() => {
    const channel = props.chatChannel
    if (!channel) return
    if (lastSentHandRef.current === null) {
      lastSentHandRef.current = handRaised
      if (!handRaised) return
    }
    if (lastSentHandRef.current === handRaised) return
    lastSentHandRef.current = handRaised
    channel.sendEvent(handRaised ? 'hand:raise' : 'hand:lower', {})
  }, [handRaised, props.chatChannel])

  // ── Shared whiteboard ─────────────────────────────────────────────────
  // The room says only "a board is open, opened by X"; the URL is composed
  // locally from this client's env and this user's identity. Same presenter
  // capability that gates screen share decides who may open one and who may
  // draw — an attendee frames the board read-only.
  const whiteboardRole = roles?.participantRole(props.localPeerId ?? undefined) ?? 'attendee'
  const canPresentWhiteboard = whiteboardRole !== 'attendee'

  const whiteboard = useWhiteboard({
    peerId: props.localPeerId ?? null,
    roomId: chatRoomId,
    displayName: meetingInfo.userName,
    canEdit: canPresentWhiteboard,
    canPresent: canPresentWhiteboard,
    // On a phone the drawing tools are the presenter's alone; everyone else
    // gets the whole board to look at (see SharedWhiteboard's fitDesktopCanvas).
    editOnlyAsPresenter: isMobileOrLandscape,
    isModerator: roles?.isModerator ?? false,
    sendEvent: props.chatChannel?.sendEvent ?? noopSendEvent,
    subscribe: props.chatChannel?.subscribe ?? noopSubscribe,
    ready: !!props.chatChannel,
  })

  // Room-wide video / document presentation. Same shape as the whiteboard:
  // local state follows the room, so a share put up by anyone lands on every
  // participant's stage instead of only the presenter's.
  const sharedMedia = useSharedMedia({
    peerId: props.localPeerId ?? null,
    isModerator: roles?.isModerator ?? false,
    sendEvent: props.chatChannel?.sendEvent ?? noopSendEvent,
    subscribe: props.chatChannel?.subscribe ?? noopSubscribe,
    ready: !!props.chatChannel,
    // Someone else's upload joins this client's list. Upsert by id so a
    // re-announce (which a late joiner triggers) does not duplicate rows.
    onLibraryAdd: (kind, item) => {
      if (kind === 'video') {
        setSharedVideos((prev) =>
          prev.some((v) => v.id === item.id)
            ? prev
            : [...prev, {
                id: item.id, name: item.name, url: item.url,
                kind: item.kind, size: item.size,
                sharedBy: item.sharedBy, sharedByName: item.sharedByName,
              }],
        )
      } else {
        setSharedDocuments((prev) =>
          prev.some((d) => d.id === item.id)
            ? prev
            : [...prev, {
                id: item.id, name: item.name, url: item.url,
                size: item.size ?? 0, type: item.type ?? 'application/octet-stream',
                uploadedAt: new Date().toISOString(),
                sharedBy: item.sharedBy, sharedByName: item.sharedByName,
                ...(item.previewUrl ? { previewUrl: item.previewUrl } : {}),
              }],
        )
      }
    },
    onLibraryRemove: (kind, id) => {
      if (kind === 'video') setSharedVideos((prev) => prev.filter((v) => v.id !== id))
      else setSharedDocuments((prev) => prev.filter((d) => d.id !== id))
    },
  })

  /**
   * Remove a document from the room and delete the stored file.
   *
   * The bytes live in the attachment store, so dropping the row on its own
   * leaves the object behind belonging to nothing — the same leak the schedule
   * form had. Only files this client uploaded are deleted: `deleteUploadedShare`
   * needs an attachment URL, so a pasted public link is left alone, and the
   * backend refuses anything already attached to a chat message.
   *
   * Deletion is best effort. The row is gone from everyone's panel either way,
   * and a failed request should not leave the document sitting there.
   */
  /**
   * Who may put a file into the meeting.
   *
   * The host and moderators run the meeting; a presenter has been handed the
   * floor deliberately. It also decides who may present a video somebody else
   * added. A plain attendee gets the panels read-only: shared documents can
   * still be downloaded, shared videos are not. Both render sites read this
   * one value — the desktop and mobile panels have drifted apart repeatedly by
   * each computing its own.
   */
  const canShareFiles =
    localParticipant.role === 'host' ||
    localParticipant.role === 'moderator' ||
    localParticipant.role === 'presenter'

  /**
   * Put a shared document on the stage. Anyone with presenter access may
   * present any document in the list, whoever added it; the desktop and mobile
   * panels both come here.
   */
  // Whether this client is still presenting document `id`: its stand-in until
  // the room echoes the share, the room's entry by this peer after. Read from
  // the link refresher's timer, which outlives renders.
  const stillPresentingRef = useRef<(id: string) => boolean>(() => false)
  useEffect(() => {
    stillPresentingRef.current = (id) =>
      localPresenting?.id === id ||
      sharedMedia.stack.some((e) => e.id === id && e.by === (props.localPeerId ?? null))
  }, [localPresenting, sharedMedia.stack, props.localPeerId])
  const stopLinkRefreshRef = useRef<(() => void) | null>(null)
  useEffect(() => () => stopLinkRefreshRef.current?.(), [])

  // A scheduled file's link is refused to anyone not invited (VAPT §6.2).
  const notifyFileLinkError = useCallback((err: unknown) => {
    const message = err instanceof Error && err.message ? err.message : 'Could not open this file.'
    toast.error({ title: t('common.error'), description: message })
  }, [t])

  const presentDocument = useCallback(
    (doc: SharedDocument) => {
      void (async () => {
        // A PowerPoint, Word or text file goes up as its PDF copy, under its own
        // name: the stage paints a PDF whatever the name says. A scheduled file
        // is private, so it goes up with a short-lived link asked for now.
        let link
        try {
          link = await documentLink(doc, 'present')
        } catch (err) {
          notifyFileLinkError(err)
          return
        }
        const { url } = link
        setLocalPresenting({ kind: 'document', id: doc.id, name: doc.name, url })
        sharedMedia.start('document', { id: doc.id, name: doc.name, url })
        // Re-announced with a fresh link before this one lapses, so the room's
        // copy, replayed to whoever joins later, still opens. Everyone already
        // watching keeps the document they have loaded.
        stopLinkRefreshRef.current?.()
        stopLinkRefreshRef.current = keepLinkFresh({
          doc,
          expiresAt: link.expiresAt,
          stillPresenting: () => stillPresentingRef.current(doc.id),
          onUrl: (next) => { sharedMedia.start('document', { id: doc.id, name: doc.name, url: next }) },
        })
      })()
    },
    [sharedMedia, notifyFileLinkError],
  )

  /** Download from either documents panel; a scheduled file asks for a link first. */
  const downloadDocument = useCallback(
    (doc: SharedDocument) => {
      void (async () => {
        let url: string
        try {
          url = (await documentLink(doc, 'download')).url
        } catch (err) {
          notifyFileLinkError(err)
          return
        }
        // Not window.open: an uploaded attachment sits behind bearer auth,
        // which a top-level navigation cannot carry, so the tab landed on a
        // 401 body instead of the file.
        await downloadSharedFile(url, doc.name).catch(() => {})
      })()
    },
    [notifyFileLinkError],
  )

  const removeSharedDocument = useCallback(
    (id: string) => {
      const doc = sharedDocuments.find((d) => d.id === id)
      if (doc?.url) void deleteUploadedShare(doc.url, doc.attachmentId)
      if (doc?.previewUrl) void deleteUploadedShare(doc.previewUrl, doc.previewAttachmentId)
      setSharedDocuments((prev) => prev.filter((d) => d.id !== id))
      sharedMedia.unpublishLibraryItem('document', id)
    },
    [sharedDocuments, setSharedDocuments, sharedMedia],
  )


  /**
   * Everything on the stage, oldest first: the room's stack, plus a share of
   * this client's that never reached it.
   *
   * Derived, not mirrored. The stack IS the room's record — the signalling
   * server relays `broadcast` to the sender too, so every client including the
   * presenter holds the same list — and an effect that copied its top element
   * into a single slot was what threw the earlier documents away. Two people
   * presenting one document each now produce two entries, and so two chips.
   */
  const stageMedia = useMemo<StageMedia[]>(() => {
    const out: StageMedia[] = sharedMedia.stack.map((e) => ({
      kind: e.kind,
      id: `${REMOTE_MEDIA_PREFIX}${e.id}`,
      name: e.name,
      url: e.url,
      videoKind: e.source,
      startedAt: e.startedAt,
      paused: e.paused,
      by: e.by,
    }))
    // Dropped the moment the room echoes the same share back, so one
    // presentation is never on the bar twice under two different ids.
    if (localPresenting && !sharedMedia.stack.some((e) => e.id === localPresenting.id)) {
      out.push({ ...localPresenting, by: props.localPeerId ?? null, local: true })
    }
    return out
  }, [sharedMedia.stack, localPresenting, props.localPeerId])

  const stageDocuments = useMemo(
    () => stageMedia.filter((m) => m.kind === 'document').map((m) => ({ id: m.id, name: m.name })),
    [stageMedia],
  )
  const stageVideos = useMemo(
    () => stageMedia.filter((m) => m.kind === 'video').map((m) => ({ id: m.id, name: m.name })),
    [stageMedia],
  )

  // Everything presentable, as one list, and which of them this client is on.
  // The stage branches below read `stage.activeKind` rather than testing each
  // source for truthiness, so starting a second thing adds a chip instead of
  // hiding the first.
  const stage = useStageContent({
    screens: shares.map((s) => ({ key: s.key, name: s.name, isLocal: s.isLocal })),
    whiteboardActive: whiteboard.active,
    whiteboardLabel: t('layouts.whiteboard'),
    documents: stageDocuments,
    videos: stageVideos,
    localPeerId: props.localPeerId ?? null,
    isModerator: roles?.isModerator ?? false,
    myRole: roles?.myRole ?? 'attendee',
    localName: meetingInfo.userName,
    roleOf: (id) => roles?.roleOf(id) ?? 'attendee',
    ready: !!props.chatChannel,
    subscribe: props.chatChannel?.subscribe ?? noopSubscribe,
    sendEvent: props.chatChannel?.sendEvent ?? noopSendEvent,
  })
  stageSelectRef.current = stage.select

  // The one entry the stage is showing, resolved from the chip the viewer is
  // on. Switching chips is therefore all it takes to go back to an earlier
  // document — the entry, its presenter and its close rights all follow.
  const onStage = useMemo(() => {
    const key = stage.active?.key
    if (!key) return null
    return stageMedia.find((m) => mediaStageKey(m.kind, m.id) === key) ?? null
  }, [stage.active?.key, stageMedia])

  const presentingDocument = onStage?.kind === 'document' ? onStage : null
  const presentingVideo = onStage?.kind === 'video' ? onStage : null

  // Who may take THIS presentation off the stage: whoever put it there, and
  // any moderator. A `by` of null is a share that never left this machine — it
  // is nobody else's business, so the local presenter keeps their own close
  // button. Asked of the selected entry rather than of the stack's top, or
  // switching back to an earlier document would offer the rights that belong
  // to whatever is on top.
  const canStopPresenting =
    !onStage || onStage.local || (roles?.isModerator ?? false) || onStage.by === props.localPeerId

  // Who is presenting what is on screen, and whether that is this client.
  //
  // Both layouts used to be handed `meetingInfo.userName` unconditionally, so
  // every viewer was told "You are sharing file" over their OWN name while
  // somebody else presented. `by` carries the presenter's peerId; the roster
  // turns it into a name, because the wire carries ids and names change.
  const shareIsPresenter = !onStage?.by || onStage.by === props.localPeerId
  const sharePresenterName = shareIsPresenter
    ? meetingInfo.userName
    : participants.find((p) => p.id === onStage?.by)?.name ?? 'Participant'

  /**
   * Take one presentation off the stage.
   *
   * By id, never `stop()`'s default: that closes the top of the stack, which
   * stopped being the thing on screen as soon as the viewer could switch chips.
   */
  const closeStageMedia = useCallback(
    (item: StageMedia | null) => {
      if (!item) return
      // The room's spelling of the id. `localPresenting` holds the unprefixed
      // one, so comparing against `item.id` directly would fail to clear it
      // for anything the room had taken — and the stand-in would then put the
      // share back on the bar as its own chip.
      const roomId = item.id.startsWith(REMOTE_MEDIA_PREFIX)
        ? item.id.slice(REMOTE_MEDIA_PREFIX.length)
        : item.id
      setLocalPresenting((cur) => (cur && cur.id === roomId ? null : cur))
      if (item.local) return
      if (!((roles?.isModerator ?? false) || item.by === props.localPeerId)) return
      sharedMedia.stop(roomId)
    },
    [roles?.isModerator, props.localPeerId, sharedMedia],
  )

  // Documents somebody else put on the stage — what switching File Sharing off
  // stops, and so what the host is warned about before it does.
  const othersDocuments = useMemo(
    () => stageMedia.filter((m) => m.kind === 'document' && !m.local && !!m.by && m.by !== props.localPeerId),
    [stageMedia, props.localPeerId],
  )

  // The room has taken the share, so the local stand-in has done its job.
  //
  // Left in place it would resurrect the presentation as a second chip the
  // moment the room's own entry went away — when a moderator closed it, or
  // when the presenter's own close raced the echo. The only thing that should
  // outlive the echo is a share the room never accepted at all, which by
  // definition never appears in the stack.
  useEffect(() => {
    if (!localPresenting) return
    if (sharedMedia.stack.some((e) => e.id === localPresenting.id)) setLocalPresenting(null)
  }, [sharedMedia.stack, localPresenting])

  const layoutSyncEnabled = useMeetingLayoutStore((s) => s.layoutSyncEnabled)

  /**
   * "Sync layout to peers" — the moderator's layout, pushed to the room.
   *
   * Wired here because this is where the layout, the channel and the moderator
   * flag are all in scope. The switch in the Adjust view dialog had no
   * transport at all before this: the state it wrote was read nowhere, and the
   * switch itself was bound to `hideTilesWithoutVideo`.
   */
  useLayoutSync({
    layoutMode,
    setLayoutMode,
    enabled: layoutSyncEnabled,
    isModerator: isRoomModerator,
    localPeerId: props.localPeerId ?? null,
    ready: !!props.chatChannel,
    subscribe: props.chatChannel?.subscribe ?? noopSubscribe,
    sendEvent: props.chatChannel?.sendEvent ?? noopSendEvent,
  })

  // "Pin for all" — a moderator pins one tile on everyone's screen. The rules
  // (who is believed, and why the sender is checked on receipt) are in
  // `pin-sync`. Applied locally first, then sent; receivers apply a moderator's
  // command and ignore anyone else's.
  const [pinnedForAllId, setPinnedForAllIdState] = useState<string | null>(null)
  // Read by the handler below so it can stay stable: VideoTile's memo does not
  // re-render for a new callback, and a tile holding an old one would pin again
  // instead of unpinning.
  const pinnedForAllRef = useRef<string | null>(null)
  const setPinnedForAllId = useCallback((id: string | null) => {
    pinnedForAllRef.current = id
    setPinnedForAllIdState(id)
  }, [])
  const pinChannel = props.chatChannel
  useEffect(() => {
    if (!pinChannel) return
    return pinChannel.subscribe('broadcast', (payload) => {
      const cmd = readPinSync(
        payload,
        props.localPeerId ?? null,
        (peerId) => roles?.roleOf(peerId) === 'host' || roles?.roleOf(peerId) === 'cohost',
      )
      if (!cmd) return
      const store = useMeetingLayoutStore.getState()
      if (cmd.action === 'set') {
        store.pinOnly(cmd.participantId)
        setPinnedForAllId(cmd.participantId)
      } else {
        store.clearPins()
        setPinnedForAllId(null)
      }
    })
  }, [pinChannel, props.localPeerId, roles, setPinnedForAllId])

  const handlePinForAll = useCallback((participantId: string) => {
    if (!isRoomModerator || !pinChannel) return
    const store = useMeetingLayoutStore.getState()
    if (pinnedForAllRef.current === participantId) {
      store.clearPins()
      setPinnedForAllId(null)
      pinChannel.sendEvent('broadcast', { body: unpinForAllBody() })
      return
    }
    store.pinOnly(participantId)
    setPinnedForAllId(participantId)
    pinChannel.sendEvent('broadcast', { body: pinForAllBody(participantId) })
  }, [isRoomModerator, pinChannel, setPinnedForAllId])

  // Tell the media layer the room is listening to something, so the microphone
  // stops running RNNoise over the leak coming back out of everyone's speakers.
  // A paused video is not playing, so it does not count. See the note on
  // `mediaAudioPlaying` in the session store.
  const setMediaAudioPlaying = useMeetingSessionStore((s) => s.setMediaAudioPlaying)
  const videoOnStage = stage.activeKind === 'video' && !!presentingVideo
  const videoPaused = !!presentingVideo?.paused
  useEffect(() => {
    setMediaAudioPlaying(videoOnStage && !videoPaused)
    return () => setMediaAudioPlaying(false)
  }, [videoOnStage, videoPaused, setMediaAudioPlaying])

  // The screen-share switcher predates the stage list and still owns which
  // screen the stage renders, so a chip press has to reach it. Selecting a
  // non-screen item leaves it alone: the screen it points at is still the one
  // to show when the viewer comes back.
  useEffect(() => {
    if (stage.active?.kind === 'screen' && stage.active.key !== selectedShareKey) {
      setSelectedShareKey(stage.active.key)
    }
  }, [stage.active, selectedShareKey])

  // Catch up a participant who arrived after the share began.
  //
  // Only matters against a server that relays `broadcast` without recording the
  // share — an updated one replays on join and this is redundant. Keyed on the
  // roster size so it fires when someone new appears, and only the presenter
  // sends, so N participants do not produce N re-announcements.
  const knownPeerCountRef = useRef(0)
  useEffect(() => {
    const count = allParticipants.length
    const grew = count > knownPeerCountRef.current
    knownPeerCountRef.current = count
    if (grew && sharedMedia.isPresenter) sharedMedia.announce()
  }, [allParticipants.length, sharedMedia.isPresenter, sharedMedia.announce, sharedMedia])

  // A presentation belongs to whoever is presenting it — when they go, so does
  // it. Without this, someone who reloaded or dropped off left their video on
  // everyone's stage with nobody able to close it but a moderator.
  useEffect(() => {
    sharedMedia.pruneAbsent(allParticipants.map((p) => p.id))
    // Identity of the participant objects churns; the ids are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantIdsKey, sharedMedia.pruneAbsent])

  // The layout branches all key off `whiteboardOpen`, so the room's state is
  // mirrored into it rather than threaded through every branch. Local state
  // follows the room and never leads it — which is what stops one client
  // showing a board nobody else has.
  //
  // Re-asserted on divergence rather than only on `active` changing: other
  // paths (the close-all-panels sweep, layout resets) still flip
  // `whiteboardOpen` locally, and without this the board would vanish for that
  // one participant while the room considered it open.
  useEffect(() => {
    if (whiteboardOpen !== whiteboard.active) setWhiteboardOpen(whiteboard.active)
  }, [whiteboard.active, whiteboardOpen, setWhiteboardOpen])

  // Someone else opening the board takes over the stage; say who.
  // A board already open when we arrive is fetched a moment after the join;
  // that is not someone opening it.
  const whiteboardWasActiveRef = useRef(whiteboard.active)
  const channelUpAtRef = useRef(0)
  if (props.chatChannel && !channelUpAtRef.current) channelUpAtRef.current = Date.now()
  useEffect(() => {
    const was = whiteboardWasActiveRef.current
    whiteboardWasActiveRef.current = whiteboard.active
    const settled = channelUpAtRef.current > 0 && Date.now() - channelUpAtRef.current > 4000
    if (!settled || was || !whiteboard.active || !whiteboard.startedBy || whiteboard.startedBy === props.localPeerId) return
    const name = props.participants.find((p) => p.id === whiteboard.startedBy)?.name
      || t('meetingScreen.participantFallbackName')
    meetingAlert(t('meetingScreen.alertOpenedWhiteboard', { name }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteboard.active, whiteboard.startedBy])

  // Whether presenter has ever been held here, and whether the current loss of
  // it has been explained. A ref rather than state: nothing renders from it,
  // and making it state would re-render the whole meeting screen on every role
  // rebroadcast.
  const presenterHistoryRef = useRef(noPresenterHistory())
  presenterHistoryRef.current = observePresenter(presenterHistoryRef.current, !shareNeedsPresenter)

  /**
   * Every route into the whiteboard, answered by one rule.
   *
   * Both the Apps menu and the approval popup's "Start Whiteboard" button come
   * through here. They used to disagree: the menu checked the presenter gate
   * and explained itself, while the popup called `whiteboard.start()` and threw
   * the `'not_presenter'` away — so a host who approved a request and then
   * turned Presenter back off left the participant holding a popup whose only
   * button did nothing at all.
   */
  const handleOpenWhiteboard = useCallback(() => {
    const decision = decideWhiteboardClick({
      canPresent: !shareNeedsPresenter,
      everHadPresenter: presenterHistoryRef.current.everHadPresenter,
      explainedRevocation: presenterHistoryRef.current.explainedRevocation,
      // Asked of the room rather than of `start()`, so the answer is the same
      // whichever button was pressed.
      openByOther: Boolean(whiteboard.active && whiteboard.startedBy && whiteboard.startedBy !== props.localPeerId),
    })

    if (decision === 'explain-revoked') {
      presenterHistoryRef.current = markRevocationExplained(presenterHistoryRef.current)
      toast.info({
        title: t('whiteboard.presenterRevokedTitle'),
        description: t('whiteboard.presenterRevokedDesc'),
      })
      return
    }
    if (decision === 'request') {
      if (presenterAsked) return
      setPresenterRequestFor('whiteboard')
      setMyPresenterOutcome(null)
      setPresenterRequestOpen(true)
      return
    }
    if (decision === 'explain-already-open') {
      toast.info({
        title: t('whiteboard.alreadyOpenTitle'),
        description: t('whiteboard.alreadyOpenDesc'),
      })
      return
    }
    // `start()` still owns the send, and still has the final word: the room
    // state it checks can move between the decision above and this line.
    const refused = whiteboard.start()
    if (refused === 'already_open') {
      toast.info({
        title: t('whiteboard.alreadyOpenTitle'),
        description: t('whiteboard.alreadyOpenDesc'),
      })
    }
  }, [whiteboard, shareNeedsPresenter, setMyPresenterOutcome, t, props.localPeerId])

  const handleCloseWhiteboard = useCallback(() => {
    // Stops it for the room, not just here. The local flag follows the
    // `whiteboard:stopped` broadcast via the effect above.
    whiteboard.stop()
    setLayoutMode("auto")
  }, [whiteboard, setLayoutMode])

  // ── Host controls, held by the room ───────────────────────────────────
  // RoomCoordinator owns every flag and pushes the full set on join, so a late
  // joiner starts from the room's rules rather than this client's defaults.
  //
  // Synchronised is not the same as enforced. Only the two chat gates are
  // refused server-side (RoomChannel's `chat_allowed?/2`); the rest are honoured
  // by the UI below, which a determined client could ignore.
  const setHostControls = useMeetingHostControlsStore((s) => s.setControls)
  const hostControls = useMeetingHostControlsStore(selectMeetingHostControls)
  // Last state the server told us, so applying an inbound broadcast doesn't
  // bounce straight back out as a change of our own.
  const serverHostControls = useRef<Partial<MeetingHostControls> | null>(null)

  useEffect(() => {
    const channel = props.chatChannel
    if (!channel) return
    const unsubscribe = channel.subscribe('host:controls', (payload) => {
      const next = payload as Partial<MeetingHostControls>
      if (!next || typeof next !== 'object') return
      serverHostControls.current = next
      setHostControls(next)
    })
    // Ask, don't wait. `chatChannel` is null until the channel reports joined,
    // so this handler is registered strictly AFTER the server's `after_join`
    // push has already been delivered to nobody. Relying on that push alone
    // left `serverHostControls` null forever, which the publisher below reads
    // as "the room has not spoken yet" and refuses to send — so a host could
    // toggle all day and the room never heard about it.
    channel.sendEvent('host:controls:get', {})
    return unsubscribe
  }, [props.chatChannel, setHostControls])

  useEffect(() => {
    const channel = props.chatChannel
    // Only a moderator may set these, and the server checks that too — an
    // attendee's local toggle changes nothing for anyone else.
    if (!channel || !isRoomModerator) return
    // Until the room has told us where it stands, there is nothing to diff
    // against and pushing would overwrite the room with our defaults.
    const known = serverHostControls.current
    if (!known) return
    const changed = (Object.keys(hostControls) as (keyof MeetingHostControls)[]).some(
      (key) => hostControls[key] !== known[key],
    )
    if (!changed) return

    // Closing a media gate has to stop what is ALREADY running. Otherwise the
    // switch only binds people who had not started yet: three peers already
    // sharing keep sharing, and the host has to find "Stop all screens" by hand
    // to make the thing they just switched off actually true.
    //
    // Fired here rather than in the dialog because this branch is exactly "a
    // moderator on this client changed something" — an inbound broadcast never
    // reaches it, so a co-host's toggle does not make every other moderator
    // fire the same moderation command.
    const closed = (key: keyof MeetingHostControls) => known[key] === true && !hostControls[key]
    if (closed('useMicrophone')) moderation?.muteAll()
    if (closed('useVideo')) moderation?.stopAllVideos()
    if (closed('shareScreen')) moderation?.stopAllScreenShares()
    if (closed('sendFiles')) othersDocuments.forEach(closeStageMedia)

    serverHostControls.current = hostControls
    // The whole map, not a diff: the server merges it and answers with what is
    // now in force, so there is one shape on the wire in both directions.
    channel.sendEvent('host:set_controls', hostControls)
  }, [props.chatChannel, isRoomModerator, hostControls, moderation, othersDocuments, closeStageMedia])

  // Auto-adjust sidebar width when side panel opens/closes
  useEffect(() => {
    if (sidePanelOpen) {
      // When panel opens, clamp rail to max 380px if it was already too wide
      setRightRailWidth((prev) => Math.min(prev, 380))
    }
    // When panel closes, do nothing - preserve current rail width
    // User can drag to resize if needed
  }, [sidePanelOpen])



  // Drag handler for rail resize - positioned after sidePanelOpen is defined
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = railDragRef.current
      if (!drag) return
      // Calculate delta: positive when dragging left (expanding sidebar)
      const dx = drag.startX - e.clientX
      const contentW = contentWRef.current

      // The whiteboard is a wide surface like a document: both want the rail
      // capped so the stage keeps room, where a video grid does not.
      const isWideStage = stage.activeKind === 'whiteboard'

      // Only apply conservative limits when side panel is open
      if (sidePanelOpen) {
        // When panel is open: prevent left rail compression
        // minMain: minimum 550px for main video area
        // maxRail: maximum 380px for right rail
        const minMain = 550
        const maxRail = 380
        const maxRailLimit = (presentingDocument || isWideStage)
          ? (contentW ? Math.min(contentW * 0.5, contentW - minMain, maxRail) : maxRail)
          : (contentW ? Math.min(contentW - minMain, maxRail) : maxRail)
        const next = Math.round(Math.min(maxRailLimit, Math.max(280, drag.startWidth + dx)))
        setRightRailWidth(next)
      } else {
        // When panel is NOT open: use original behavior (no maxRail limit)
        const minMain = 450
        const maxRailLimit = (presentingDocument || isWideStage)
          ? (contentW ? Math.min(contentW * 0.5, contentW - minMain) : 720)
          : (contentW ? Math.max(450, contentW - minMain) : 720)
        const next = Math.round(Math.min(maxRailLimit, Math.max(350, drag.startWidth + dx)))
        setRightRailWidth(next)
      }
    }
    const onUp = () => {
      railDragRef.current = null
    }

    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
    }
  }, [presentingDocument, sidePanelOpen, stage.activeKind])

  // Helper to close all side panels
  const closeAllSidePanels = useCallback(() => {
    setChatOpen(false)
    setParticipantsOpen(false)
    setFilesOpen(false)
    setMeetingInfoPanelOpen(false)
    setPollsPanelOpen(false)
    setSurveysPanelOpen(false)
    setBrandingPanelOpen(false)
    setVideoSharePanelOpen(false)
    setDocumentSharePanelOpen(false)
    setWebinarUsersOpen(false)
    setBreakoutRoomsPanelOpen(false)
    setTranscriptionPanelOpen(false)
    // The whiteboard is not closed here. It is shared room state on the main
    // stage, so dismissing side panels — or tapping the scrim — must not end a
    // board everyone else is drawing on.
  }, [setChatOpen, setParticipantsOpen, setFilesOpen, setMeetingInfoPanelOpen, setPollsPanelOpen, setSurveysPanelOpen, setBrandingPanelOpen, setVideoSharePanelOpen, setDocumentSharePanelOpen, setWebinarUsersOpen, setBreakoutRoomsPanelOpen, setTranscriptionPanelOpen])

  // Switching the chat feature off has to shut a panel that is already open,
  // for everyone. Hiding the button only stops it being opened again — someone
  // reading chat when the host flips the switch would otherwise carry on
  // reading it, and on the host's own screen the panel would sit there with no
  // button left to close it.
  useEffect(() => {
    if (!hostAllowChat) setChatOpen(false)
  }, [hostAllowChat, setChatOpen])

  // ─── Main-stage selection (spotlight / sidebar) ──────────────────────────
  // Priority: an explicit pin always wins → else the SFU's dominant speaker →
  // else the first remote peer → else self (only when alone in the room).
  //
  // The speaker is resolved among REMOTE peers only. Your own tile lives in the
  // spotlight PiP / sidebar rail, so promoting yourself to the main stage would
  // both duplicate you and flip the big tile every time you talk.
  const primaryParticipant = useMemo(() => {
    const pinned = layoutParticipants.find((p) => p.id === pinnedIds[0])
    if (pinned) return pinned
    const speaking = activeSpeakerId
      ? layoutParticipants.find((p) => p.id === activeSpeakerId && !p.isLocal)
      : undefined
    if (speaking) return speaking
    // Nobody has spoken yet (or the last speaker left) — hold a remote peer on
    // the stage so it never sits empty; self takes it only if there is no one else.
    // Additional cameras are skipped here: an unattended document cam should not
    // seize the main stage. Pinning one still promotes it (the branch above).
    const people = layoutParticipants.filter((p) => p.sourceKind !== "additional")
    return people.find((p) => !p.isLocal) ?? people[0] ?? layoutParticipants[0]
  }, [layoutParticipants, pinnedIds, activeSpeakerId])

  // ─── Document Picture-in-Picture ─────────────────────────────────────────
  //
  // Roster: yourself, plus the active speaker — and *only* a genuine active
  // speaker. Deliberately NOT `primaryParticipant`: that one falls back to "any
  // remote peer" so the main stage never sits empty, which in a 360px PiP means
  // someone who joined muted and has never spoken takes half the window. Here
  // an empty second slot is the correct answer, so we match activeSpeakerId
  // strictly and show one tile until somebody actually speaks.
  //
  // `additional` sources (document cams) are excluded — an unattended second
  // camera is not a speaker.
  //
  // Local video is taken from props.localStream because Participant.stream is
  // undefined for the local peer by design (its tile reads MediaStreamContext).
  const { togglePictureInPicture, syncPictureInPicture, closePictureInPicture, pipState } =
    usePictureInPicture()


  // The PiP's second tile: the last OTHER person who spoke — see nextPipSpeaker.
  const [pipSpeakerId, setPipSpeakerId] = useState<string | null>(null)
  useEffect(() => {
    setPipSpeakerId((current) => nextPipSpeaker(current, activeSpeakerId, allParticipants))
  }, [activeSpeakerId, allParticipants])

  const pipParticipants = useMemo<PipParticipant[]>(() => {
    const local = allParticipants.find((p) => p.isLocal)
    // Never a screen-share session, which has no picture of its own to show
    // (useActiveSpeakers already passes over one; this holds whatever names it).
    // `pipSpeakerId`, not `activeSpeakerId` directly: it's the debounced id
    // `nextPipSpeaker` settles on, so PiP doesn't swap faces on every blip.
    const speaker = pipSpeaker(allParticipants, pipSpeakerId)

    const out: PipParticipant[] = []
    if (local) {
      out.push({
        id: local.id,
        name: `${local.name} (You)`,
        avatarName: local.name,
        // Camera off ⇒ no stream ⇒ the tile renders its placeholder instead of
        // a frozen last frame.
        stream: local.videoOn ? (props.localStream ?? local.stream ?? null) : null,
        isLocal: true,
      })
    }
    if (speaker) {
      out.push({
        id: speaker.id,
        name: speaker.name,
        stream: speaker.videoOn ? (speaker.stream ?? null) : null,
      })
    }
    return out
  }, [allParticipants, pipSpeakerId, props.localStream])

  const handleToggleDocumentPiP = useCallback(() => {
    void togglePictureInPicture(
      pipParticipants,
      {
        participantName: `${meetingInfo.userName} (You)`,
        // Routed through the same store actions the control bar uses, so the
        // two surfaces cannot diverge — neither owns the state.
        onToggleMic: () => useMeetingSessionStore.getState().toggleMic(),
        onToggleCam: () => useMeetingSessionStore.getState().toggleCam(),
        onLeave: handleLeaveMeeting,
      },
      { micOn, camOn },
    )
  }, [togglePictureInPicture, pipParticipants, meetingInfo.userName, handleLeaveMeeting, micOn, camOn])

  // Keep an open PiP window in step with the call: someone joins or leaves, the
  // speaker changes, a camera or mic toggles anywhere. No-op while closed.
  useEffect(() => {
    syncPictureInPicture(pipParticipants, { micOn, camOn })
  }, [syncPictureInPicture, pipParticipants, micOn, camOn])

  // The PiP window is a renderer too, so its speaker counts as on screen.
  // Only grid tiles declared themselves (VideoTile's `retain`), so a speaker
  // shown in PiP but not on the grid's page was paused by lastN — and under
  // LAZY_CONSUME never consumed at all — leaving the PiP tile frozen or blank.
  // Keyed by string so a roster re-render does not release and re-retain it.
  const pipSpeakerKey = useMemo(() => {
    const speaker = pipSpeaker(allParticipants, pipSpeakerId)
    return speaker ? layerKeyForTile(speaker) : null
  }, [allParticipants, pipSpeakerId])
  useEffect(() => {
    if (!pipState.isActive) return
    return renderedTiles.retain(pipSpeakerKey)
  }, [pipState.isActive, pipSpeakerKey])

  // ─── Auto PiP on tab switch ──────────────────────────────────────────────
  //
  // Deliberately NOT `requestWindow()` inside a visibilitychange handler:
  // Document PiP requires *transient user activation*, and once the tab is
  // hidden there is none left — the call throws NotAllowedError. The sanctioned
  // way to enter automatically is the Media Session "enterpictureinpicture"
  // action, which the browser invokes itself as the user switches away.
  //
  // Chrome (120+, desktop; Edge likewise) invokes it on a TAB switch for a page
  // that is capturing the camera or microphone, while the user leaves the
  // browser's "Automatic picture-in-picture" site setting on (its default).
  // Muting keeps the capture (`track.enabled = false`), so a muted host still
  // qualifies. Switching to another APPLICATION fires nothing — the browser
  // window is still showing, and there is no API that opens PiP for it.
  // An auto-opened window closes when the user comes back to the meeting; one
  // they opened by hand stays until they close it. Three flags, all refs (no
  // re-render needed):
  //   opened — the window now showing was opened automatically
  //   armed  — we are watching for the user to come back
  //   away   — they have left the meeting since we started watching
  // "Come back" is either signal: the tab visible again (they switched tabs)
  // or this window focused again (a whole-screen or window share never hides
  // the tab — they went to another app and returned).
  // Never on a phone browser: PiP is not offered there (see ControlBar), and a
  // saved "on" from a desktop session must not open one behind the user's back.
  const autoPipEnabled = autoPictureInPicture && !responsiveDevice.isMobileDevice
  const autoPipOpenedRef = useRef(false)
  const autoPipArmedRef = useRef(false)
  const autoPipAwayRef = useRef(false)

  const resetAutoPip = useCallback(() => {
    autoPipOpenedRef.current = false
    autoPipArmedRef.current = false
    autoPipAwayRef.current = false
  }, [])

  // Whatever closed the window — the user, a return to the meeting, leaving —
  // it is no longer an auto-opened one. Without this, a window the user closed
  // by hand left `opened` set, and the next PiP they opened THEMSELVES was shut
  // the moment they came back to the meeting.
  useEffect(() => {
    if (!pipState.isActive) resetAutoPip()
  }, [pipState.isActive, resetAutoPip])

  /** The menu's Picture-in-Picture item: the user's own window, never auto-closed. */
  const handleManualPiP = useCallback(() => {
    resetAutoPip()
    handleToggleDocumentPiP()
  }, [resetAutoPip, handleToggleDocumentPiP])

  // What the handler acts on, read at the moment Chrome invokes it. Through a
  // ref so the handler is registered ONCE per setting change: depending on
  // these directly re-registered it on every speaker change and mic/camera
  // toggle, each time briefly telling Chrome the page had no handler at all.
  const autoPipActRef = useRef({ open: handleToggleDocumentPiP, active: pipState.isActive })
  autoPipActRef.current = { open: handleToggleDocumentPiP, active: pipState.isActive }

  // Share click → PiP. Asked for in the same click as the picker (see
  // `toggleScreenShare` above), so it opens whatever the user goes on to pick.
  // Watching for the return starts only once the share has SETTLED: the picker
  // and the new PiP window both take focus, and Chrome hands it back to this
  // window for a moment before moving to a shared tab — measured at ~100 ms
  // after the share started, which read as "came back" and shut the window it
  // had just opened. Whatever state things are in after the settle is the
  // truth: on the shared tab or in another app, the user is away.
  // A cancelled picker takes the window back down.
  pipOnShareStartRef.current = (started) => {
    if (!autoPipEnabled) return
    const { open, active } = autoPipActRef.current
    if (active) return
    resetAutoPip()
    autoPipOpenedRef.current = true
    vclog("screen", "auto-pip: opening for screen share")
    open()
    void started.then((ok) => {
      if (!autoPipOpenedRef.current) return
      if (!ok) {
        vclog("screen", "auto-pip: share cancelled, closing")
        resetAutoPip()
        void closePictureInPicture()
        return
      }
      window.setTimeout(() => {
        if (!autoPipOpenedRef.current) return
        autoPipArmedRef.current = true
        autoPipAwayRef.current = document.visibilityState === "hidden" || !document.hasFocus()
      }, AUTO_PIP_SHARE_SETTLE_MS)
    })
  }

  useEffect(() => {
    if (!autoPipEnabled) return
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return

    // "enterpictureinpicture" is not in lib.dom's MediaSessionAction union yet.
    const ms = navigator.mediaSession as unknown as {
      setActionHandler: (action: string, handler: (() => void) | null) => void
    }

    try {
      ms.setActionHandler("enterpictureinpicture", () => {
        // Including while presenting: that is when the host most needs the call
        // in view, having just gone to the tab they share. This used to bail
        // out for a "screen-share popup" that was never built, so a presenter
        // switching tabs got nothing at all.
        const { open, active } = autoPipActRef.current
        vclog("screen", "auto-pip: browser asked to enter", { alreadyOpen: active })
        if (active) return
        // Chrome only asks once the user has already gone (tab switched away,
        // or the window covered), so this starts out away and armed.
        autoPipOpenedRef.current = true
        autoPipArmedRef.current = true
        autoPipAwayRef.current = true
        open()
      })
      vclog("screen", "auto-pip: handler registered")
    } catch (err) {
      // Action unsupported here — nothing registered, nothing to clean up.
      vclog("screen", "auto-pip: handler unsupported", { err: String(err) })
      return
    }

    return () => {
      try {
        ms.setActionHandler("enterpictureinpicture", null)
      } catch {
        // ignore
      }
    }
  }, [autoPipEnabled])

  /**
   * Close the Picture-in-Picture window when this screen goes.
   *
   * Leaving with Alt+L exited the meeting and left the PiP window floating over
   * whatever the user went back to — a live-looking tile of a call they were no
   * longer in.
   *
   * Hooked to the UNMOUNT rather than to the leave click, because there is no
   * single click. The meeting is left from Alt+L, from the control bar, from
   * End for all, by being removed by the host, and by navigating away — four of
   * those go through `onLeaveMeeting` at different call sites and one does not
   * go through this component at all. `screen === "meeting"` in VcApp is what
   * they all converge on, so unmounting is the one event that cannot be missed.
   *
   * Through a ref so the effect keeps empty deps: `closePictureInPicture` is
   * rebuilt on some renders, and depending on it directly would tear the window
   * down mid-meeting every time its identity changed.
   */
  const closePipRef = useRef(closePictureInPicture)
  closePipRef.current = closePictureInPicture
  useEffect(() => () => { void closePipRef.current() }, [])

  // The page behind the meeting takes the meeting's dark surface while it is on
  // screen, so a moment before it repaints (back from another app) reads as
  // the meeting, not a white flash. See `html.vc-in-meeting` in index.css.
  useEffect(() => {
    const root = document.documentElement
    root.classList.add('vc-in-meeting')
    return () => root.classList.remove('vc-in-meeting')
  }, [])

  /**
   * End the participation state with the visit.
   *
   * The session store is module-scoped and never reset, so leaving and
   * rejoining without a page reload began the new visit holding the old one's
   * flags. A hand left raised stayed raised on this screen and reached nobody:
   * the mirror effect above skips its first run, records the stale `true`, and
   * then finds nothing changed — so no `hand:raise` was ever sent, and the only
   * person who could see the hand was the one who had raised it.
   *
   * On unmount for the same reason the PiP window is: there are five ways out
   * of a meeting and this is the one they all pass through.
   */
  useEffect(() => () => {
    useMeetingSessionStore.getState().resetParticipationState()
  }, [])

  // Coming back to the meeting closes a window that auto-PiP opened. One the
  // user opened by hand is left alone — they asked for it, and returning should
  // not dismiss it. See the flags above `resetAutoPip`.
  useEffect(() => {
    const onLeave = () => {
      if (autoPipArmedRef.current) autoPipAwayRef.current = true
    }
    const onReturn = () => {
      if (document.visibilityState !== "visible" || !document.hasFocus()) return
      if (!autoPipOpenedRef.current || !autoPipArmedRef.current || !autoPipAwayRef.current) return
      vclog("screen", "auto-pip: back in the meeting, closing")
      resetAutoPip()
      void closePictureInPicture()
    }
    const onVisibility = () => (document.visibilityState === "hidden" ? onLeave() : onReturn())
    window.addEventListener("blur", onLeave)
    window.addEventListener("focus", onReturn)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      window.removeEventListener("blur", onLeave)
      window.removeEventListener("focus", onReturn)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [closePictureInPicture, resetAutoPip])

  // Non-pinned peers — only ever a step towards `otherParticipants`, which is
  // for the layouts where a participant IS the main stage. Whoever is on the
  // stage must not also be in the rail.
  //
  // This used to feed the document / video rail as well, directly against its
  // own description of itself ("so everyone still belongs in the rail"): the
  // stage there is the document, so a pin puts a participant nowhere, and
  // neither DocumentPresentationLayout nor VideoShareLayout has a pinned area
  // to put them in — `pinnedIds` reaches them only as a badge. Clicking a rail
  // tile therefore deleted it from the screen. `pinnedIds` also starts at
  // `['you']`, so self was missing from that rail before anything was clicked.
  const nonPinnedParticipants = useMemo(
    () => layoutParticipants.filter((p) => !pinnedIds.includes(p.id)),
    [layoutParticipants, pinnedIds],
  )

  // Everyone who is not on the main stage. Excluding the primary is what keeps
  // self out of the sidebar rail while self is also the big tile.
  //
  // NOT for "pinned" mode — see `rightRailParticipants`. This used to claim the
  // primary was always in `pinnedIds` there and so cost nothing; it is not.
  // `primaryParticipant` resolves `pinnedIds[0]`, and that is the sentinel
  // 'you' the store rests on, which matches no real participant once a peerId
  // exists — so the primary falls through to "first remote peer", is not
  // pinned, and got dropped from the rail while the pinned grid rendered only
  // the pinned. That participant appeared nowhere at all.
  const otherParticipants = useMemo(
    () => nonPinnedParticipants.filter((p) => p.id !== primaryParticipant?.id),
    [nonPinnedParticipants, primaryParticipant],
  )

  // Spotlight corner PiP — always your own tile, never a remote peer. Sourced
  // from layoutParticipants so "hide tiles with no video" / "hidden self video"
  // still apply, and dropped entirely when self already holds the main stage.
  const spotlightSelfTile = useMemo(
    // "Your own tile" means your face, not one of your extra cameras — those
    // belong in the rail with everything else.
    () => layoutParticipants.find(
      (p) => p.isLocal && p.sourceKind !== "additional" && p.id !== primaryParticipant?.id,
    ),
    [layoutParticipants, primaryParticipant],
  )

  const isScreenShareLayoutActive = isPresenting

  // The whiteboard occupies the main stage exactly like a screen share, so the
  // rail must list EVERYONE — self included.
  //
  // Without this it fell through to `otherParticipants`, which drops whoever
  // holds the spotlight. On the shared stage nobody is on the stage, so that
  // subtraction removed a real participant for no reason: in a two-person
  // meeting the remote peer was the spotlight, leaving a rail containing only
  // your own tile.
  // What is ON THE STAGE, not merely what is running.
  //
  // This keyed off `whiteboardOpen`, which is true whenever a board exists
  // anywhere in the room — so the rail and its splitter were reserved for stage
  // content that the stage was not actually showing. Two ways that went wrong:
  // a ghost board left behind by a departed presenter put the whole layout into
  // presentation mode over an empty pane, and picking a screen share from the
  // switcher while a board stayed open left the layout describing the board.
  // Following `stage.activeKind` makes the layout agree with the switcher,
  // which is the thing that decides what is rendered.
  const isStageContentActive = isScreenShareLayoutActive || stage.activeKind === 'whiteboard'
  // A document or a video on the stage is the same case as a screen share or a
  // whiteboard: the stage is not a person, so the rail holds the whole room.
  const contentOnStage = isStageContentActive || !!presentingDocument || !!presentingVideo
  const rightRailParticipants =
    contentOnStage
      ? stageRailParticipants
      : // "Pinned" has no single big tile to keep out of the rail — its main
        // area IS the grid of pinned participants — so the rail is everyone
        // else. Excluding the primary here as well hid a real participant:
        // pinning yourself in a two-person call left the other person in
        // neither half, and the screen showed only the pin.
        layoutMode === 'pinned'
        ? nonPinnedParticipants
        : otherParticipants
  const rightRailPageCount = Math.max(1, Math.ceil(rightRailParticipants.length / maxTilesPerPage))
  const safeRightRailPage = Math.min(rightRailPage, rightRailPageCount - 1)

  useEffect(() => {
    if (rightRailPage !== safeRightRailPage) {
      const timeoutId = setTimeout(() => setRightRailPage(safeRightRailPage), 0)
      return () => clearTimeout(timeoutId)
    }
  }, [rightRailPage, safeRightRailPage, setRightRailPage])

  // Dynamic layout for sidebar rail (similar to VideoGrid)
  const RAIL_GAP = 8
  const RAIL_PAD_Y = 48 * 2 // pt-12 + pb-12
  const RAIL_ASPECT = 16 / 9

  // Compute optimal row distribution for sidebar (max 6 tiles)
  // This mirrors the computeRowDistribution from VideoGrid
  const computeRailLayout = (count: number, containerW: number, containerH: number) => {
    // Rows share the rail's height evenly, and each tile fills its row's height
    // (RailTiles caps the width at 16:9) — the rail as designed. Rows sized to a
    // 16:9 tile instead left every tall rail as a short stack with empty space
    // around it; filled rows have no gaps to leave and always sum to exactly
    // the available height.
    const evenRows = (rows: number[]) => ({
      rows,
      rowH: Math.max(0, (containerH - RAIL_GAP * (rows.length - 1)) / rows.length),
    })

    if (count === 0 || containerW === 0 || containerH === 0) {
      // One row PER TILE, not one row total. Returning `[1]` here dropped every
      // tile after the first: the chunking below slices the participants by
      // these row sizes, so a single row of one is a rail with one tile in it
      // no matter how many people are in the meeting. Reachable whenever the
      // rail has not been measured yet, which is every first paint.
      //
      // No tiles means no rows, NOT one empty one. `Math.max(1, count)` made a
      // row for nobody, and a single empty row is still a row: `railRowChunks`
      // came out as `[[]]`, whose length is 1, so the callers' `length === 0`
      // empty state never ran and the rail painted a full-height blank beside
      // the shared document instead.
      if (count === 0) return { rows: [], rowH: containerH }
      return { rows: Array.from({ length: count }, () => 1), rowH: containerH }
    }

    // Special case: 2 tiles should be in same row (unless sidebar is narrow - then stack vertically)
    if (count === 2) {
      // If sidebar is narrow, stack vertically to avoid tall rectangles
      if (containerW < 720) return evenRows([1, 1])
      return evenRows([2])
    }

    // Special case: 3 tiles - stack vertically in narrow sidebar
    if (count === 3) {
      if (containerW < 420) return evenRows([1, 1, 1])
    }

    let bestRows = 1
    let bestScore = -Infinity

    // Try different row counts and pick the one that gives best 16:9 match
    for (let numRows = 1; numRows <= Math.min(count, 6); numRows++) {
      const rowH = (containerH - RAIL_GAP * (numRows - 1)) / numRows
      const avgCols = count / numRows
      const tileW = (containerW - RAIL_GAP * (avgCols - 1)) / avgCols
      const ratio = tileW / rowH

      // Score based on how close to 16:9 and how much area is used
      const aspectScore = ratio > RAIL_ASPECT ? RAIL_ASPECT / ratio : ratio / RAIL_ASPECT
      const areaScore = aspectScore * tileW * rowH

      // Calculate how tiles would distribute in this row count
      const base = Math.floor(count / numRows)
      const extra = count % numRows
      const singleTileRows = base === 1 ? numRows - extra : 0

      // Penalize layouts with single-tile rows (prefer pairs)
      const distributionPenalty = singleTileRows > 0 ? singleTileRows * 0.3 : 0

      // Boost for even distribution where all rows have similar counts
      const maxCols = base + (extra > 0 ? 1 : 0)
      const minCols = base
      const evennessBonus = (maxCols - minCols) <= 1 ? 0.2 : 0

      const score = areaScore * (1 + evennessBonus - distributionPenalty)

      if (score > bestScore) {
        bestScore = score
        bestRows = numRows
      }
    }

    // Distribute tiles: floor in early rows, ceil in later rows
    const base = Math.floor(count / bestRows)
    const extra = count % bestRows
    const rows: number[] = []
    for (let r = 0; r < bestRows; r++) {
      rows.push(r < bestRows - extra ? base : base + 1)
    }

    return evenRows(rows)
  }

  const railAvailH = Math.max(0, rightRailH - RAIL_PAD_Y)

  // Limit to max 6 tiles per page based on actual right-rail participant source
  const rightRailSlice = rightRailParticipants.slice(
    safeRightRailPage * maxTilesPerPage,
    safeRightRailPage * maxTilesPerPage + maxTilesPerPage,
  )

  // Calculate dynamic layout for current slice
  const railLayout = computeRailLayout(rightRailSlice.length, rightRailWidth, railAvailH)
  const railRowSizes = railLayout.rows
  const railRowH = railLayout.rowH

  // Create row chunks
  const railRowChunks: typeof rightRailSlice[] = []
  let cursor = 0
  for (const size of railRowSizes) {
    railRowChunks.push(rightRailSlice.slice(cursor, cursor + size))
    cursor += size
  }

  // Pinned layout - apply same dynamic layout as sidebar with 6 tile limit
  const [pinnedAreaEl, setPinnedAreaEl] = useState<HTMLDivElement | null>(null)
  const [pinnedAreaH, setPinnedAreaH] = useState(0)
  const [pinnedAreaW, setPinnedAreaW] = useState(0)

  useEffect(() => {
    if (!pinnedAreaEl) return
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      setPinnedAreaH(rect.height)
      setPinnedAreaW(rect.width)
    })
    ro.observe(pinnedAreaEl)
    return () => ro.disconnect()
  }, [pinnedAreaEl])

  // Sync pinned page when out of range - now from useMeetingLayout hook (maxTilesPerPinnedPage, pinnedPageCount, safePinnedPage)

  // Get pinned participants data
  //
  // Indexed and memoised: this ran in the render body, so every render walked
  // the whole tile list once per pinned id — up to six scans of a 250-entry
  // array for a list that only changes when a pin or the roster does.
  const pinnedParticipants = useMemo(() => {
    const byId = new Map(layoutParticipants.map((lp) => [lp.id, lp]))
    return pinnedIds
      .map((pid) => byId.get(pid))
      .filter((p): p is NonNullable<typeof p> => p !== undefined)
  }, [pinnedIds, layoutParticipants])

  // Slice pinned participants for pagination
  const pinnedSlice = pinnedParticipants.slice(
    safePinnedPage * maxTilesPerPinnedPage,
    safePinnedPage * maxTilesPerPinnedPage + maxTilesPerPinnedPage,
  )

  // Calculate dynamic layout for pinned area
  const pinnedAvailH = Math.max(0, pinnedAreaH - RAIL_PAD_Y)
  const pinnedLayout = computeRailLayout(pinnedSlice.length, pinnedAreaW, pinnedAvailH)
  const pinnedRowSizes = pinnedLayout.rows
  const pinnedRowH = pinnedLayout.rowH

  // Create row chunks for pinned area
  const pinnedRowChunks: typeof pinnedSlice[] = []
  let pinnedCursor = 0
  for (const size of pinnedRowSizes) {
    pinnedRowChunks.push(pinnedSlice.slice(pinnedCursor, pinnedCursor + size))
    pinnedCursor += size
  }

  // Sync right rail page - now from useMeetingLayout hook

  // A shared document or video on a phone: the content takes the screen and
  // everyone else is a scrollable strip below it (beside it in landscape),
  // instead of the desktop layout's fixed-width rail, which squeezed the
  // content into a sliver and pushed the tiles off to the right.
  const wrapMobileStage = (content: React.ReactNode) =>
    isMobileOrLandscape ? (
      <MobileStageLayout
        stage={content}
        participants={stageRailParticipants}
        isLandscape={isLandscape}
        onVisibleCountChange={setStripVisibleCount}
        renderTile={(p) => (
          <VideoTile
            participant={p}
            reaction={reactionFor(p?.id)}
            isPinned={pinnedIds.includes(p.id)}
            onPin={handlePin}
            onPinForAll={isRoomModerator ? handlePinForAll : undefined}
            pinnedForAllId={pinnedForAllId}
            onMaximize={handleMaximize}
            onOpenChat={handleOpenChatFromTile}
            virtualBackgroundUrl={p.isLocal ? selectedBackground?.url ?? null : null}
          />
        )}
      />
    ) : (
      content
    )

  return (
    <div
      ref={rootRef}
      // Focusable only in the sense that code can focus it — never in the tab
      // order. Entering full screen parks focus here so the shortcuts, which
      // listen on `window`, keep hearing the keyboard instead of an embedded
      // player's iframe swallowing it. `outline-none` because this is a
      // programmatic landing spot, not something the user navigated to.
      tabIndex={-1}
      className="flex h-[100dvh] bg-vc-surface text-vc-text overflow-hidden outline-none"
      onMouseEnter={() => setIsHoveringScreen(true)}
      onMouseLeave={() => setIsHoveringScreen(false)}
    >
      <ShortcutFeedbackOverlay />

      {/* Where a participant is told the rooms have opened. Driven by
          `invitePromptOpen`, which survives being dismissed so the Breakout
          Rooms control can offer it again — the host who cancelled needs a way
          back in as much as anybody. */}
      <BreakoutJoinDialog
        open={invitePromptOpen && breakoutActive}
        onOpenChange={setInvitePromptOpen}
        rooms={activeBreakoutRooms}
        assignedRoomId={myAssignedRoomId}
        selfSelect={breakoutAssignment === 'self'}
        currentRoomId={currentBreakoutRoomId}
        onReturnToMain={() => { void leaveBreakoutRoom() }}
        busy={breakoutSwitching}
        capacity={roomCapacity}
        onJoin={(roomId) => { void joinBreakoutRoom(roomId) }}
      />

      {/* Toast Notifications - Top Left */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} onClearAll={clearToasts} clearAllLabel={t('panels.clearAll')} />

      {isMyVideoPaused && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-2xl border border-white/20 bg-vc-surface-elevated/95 p-6 text-center shadow-2xl">
            <h3 className="text-xl font-semibold text-white">Invite participants to join meeting </h3>
            <p className="mt-2 text-sm text-white/70">Your video and audio are paused.</p>
            <Button
              type="button"
              onClick={handleResumeMyVideo}
              className="mt-5 w-full"
            >
              Resume My Video
            </Button>
          </div>
        </div>
      )}

      <TranscriptionConsentBanner
        // Only surfaces when the session's meeting record explicitly enabled
        // transcription. Dismissing hides it for the rest of the session.
        show={transcriptionEnabledForSession && !transcriptionBannerDismissed}
        onDismiss={() => setTranscriptionBannerDismissed(true)}
      />

      <WebinarStartBanner
        // Only surfaces when this `/meeting/:id` is actually a webinar (the
        // id resolves in the webinar store, not the meetings store).
        show={isWebinarSession && !webinarBannerDismissed}
        onStart={() => setWebinarBannerDismissed(true)}
        onDismiss={() => setWebinarBannerDismissed(true)}
      />

      {/* Recording Status Bar — hidden; controls moved into LeftSection */}
      {/* <RecordingStatusBar
        isRecording={recording.isRecording}
        isPaused={recording.isPaused}
        formattedDuration={recording.formattedDuration}
        storageMode={recording.storageMode}
        onStop={recording.stopRecording}
        onPause={recording.pauseRecording}
        onResume={recording.resumeRecording}
      /> */}

      <BroadcastIndicator
        isLive={broadcasting.isLive}
        platformLabel={broadcasting.platformLabel}
        durationSeconds={broadcasting.durationSeconds}
        connection={broadcasting.connection}
        onStop={() => void broadcasting.stop()}
      />

      {/* Which room this is. Only while breakout rooms exist — before that
          there is one room and naming it says nothing. */}
      <RoomNameBadge roomName={currentRoomName} offset={broadcasting.isLive} />



      {/* Main container with content area and side panel */}
      <div className="flex flex-col flex-1 min-w-0 h-[100dvh] overflow-hidden">
        {/* Content + Side Panel Row */}
        <div ref={contentRef} className="flex flex-1 min-w-0 overflow-hidden">
          {/* Main content area - flex to fill available space */}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <main
              className="relative flex flex-col flex-1 min-w-0 lg:min-w-[500px] px-4 pt-4 overflow-hidden"
              style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
            >
              {/* Waiting to join — top left hover widget.
                  Moderators only. `useLobby` already refuses to fetch for
                  anyone else, so this is the second lock rather than the
                  first: a list that arrived by any other route — a stale
                  render, a future caller — still must not offer admit and
                  reject to an attendee. */}
              {isRoomModerator && (
              <JoinRequestsWidget
                joinRequests={joinRequests}
                onAdmit={handleAdmitFromLobby}
                onDeny={handleRejectFromLobby}
                onAdmitAll={handleAdmitAllFromLobby}
                permissionRequests={permissionRequests}
                onApproveRequest={approvePermissionRequest}
                onRejectRequest={rejectPermissionRequest}
              />
              )}
              {!isRoomModerator && (presenterAsked || (myPresenterOutcome === 'approved' && !presenterRequestOpen)) && (
                <MyRequestChip
                  subject={presenterRequestFor}
                  approved={!presenterAsked}
                  onCancel={() => {
                    moderation?.cancelPresenterRequest()
                    setPresenterAsked(false)
                  }}
                  onStart={() => {
                    setMyPresenterOutcome(null)
                    startApprovedShare()
                  }}
                  onDismiss={() => setMyPresenterOutcome(null)}
                />
              )}

              {/* Switcher for everything being presented. Renders itself away
                  when there is only one, so the single-share case is unchanged. */}
              <StageContentBar
                items={stage.items}
                activeKey={stage.active?.key ?? null}
                onSelect={stage.select}
                canSync={stage.canSync}
                syncActive={stage.isSyncOwner}
                onSync={stage.syncRoom}
                following={stage.following}
                followingLocked={stage.followingLocked}
                onStopFollowing={stage.stopFollowing}
                syncOnByLabel={
                  stage.syncOn && !stage.isSyncOwner && (roles?.isModerator ?? false)
                    ? t('layouts.syncOnBy', { name: stage.syncOwnerName ?? '' })
                    : null
                }
                onTurnOffSync={stage.hostCanTurnOff ? stage.turnOffSync : undefined}
                turnOffSyncLabel={t('layouts.turnOffSync')}
                syncLabel={t('layouts.syncEveryone')}
                syncingLabel={t('layouts.syncingEveryone')}
                followingLabel={t('layouts.followingHost')}
              />

              {/* Video Grid */}
              <div className="relative flex-1 min-h-0">
                <div className="h-full w-full">
                  {stage.activeKind === 'whiteboard' ? (
                    isMobileOrLandscape ? (
                      // The same phone layout as a shared screen or document:
                      // the board takes the stage and everyone else is a strip
                      // that scrolls. It had 40% of the height in portrait and
                      // folded everyone past three into an "N others" tile.
                      wrapMobileStage(
                        <div className="relative h-full w-full overflow-hidden rounded-xl bg-card">
                          <WhiteboardPanel
                            isOpen={whiteboardOpen}
                            url={whiteboard.url}
                            canClose={whiteboard.canClose}
                            configured={whiteboard.configured}
                            onClose={handleCloseWhiteboard}
                            // Watching, not drawing: show the board the way a
                            // laptop shows it. The presenter keeps the phone's
                            // own layout, which is where its touch tools are.
                            fitDesktopCanvas={!whiteboard.canEdit}
                          />
                        </div>,
                      )
                    ) : (
                      // Desktop: Use original sidebar layout
                      <div className="flex h-full w-full min-w-0 overflow-hidden">
                        <div className="min-h-0 min-w-0 flex-1 pr-1 sm:pr-2">
                          <div className="relative h-full w-full overflow-hidden rounded-lg sm:rounded-xl md:rounded-[14px] ring-1 ring-white/10 bg-card">
                            <WhiteboardPanel
                              isOpen={whiteboardOpen}
                                url={whiteboard.url}
                                canClose={whiteboard.canClose}
                                configured={whiteboard.configured}
                              onClose={handleCloseWhiteboard}
                            />
                          </div>
                        </div>

                        <div
                          role="separator"
                          aria-orientation="vertical"
                          onPointerDown={(e) => {
                            railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
                          }}
                          className="relative mr-2 hidden w-12 cursor-col-resize select-none items-center justify-center lg:flex -ml-4"
                          title="Resize"
                        >
                          <div className="grid gap-1">
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                          </div>
                        </div>

                        <aside className="min-h-0 shrink-0 overflow-hidden" style={{ width: rightRailWidth }}>
                          <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
                            {rightRailPageCount > 1 ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setRightRailPage((p) => Math.max(0, p - 1))}
                                  disabled={safeRightRailPage <= 0}
                                  className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                  title="Previous"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                                  disabled={safeRightRailPage >= rightRailPageCount - 1}
                                  className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                  title="Next"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
                                  </svg>
                                </button>
                                <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                                  {safeRightRailPage + 1}/{rightRailPageCount}
                                </div>
                              </>
                            ) : null}

                            <RailTiles
                              rows={railRowChunks}
                              rowHeight={railRowH}
                              onSelect={handlePin}
                              renderTile={(p) => (
                                <VideoTile
                                  participant={p}
                                  reaction={reactionFor(p?.id)}
                                  isPinned={pinnedIds.includes(p.id)}
                                  onPin={handlePin}
                                  onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                                  pinnedForAllId={pinnedForAllId}
                                  onMaximize={handleMaximize}
                                />
                              )}
                            />
                          </div>
                        </aside>
                      </div>
                    )
                  ) : stage.activeKind === 'screen' ? (
                    isMobileOrLandscape ? (
                      // Mobile: Use responsive ScreenShareLayout
                      <ScreenShareLayout
                        screenVideoRef={screenVideoRef}
                        attachVideo={attachPresentationVideo}
                        participants={stageRailParticipants}
                        reactionFor={reactionFor}
                        pinnedIds={pinnedIds}
                        onPin={handlePin}
                        onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                        pinnedForAllId={pinnedForAllId}
                        onMaximize={handleMaximize}
                        onOpenChat={handleOpenChatFromTile}
                        onOpenParticipants={handleOpenParticipants}
                        selectedBackground={selectedBackground}
                        presenterName={presenterName}
                        onStopPresenting={canStopActiveShare ? stopActiveShare : undefined}
                        isOwnShare={activeShare?.isLocal ?? true}
                        isLandscape={isLandscape} // Row layout in landscape, column in portrait
                        shareKey={activeShare?.key}
                        onVisibleCountChange={setStripVisibleCount}
                      />
                    ) : (
                      // Desktop: Use original sidebar layout
                      <div className="flex h-full w-full min-w-0 overflow-hidden">
                        {/* Main area - screen share */}
                        <div className="min-h-0 min-w-0 flex-1 pr-2">
                          <div ref={screenStageRef} className="relative h-full w-full overflow-hidden rounded-[12px] ring-1 ring-white/10 bg-black group">
                            <ZoomPanVideo
                              zoom={screenZoomLevel}
                              videoRef={attachPresentationVideo}
                              onMetricsChange={setScreenZoomMetrics}
                              onSourceChange={() => dispatchScreenZoom({ type: 'shareChanged' })}
                              autoPlay
                              playsInline
                              muted={isLocalPresenter}
                            />
                            {/* Presenter info overlay */}
                            <div className="absolute bottom-4 left-4 z-10">
                              <div className="flex items-center gap-2 rounded-lg bg-black/60 px-3 py-2 text-white">
                                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-sm font-bold">
                                  {presenterName[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{presenterName}</p>
                                  <p className="text-xs text-white/70">Presenting</p>
                                </div>
                              </div>
                            </div>

                            {/* Stop sharing, on the thing being shared.

                                The mobile ScreenShareLayout has had this at its
                                own top-right for as long as it has existed; the
                                desktop branch is a hand-rolled <video> with an
                                overlay per corner — presenter name, share
                                switcher, zoom — and simply never grew one. So
                                the presenter had to go back to the control bar
                                to stop, while looking straight at the share.

                                It stops WHAT IS ON THE STAGE, not "my share":
                                with several people sharing, `isLocalPresenter`
                                stays true while you page over to somebody
                                else's screen, so keying off it would put a
                                button reading "stop" over a screen it would not
                                stop. Your own share ends it; anyone else's is a
                                moderator action — the same
                                `moderate:stop_screenshare` the Participants
                                list and the tile menu already send, which the
                                channel gates on the sender being a moderator,
                                so this is a second door to an existing power
                                rather than a new one. */}
                            {canStopActiveShare && (
                              <div className="absolute top-4 right-4 z-10">
                                <button
                                  type="button"
                                  onClick={stopActiveShare}
                                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                                  title={
                                    activeShare?.isLocal
                                      ? t("screenShare.stopSharing")
                                      : t("videoTile.stopScreenshare")
                                  }
                                >
                                  <span>
                                    {activeShare?.isLocal
                                      ? t("screenShare.stopSharing")
                                      : t("videoTile.stopScreenshare")}
                                  </span>
                                </button>
                              </div>
                            )}

                            {/* More than one person sharing: only one fits the
                                stage, so give the viewer a way to reach the
                                others. Hidden entirely for a single share. */}
                            {shares.length > 1 && (
                              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 z-10">
                                <button
                                  onClick={() => stepShare(-1)}
                                  className="grid h-8 w-8 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors"
                                  title="Previous screen share"
                                  aria-label="Previous screen share"
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="px-2 text-xs text-white/90 tabular-nums whitespace-nowrap">
                                  {activeShareIndex + 1} of {shares.length}
                                </span>
                                <button
                                  onClick={() => stepShare(1)}
                                  className="grid h-8 w-8 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors"
                                  title="Next screen share"
                                  aria-label="Next screen share"
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Zoom and fullscreen controls */}
                            <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 z-10">
                              <button
                                onClick={() => dispatchScreenZoom({ type: 'zoomOut' })}
                                disabled={screenZoomLevel <= 0.5}
                                className="grid h-10 w-10 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors disabled:opacity-40"
                                title="Zoom out"
                              >
                                <ZoomOut className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => dispatchScreenZoom({ type: 'zoomIn' })}
                                disabled={screenZoomLevel >= 3}
                                className="grid h-10 w-10 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors disabled:opacity-40"
                                title="Zoom in"
                              >
                                <ZoomIn className="h-5 w-5" />
                              </button>
                              {/* Everyone's — presenter and viewers alike. Fullscreens
                                  this stage (screenStageRef), so these controls come
                                  along and the same button brings it back. */}
                              <button
                                onClick={toggleScreenStageFullscreen}
                                className="grid h-10 w-10 place-items-center rounded-full bg-gray-700/80 hover:bg-gray-600 text-white transition-colors"
                                title={screenStageFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                aria-pressed={screenStageFullscreen}
                              >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Resizable divider */}
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          onPointerDown={(e) => {
                            railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
                          }}
                          className="relative mr-2 hidden w-12 cursor-col-resize select-none items-center justify-center lg:flex -ml-4"
                          title="Resize"
                        >
                          <div className="grid gap-1">
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                            <span className="h-1 w-1 rounded-full bg-card/40" />
                          </div>
                        </div>

                        {/* Right rail - participants */}
                        <aside className="min-h-0 shrink-0 overflow-hidden" style={{ width: rightRailWidth }}>
                          <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
                            {rightRailPageCount > 1 ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setRightRailPage((p) => Math.max(0, p - 1))}
                                  disabled={safeRightRailPage <= 0}
                                  className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                  title="Previous"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                                  disabled={safeRightRailPage >= rightRailPageCount - 1}
                                  className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                  title="Next"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
                                  </svg>
                                </button>
                                <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                                  {safeRightRailPage + 1}/{rightRailPageCount}
                                </div>
                              </>
                            ) : null}

                            <RailTiles
                              rows={railRowChunks}
                              rowHeight={railRowH}
                              renderTile={(p) => (
                                <VideoTile
                                  participant={p}
                                  reaction={reactionFor(p?.id)}
                                  isPinned={pinnedIds.includes(p.id)}
                                  onPin={handlePin}
                                  onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                                  pinnedForAllId={pinnedForAllId}
                                  onMaximize={handleMaximize}
                                  virtualBackgroundUrl={null}
                                />
                              )}
                            />
                          </div>
                        </aside>
                      </div>
                    )
                  ) : stage.activeKind === 'document' && presentingDocument ? (
                    wrapMobileStage(<DocumentPresentationLayout
                      phoneLayout={isMobileOrLandscape}
                      documentName={presentingDocument.name}
                      documentUrl={presentingDocument.url}
                      canClose={canStopPresenting}
                      isPresenter={shareIsPresenter}
                      presenterName={sharePresenterName}
                      reactionFor={reactionFor}
                      pinnedIds={pinnedIds}
                      railRowChunks={railRowChunks}
                      rightRailWidth={rightRailWidth}
                      rightRailPageCount={rightRailPageCount}
                      safeRightRailPage={safeRightRailPage}
                      railRowH={railRowH}
                      onPin={handlePin}
                      onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                      pinnedForAllId={pinnedForAllId}
                      onMaximize={handleMaximize}
                      onOpenChat={handleOpenChatFromTile}
                      setRightRailEl={setRightRailEl}
                      onSetRightRailPage={setRightRailPage}
                      selectedBackground={selectedBackground}
                      onClose={() => {
                        // Take it off everyone's stage too, not just mine —
                        // and this one, not whatever happens to be on top.
                        closeStageMedia(presentingDocument)
                      }}
                      railDragRef={railDragRef}
                      fullscreenControls={fullscreenMediaControls}
                      // Ink is keyed by the room's id for the presentation —
                      // the one without our local prefix.
                      annotation={{
                        shareId: presentingDocument.id.startsWith(REMOTE_MEDIA_PREFIX)
                          ? presentingDocument.id.slice(REMOTE_MEDIA_PREFIX.length)
                          : presentingDocument.id,
                        selfId: props.localPeerId ?? null,
                        isModerator: roles?.isModerator ?? false,
                        isAnnotator: roles?.isAnnotator ?? false,
                        onRequestAnnotation: moderation?.requestAnnotation,
                        channel: props.chatChannel ?? null,
                      }}
                    />)
                  ) : stage.activeKind === 'video' && presentingVideo ? (
                    wrapMobileStage(<VideoShareLayout
                      // A fresh player per video. One video replacing another
                      // otherwise reused this one, and the new YouTube player
                      // was handed the old video's last pause or seek on ready.
                      // Keyed on the room's id without the prefix, so the
                      // presenter's own stand-in becoming the room's entry is
                      // not a reload.
                      key={presentingVideo.id.startsWith(REMOTE_MEDIA_PREFIX)
                        ? presentingVideo.id.slice(REMOTE_MEDIA_PREFIX.length)
                        : presentingVideo.id}
                      phoneLayout={isMobileOrLandscape}
                      videoName={presentingVideo.name}
                      videoUrl={presentingVideo.url}
                      videoKind={presentingVideo.videoKind}
                      startedAt={presentingVideo.startedAt}
                      // Only warn the presenter. A viewer is by definition
                      // looking at a URL that did reach them.
                      localOnly={
                        !presentingVideo.id.startsWith(REMOTE_MEDIA_PREFIX) &&
                        !shareableUrl(presentingVideo.url)
                      }
                      canClose={canStopPresenting}
                      // Asked of the room, not inferred from the local id: the
                      // effect that mirrors the room's stage rewrites this
                      // client's own entry to a `remote-` id as soon as the
                      // share is echoed back, so the presenter's own video was
                      // being judged somebody else's and lost its controls.
                      isPresenter={shareIsPresenter}
                      onControl={(action, position) => sharedMedia.controlMedia(action, position)}
                      command={sharedMedia.command}
                      onProgress={sharedMedia.updatePlayback}
                      startPaused={presentingVideo.paused ?? false}
                      presenterName={sharePresenterName}
                      reactionFor={reactionFor}
                      pinnedIds={pinnedIds}
                      railRowChunks={railRowChunks}
                      rightRailWidth={rightRailWidth}
                      rightRailPageCount={rightRailPageCount}
                      safeRightRailPage={safeRightRailPage}
                      railRowH={railRowH}
                      onPin={handlePin}
                      onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                      pinnedForAllId={pinnedForAllId}
                      onMaximize={handleMaximize}
                      onOpenChat={handleOpenChatFromTile}
                      setRightRailEl={setRightRailEl}
                      onSetRightRailPage={setRightRailPage}
                      selectedBackground={selectedBackground}
                      onClose={() => {
                        closeStageMedia(presentingVideo)
                      }}
                      railDragRef={railDragRef}
                      fullscreenControls={fullscreenMediaControls}
                    />)
                  ) : layoutMode === "tiled" ? (
                    <VideoGrid
                      participants={gridParticipants}
                      // Already sliced to one page above, so this only tells
                      // VideoGrid not to page it a second time at its own
                      // default — which, at any density above that default,
                      // cut each page into several and hid most of the tiles
                      // behind a second set of arrows.
                      pageSize={maxTiles}
                      variant="legacy"
                      reactionFor={reactionFor}
                      onPin={handlePin}
                      onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                      pinnedForAllId={pinnedForAllId}
                      onMaximize={handleMaximize}
                      onOpenChat={handleOpenChatFromTile}
                      onOpenParticipants={handleOpenParticipants}
                      virtualBackgroundUrl={null}
                      showEmptyState={hiddenNoVideoCount === 0 && !selfHiddenEmpty}
                    />
                  ) : layoutMode === "auto" ? (
                    <VideoGrid
                      participants={gridOrderedParticipants}
                      // The whole roster, paged by VideoGrid at the density the
                      // user picked. Without this prop it paged at its own
                      // constant and the Settings dropdown moved a value that
                      // reached nothing.
                      pageSize={maxTiles}
                      reactionFor={reactionFor}
                      onPin={handlePin}
                      onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                      pinnedForAllId={pinnedForAllId}
                      onMaximize={handleMaximize}
                      onOpenChat={handleOpenChatFromTile}
                      onOpenParticipants={handleOpenParticipants}
                      virtualBackgroundUrl={null}
                      showEmptyState={hiddenNoVideoCount === 0 && !selfHiddenEmpty}
                    />
                  ) : layoutMode === "spotlight" ? (
                    <div className="relative h-full w-full">
                      <div className="h-full w-full">
                        {primaryParticipant ? (
                          <VideoTile
                            participant={primaryParticipant}
                            reaction={reactionFor(primaryParticipant?.id)}
                            isPinned={pinnedIds.includes(primaryParticipant.id)}
                            onPin={handlePin}
                            onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                            pinnedForAllId={pinnedForAllId}
                            onMaximize={handleMaximize}
                            onOpenChat={handleOpenChatFromTile}
                            virtualBackgroundUrl={null}
                          />
                        ) : null}
                      </div>

                      {/* Self preview — fixed in the corner while the main stage
                          follows the active speaker. Not clickable: pinning
                          yourself here would swap you onto the stage and hide
                          the very control you'd need to undo it. */}
                      {spotlightSelfTile ? (
                        <div
                          className="absolute bottom-6 right-6 h-[120px] w-[190px] overflow-hidden rounded-[12px] ring-1 ring-white/15"
                          title={spotlightSelfTile.name}
                        >
                          <VideoTile
                            participant={spotlightSelfTile}
                            reaction={reactionFor(spotlightSelfTile?.id)}
                            isPinned={pinnedIds.includes(spotlightSelfTile.id)}
                            onPin={handlePin}
                            onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                            pinnedForAllId={pinnedForAllId}
                            onMaximize={handleMaximize}
                            onOpenChat={handleOpenChatFromTile}
                            virtualBackgroundUrl={null}
                            showMenu={false}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : layoutMode === "pinned" ? (
                    <div className="flex h-full w-full min-w-0 overflow-hidden">
                      {/* Main area - shows grid of all pinned participants with dynamic layout */}
                      <div className="min-h-0 min-w-0 flex-1 pr-2 relative">
                        {/* Pagination controls for pinned area */}
                        {pinnedPageCount > 1 ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setPinnedPage((p) => Math.max(0, p - 1))}
                              disabled={safePinnedPage <= 0}
                              className="absolute left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                              title="Previous"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setPinnedPage((p) => Math.min(pinnedPageCount - 1, p + 1))}
                              disabled={safePinnedPage >= pinnedPageCount - 1}
                              className="absolute left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                              title="Next"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
                              </svg>
                            </button>
                            <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                              {safePinnedPage + 1}/{pinnedPageCount}
                            </div>
                          </>
                        ) : null}

                        <div ref={(node) => setPinnedAreaEl(node)} className="h-full w-full">
                          <RailTiles
                            rows={pinnedRowChunks}
                            rowHeight={pinnedRowH}
                            padY={0}
                            renderTile={(p) => (
                              <VideoTile
                                participant={p}
                                reaction={reactionFor(p?.id)}
                                isPinned={true}
                                onPin={handlePin}
                                onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                                pinnedForAllId={pinnedForAllId}
                                onMaximize={handleMaximize}
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Right rail - other non-pinned participants */}
                      {otherParticipants.length > 0 && (
                        <>
                          <div
                            role="separator"
                            aria-orientation="vertical"
                            onPointerDown={(e) => {
                              railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
                            }}
                            className="relative mr-2 hidden w-12 cursor-col-resize select-none items-center justify-center lg:flex -ml-4"
                            title="Resize"
                          >
                            <div className="grid gap-1">
                              <span className="h-1 w-1 rounded-full bg-card/40" />
                              <span className="h-1 w-1 rounded-full bg-card/40" />
                              <span className="h-1 w-1 rounded-full bg-card/40" />
                            </div>
                          </div>

                          <aside className="min-h-0 shrink-0 overflow-hidden" style={{ width: rightRailWidth }}>
                            <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
                              {rightRailPageCount > 1 ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setRightRailPage((p) => Math.max(0, p - 1))}
                                    disabled={safeRightRailPage <= 0}
                                    className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                    title="Previous"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                                    disabled={safeRightRailPage >= rightRailPageCount - 1}
                                    className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                    title="Next"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
                                    </svg>
                                  </button>
                                  <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                                    {safeRightRailPage + 1}/{rightRailPageCount}
                                  </div>
                                </>
                              ) : null}

                              <RailTiles
                                rows={railRowChunks}
                                rowHeight={railRowH}
                                renderTile={(p) => (
                                  <VideoTile
                                    participant={p}
                                    reaction={reactionFor(p?.id)}
                                    isPinned={pinnedIds.includes(p.id)}
                                    onPin={handlePin}
                                    onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                                    pinnedForAllId={pinnedForAllId}
                                    onMaximize={handleMaximize}
                                    virtualBackgroundUrl={null}
                                  />
                                )}
                              />
                            </div>
                          </aside>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex h-full w-full min-w-0 overflow-hidden">
                      <div className="min-h-0 min-w-0 flex-1 pr-2">
                        {primaryParticipant ? (
                          <VideoGrid
                            participants={[primaryParticipant]}
                            // One stage, whoever is on it: a change of speaker
                            // re-targets this tile instead of replacing it.
                            tileKey={stageTileKey}
                            reactionFor={reactionFor}
                            onPin={handlePin}
                            onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                            pinnedForAllId={pinnedForAllId}
                            onMaximize={handleMaximize}
                            onOpenChat={handleOpenChatFromTile}
                            onOpenParticipants={handleOpenParticipants}
                            virtualBackgroundUrl={null}
                          />
                        ) : null}
                      </div>

                      <div
                        role="separator"
                        aria-orientation="vertical"
                        onPointerDown={(e) => {
                          railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
                        }}
                        className="relative mr-2 hidden w-12 cursor-col-resize select-none items-center justify-center lg:flex -ml-4"
                        title="Resize"
                      >
                        <div className="grid gap-1">
                          <span className="h-1 w-1 rounded-full bg-card/40" />
                          <span className="h-1 w-1 rounded-full bg-card/40" />
                          <span className="h-1 w-1 rounded-full bg-card/40" />
                        </div>
                      </div>

                      <aside className="min-h-0 shrink-0 overflow-hidden" style={{ width: rightRailWidth }}>
                        <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
                          {rightRailPageCount > 1 ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setRightRailPage((p) => Math.max(0, p - 1))}
                                disabled={safeRightRailPage <= 0}
                                className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                title="Previous"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                                disabled={safeRightRailPage >= rightRailPageCount - 1}
                                className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                                title="Next"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
                                </svg>
                              </button>
                              <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                                {safeRightRailPage + 1}/{rightRailPageCount}
                              </div>
                            </>
                          ) : null}

                          <RailTiles
                            rows={railRowChunks}
                            rowHeight={railRowH}
                            renderTile={(p) => (
                              <VideoTile
                                participant={p}
                                reaction={reactionFor(p?.id)}
                                isPinned={pinnedIds.includes(p.id)}
                                onPin={handlePin}
                                onPinForAll={isRoomModerator ? handlePinForAll : undefined}
                                pinnedForAllId={pinnedForAllId}
                                onMaximize={handleMaximize}
                              />
                            )}
                          />
                        </div>
                      </aside>
                    </div>
                  )}
                </div>
                {/* Every tile hidden for having no video. Shared content owns the
                    stage, so it is never empty there. */}
                {!contentOnStage && hiddenNoVideoCount > 0 && layoutParticipants.length === 0 && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="text-sm text-white/40">{t("layouts.noVideosToDisplay")}</span>
                  </div>
                )}
                {!contentOnStage && selfHiddenEmpty && (
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
                    <span className="text-sm text-white/40">{t("layouts.selfHiddenTitle")}</span>
                    {/* Only true while the camera is sending: with it off, nobody sees you. */}
                    {camOn && <span className="text-sm text-white/40">{t("layouts.selfHiddenSubtitle")}</span>}
                    <button
                      type="button"
                      onClick={() => setHiddenSelfVideo(false)}
                      className="pointer-events-auto flex min-h-[44px] min-w-[44px] items-center justify-center px-3 text-sm text-white/60 underline underline-offset-2 hover:text-white/80"
                    >
                      {t("panels.show")}
                    </button>
                  </div>
                )}
              </div>


              {/* Captions overlay — only in a meeting with transcription, its
                  one source. Mounted nowhere else, so nothing can caption,
                  or listen, outside the transcription pipeline (which stops
                  sending audio whenever this participant is muted). */}
              {transcriptionEnabledForSession && (
                <CaptionsOverlay isActive={captionsOn} entries={props.liveCaptions ?? []} />
              )}

              {/* Presenting banner (when screen sharing) */}
              {/* {screenOn && (
            <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-2xl bg-red-500/90 backdrop-blur-sm px-4 py-2 text-sm font-semibold text-white shadow-lg">
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                You are presenting
                <button
                  type="button"
                  onClick={stopScreenShare}
                  className="ml-2 rounded-lg bg-card/20 px-2 py-1 text-xs font-medium transition hover:bg-card/30"
                >
                  Stop presenting
                </button>
              </div>
            </div>
          )} */}

              <GridPagination
                layoutMode={layoutMode}
                totalPages={totalPages}
                safeGridPage={safeGridPage}
                onPageChange={setGridPage}
              />

              {/* Notices at the bottom right of the video area: they move with
                  the videos when a side panel opens. */}
              <MeetingNoticeStack />

            </main>
          </div>

          {/* Side Panel - Desktop: inline or overlay based on setting, Mobile/Landscape: always overlay */}
          {!isMobileOrLandscape && renderInFullscreenHost(
            <>
              {/* Overlay scrim — only rendered in overlay mode while the panel
                  is open. Clicking it closes the drawer, matching the standard
                  modal-dismiss pattern users expect from "Side drawer over content". */}
              {panelOverlayMode && sidePanelOpen && (
                <div
                  onClick={closeAllSidePanels}
                  className="fixed inset-0 z-30 bg-black/40 transition-opacity duration-200"
                  aria-hidden="true"
                />
              )}
            <div
              style={{
                // panelLayoutOpen drives width; CSS transition smooths the resize.
                // The 280ms matches the inner panel translateX transition.
                width: panelOverlayMode ? undefined : (panelLayoutOpen ? "360px" : "0px"),
                overflow: "hidden",
                flexShrink: panelOverlayMode ? undefined : 0,
                transition: panelOverlayMode ? undefined : "width 280ms cubic-bezier(0.4,0,0.2,1)",
                // Overlay mode (the preference, or forced by nested fullscreen):
                // fixed positioning, so the panel does not need a flex parent.
                ...(panelOverlayMode ? {
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  height: '100%',
                  zIndex: 40,
                  pointerEvents: sidePanelOpen ? 'auto' : 'none',
                } : {}),
              }}
            >
              <div
                style={{
                  height: "100%",
                  // Overlay and inline modes share the same width so the panel
                  // contents render identically; the only difference is fixed
                  // positioning + scrim above.
                  width: "360px",
                  transform: sidePanelOpen ? "translateX(0)" : "translateX(100%)",
                  opacity: sidePanelOpen ? 1 : 0,
                  transition: "transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease",
                  willChange: "transform",
                  // When in overlay mode, add shadow and background
                  ...(sideDrawerOverContent ? {
                    boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
                  } : {}),
                }}
              >
                {!customPanel && (
                  <SidePanelTabs
                    defaultTab={sidePanelDefaultTab}
                    participants={participants}
                    localParticipant={localParticipant}
                    messages={chat.messages}
                    unreadChat={chat.unreadCount}
                    files={[]}
                    lobbyUsers={lobbyUsers}
                    meetingInfo={meetingInfoPanelOpen ? meetingInfo : undefined}
                    typingUsers={chat.typingUsers}
                    canChat={hostSendChat}
                    canPrivateChat={hostPrivateChat}
                    canSendFiles={hostSendFiles}
                    canUseEmojis={hostCanUseEmojis}
                    onMuteAll={handleMuteAll}
                    onStopAllVideos={handleStopAllVideos}
                    onStopAllScreens={handleStopAllScreens}
                    onEndMeeting={handleEndMeetingForAll}
                    onRequestAudio={handleRequestAudio}
                    onRequestVideo={handleRequestVideo}
                    onAdmit={handleAdmitFromLobby}
                    onReject={handleRejectFromLobby}
                    onAdmitAll={handleAdmitAllFromLobby}
                    onSpotlight={(id: string) => {
                      handlePin(id)
                    }}
                    onPin={(id: string) => {
                      handlePin(id)
                    }}
                    onClose={() => {
                      setChatOpen(false)
                      setParticipantsOpen(false)
                      setFilesOpen(false)
                      setMeetingInfoPanelOpen(false)
                    }}
                    privateThreads={chat.privateThreads}
                    unreadGroup={chat.unreadGroup}
                    unreadPrivate={chat.unreadPrivate}
                    onSendMessage={handleSendChat}
                    onAttachChat={canAttachInChat ? handleAttachChat : undefined}
                    // This panel stays mounted when the sidebar closes (it
                    // animates to zero width), so chat has to be told when it
                    // is no longer on screen. Only open/closed belongs here —
                    // switching tabs unmounts ChatPanel outright, which its own
                    // cleanup already handles.
                    panelVisible={sidePanelOpen}
                    onActiveChatThreadChange={chat.setActiveThread}
                    onClearChatThread={chat.clearThread}
                    onMarkChatRead={chat.markAllRead}
                    onClearChat={chat.clearAll}
                    onMuteParticipant={handleMuteParticipant}
                    onUnmuteParticipant={handleUnmuteParticipant}
                    onStopVideo={handleStopVideo}
                    onStartVideo={handleStartVideo}
                    onStopScreen={handleStopScreen}
                    onRemoveParticipant={handleRemoveParticipant}
                    onChangeRole={handleChangeRole}
                    onShare={handleOpenShare}
                    onChat={handleOpenChatWithParticipant}
                    onSelectParticipant={handleSelectParticipantFromPanel}
                    selectedPrivateChat={selectedPrivateChat}
                    onShowToast={handleShowChatToast}
                    shareBlocked={shareBlocked}
                    onAllowShare={handleAllowShare}
                    onSetPresenter={handleSetPresenter}
                    onSetAnnotator={handleSetAnnotator}
                    permissionRequests={permissionRequests}
                    onApproveRequest={approvePermissionRequest}
                    onRejectRequest={rejectPermissionRequest}
                  />
                )}

                {/* Custom Panels */}
                {customPanel === "polls" && (
                  <PollsPanel
                    polls={polls}
                    canCreatePoll={canManagePolls}
                    localParticipantId={localParticipant.id}
                    votedPollIds={votedPollIds}
                    onClose={() => setPollsPanelOpen(false)}
                    onCreatePoll={() => setCreatePollOpen(true)}
                    onViewPoll={(pollId) => {
                      setActivePollId(pollId)
                      setPollResultsOpen(true)
                    }}
                    onVotePoll={(pollId) => {
                      setActivePollId(pollId)
                      setPollVotingOpen(true)
                    }}
                  />
                )}

                {customPanel === "surveys" && sidePanelOpen && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden animate-in slide-in-from-right-4 duration-300">
                    <SurveysPanel
                      surveys={surveys}
                      onClose={() => setSurveysPanelOpen(false)}
                      onCreateSurvey={createSurvey}
                      onCreateSurveyClick={() => setCreateSurveyOpen(true)}
                      onDeleteSurvey={removeSurvey}
                      onEditSurvey={handleEditSurvey}
                      localParticipantName={meetingInfo.userName}
                    />
                  </div>
                )}

                {sidePanelOpen && customPanel === "branding" && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden animate-in slide-in-from-right-4 duration-300">
                    <BrandingPanel
                      logos={logos}
                      backgrounds={backgrounds}
                      overlays={overlays}
                      videoClips={videoClips}
                      audioClips={audioClips}
                      onClose={() => setBrandingPanelOpen(false)}
                      onAddLogo={handleAddLogo}
                      onAddBackground={handleAddBackground}
                      onDeleteLogo={handleDeleteLogo}
                      onDeleteBackground={handleDeleteBackground}
                      onToggleOverlay={handleToggleOverlay}
                      onAddVideoClip={handleAddVideoClip}
                      onAddAudioClip={handleAddAudioClip}
                      onDeleteVideoClip={handleDeleteVideoClip}
                      onDeleteAudioClip={handleDeleteAudioClip}
                    />
                  </div>
                )}

                {sidePanelOpen && customPanel === "videoShare" && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden animate-in slide-in-from-right-4 duration-300">
                    <VideoSharePanel
                      videos={sharedVideos}
                      onClose={() => setVideoSharePanelOpen(false)}
                      localPeerId={props.localPeerId ?? null}
                      canShare={canShareFiles}
                      onShareVideo={(video) => {
                        const next: SharedVideo = {
                          ...video,
                          id: `vid-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                          sharedBy: props.localPeerId ?? undefined,
                          sharedByName: meetingInfo.userName,
                        }
                        setSharedVideos((prev) => [...prev, next])
                        // Into everyone's list, without presenting it.
                        sharedMedia.publishLibraryItem('video', {
                          id: next.id, name: next.name, url: next.url, kind: next.kind,
                          size: next.size, sharedBy: props.localPeerId ?? '',
                          sharedByName: meetingInfo.userName,
                        })
                      }}
                      onRemoveVideo={(id) => {
                        setSharedVideos((prev) => prev.filter((v) => v.id !== id))
                        sharedMedia.unpublishLibraryItem('video', id)
                      }}
                      onPlayVideo={(video) => {
                        setLocalPresenting({
                          kind: 'video',
                          id: video.id,
                          name: video.name,
                          url: video.url,
                          videoKind: video.kind,
                        })
                        // Put it on everyone's stage. Returns false for a blob:
                        // URL (an upload) — that link only resolves inside this
                        // tab, so it stays a local preview and the banner in the
                        // player explains why.
                        sharedMedia.start('video', {
                          id: video.id,
                          name: video.name,
                          url: video.url,
                          source: video.kind === 'embed' ? 'embed' : 'file',
                        })
                      }}
                    />
                  </div>
                )}

                {sidePanelOpen && customPanel === "documentShare" && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden">
                    <DocumentSharePanel
                      documents={sharedDocuments}
                      onClose={() => setDocumentSharePanelOpen(false)}
                      canShare={canShareFiles}
                      localPeerId={props.localPeerId ?? null}
                      onShareDocument={(doc) => {
                        const next: SharedDocument = {
                          ...doc,
                          id: `doc-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                          uploadedAt: new Date().toISOString(),
                          sharedBy: props.localPeerId ?? undefined,
                          sharedByName: meetingInfo.userName,
                        }
                        setSharedDocuments((prev) => [...prev, next])
                        sharedMedia.publishLibraryItem('document', {
                          id: next.id, name: next.name, url: next.url,
                          size: next.size, type: next.type,
                          sharedBy: props.localPeerId ?? '',
                          sharedByName: meetingInfo.userName,
                          ...(next.previewUrl ? { previewUrl: next.previewUrl } : {}),
                        })
                      }}
                      onRemoveDocument={removeSharedDocument}
                      onDownloadDocument={downloadDocument}
                      presentingDocumentId={presentingDocument?.id ?? null}
                      onPresentDocument={presentDocument}
                    />
                  </div>
                )}

                {/* Breakout Rooms Panel - Now handled via ControlBar dropdown only */}
                {/* {sidePanelOpen && customPanel === "breakoutRooms" && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden animate-in slide-in-from-right-4 duration-300">
                    <BreakoutRoomsPanel
                      rooms={activeBreakoutRooms}
                      currentRoomId={currentBreakoutRoomId}
                      onClose={() => setBreakoutRoomsPanelOpen(false)}
                      settings={breakoutSettings}
                    />
                  </div>
                )} */}

                {sidePanelOpen && customPanel === "webinarUsers" && (
                  <div className="h-full w-[360px] bg-pannelcolor rounded-l-xl text-vc-panel-text shadow-lg ring-1 ring-vc-panel-border overflow-hidden animate-in slide-in-from-right-4 duration-300">
                    <WebinarUsersPanel
                      onClose={() => setWebinarUsersOpen(false)}
                    />
                  </div>
                )}

                {sidePanelOpen && customPanel === "transcription" && (
                  <div className="fixed top-0 right-0 h-full w-[360px] z-50" style={{
                    transform: sidePanelOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: `transform ${ANIMATION_DURATION.PANEL_TRANSITION}ms cubic-bezier(0.2,0,0,1)`,
                    willChange: 'transform',
                  }}>
                    <div className="h-full w-full bg-card shadow-2xl">
                      <TranscriptionPanel
                        onClose={() => setTranscriptionPanelOpen(false)}
                      />
                    </div>
                  </div>
                )}
                {customPanel}
              </div>
            </div>
            </>
          )}
        </div>

        {isMobileOrLandscape && !fullscreenOn && !permanentBottomBar && !sidePanelOpen && (!isControlBarVisible || holdTapCatcher) && (
          <div
            aria-hidden
            className="fixed inset-0 z-40"
            onPointerDown={() => setHoldTapCatcher(true)}
            onPointerCancel={() => setHoldTapCatcher(false)}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setHoldTapCatcher(false)
            }}
          />
        )}

        {/* The only way out, now that the bar carrying it is hidden.
            Bottom-right, above everything, and only while full screen is on. */}
        {fullscreenOn && (
          <button
            type="button"
            onClick={() => { void toggleFullscreen() }}
            aria-label={t('controlBar.exitFullScreen')}
            title={t('controlBar.exitFullScreen')}
            className="fixed bottom-6 right-6 z-[70] grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white/85 ring-1 ring-white/15 backdrop-blur transition hover:bg-black/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <Minimize2 className="h-5 w-5" />
          </button>
        )}

        {/* Mic and camera in whole-page full screen (More > Full screen),
            which collapses the bar. Bottom-centre: the name badges and tile
            menus hold the bottom corners. Lifted while shared content is on
            the stage, whose own zoom and playback bars sit at its bottom
            centre. Only the whole page: a single maximised tile gets none, and
            a shared video or document holding full screen itself carries the
            buttons in its header. */}
        {fullscreenMediaControls && fullscreenOn && !inNestedFullscreen && (
          <div
            className={cn(
              "fixed left-1/2 z-[70] flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/55 px-3 py-2 ring-1 ring-white/15 backdrop-blur",
              contentOnStage ? "bottom-20" : "bottom-6",
            )}
          >
            {fullscreenMediaControls}
          </div>
        )}

        {/* Full screen is for the meeting, not for the furniture around it, so the
            bar goes entirely — collapsed to nothing and taken out of the tab
            order, the same way it already hides itself. It wins over both the
            auto-hide and the mobile toggle rather than racing them: whatever
            those two think, full screen means gone. The one way back is the
            button below; without it the only exit would be Esc, which is not
            discoverable. */}
        <div
          className={cn(
            "transition-[height,opacity] duration-300 ease-in-out bg-vc-control-bg flex items-center",
            fullscreenOn
              ? "h-0 opacity-0 pointer-events-none"
              : !isControlBarVisible
                ? "h-0 opacity-0 pointer-events-none"
                // On phones the bar stays clear of the bottom edge, where a
                // sideways swipe (scrolling the controls) would switch apps
                // under gesture navigation, and of the iPhone home indicator.
                : isMobileOrLandscape
                  ? "h-[calc(84px_+_env(safe-area-inset-bottom))] pb-[calc(12px_+_env(safe-area-inset-bottom))] opacity-100"
                  : "h-[72px] opacity-100"
          )}
          aria-hidden={fullscreenOn || undefined}
          style={{ overflow: fullscreenOn ? 'hidden' : (isControlBarVisible ? 'visible' : 'hidden') }}
        >
          <ControlBar
            anyRemoteCameraOn={anyRemoteCameraOn}
            anyRemoteMicOn={anyRemoteMicOn}
            // Locking is a moderator action, and withholding the CALLBACK is
            // how that is enforced in the bar: every lock surface renders only
            // when it is present. Passed unconditionally — as it was — a
            // participant got a working Lock row, and the server's refusal
            // (requireModerator) was the only thing standing between them and
            // locking the host's room.
            onToggleLock={roles?.isModerator ? handleToggleRoomLock : undefined}
            canEndForAll={roles?.isModerator ?? false}
            screenOn={screenOn}
            chatOn={chatOpen}
            participantsOn={participantsOpen}
            webinarUsersOn={webinarUsersOpen}
            someoneElseRecording={(props.someoneElseIsRecording ?? false) || othersCloudRecording}
            cloudRecordingOn={ownCloudRecording}
            cloudRecordingDuration={ownCloudRecording ? recording.formattedDuration : undefined}
            cloudRecordingStopping={cloudStopping}
            cloudRecordingPending={recording.isPending}
            onStopCloudRecording={ownCloudRecording ? handleStopCloudRecording : undefined}
            localRecordingOn={localRecording.isRecording}
            localRecordingDuration={localRecording.isRecording ? localRecording.formattedDuration : undefined}
            localRecordingPaused={localRecording.isRecording ? localRecording.isPaused : false}
            onPauseLocalRecording={localRecording.isRecording ? localRecording.pause : undefined}
            onResumeLocalRecording={localRecording.isRecording ? localRecording.resume : undefined}
            onStopLocalRecording={localRecording.isRecording ? () => void localRecording.stop() : undefined}
            meetingStartTime={meetingStartTime}
            onOpenMeetingInfo={handleToggleMeetingInfo}
            unreadChatCount={chat.unreadCount}
            onReact={handleSendReaction}
            onToggleScreen={toggleScreenShare}
            onToggleChat={handleToggleChat}
            onToggleParticipants={handleToggleParticipants}
            onToggleWebinarUsers={handleToggleWebinarUsers}
            onStartCloudRecording={cloudRecordingAvailable ? handleStartCloudRecording : undefined}
            onStartLocalRecording={handleStartLocalRecording}
            onAdjustView={handleAdjustView}
            onToggleFullscreen={handleToggleFullscreen}
            onOpenSettings={handleOpenSettings}
            // Moderators only — host and co-host, the same test the server
            // applies to `host:set_controls`. Dropping the callback is how this
            // control bar hides an entry (see `onOpenTranscription`), so an
            // attendee gets no menu item rather than a disabled one that
            // opens a dialog whose every toggle the server would refuse.
            //
            // A presenter grant deliberately does NOT qualify: presenting is
            // permission to share your own screen, not to set the room's rules.
            onOpenHostControls={isRoomModerator ? handleOpenHostControls : undefined}
            onOpenShortcuts={handleOpenShortcuts}
            onOpenWhiteboard={handleOpenWhiteboard}
            pendingRequestLabel={pendingRequestLabel}
            // Apps → Breakout Rooms means "take me to the rooms". Which screen
            // that is depends on whether there are any: the setup dialog before
            // they exist, and once they are running the same manage screen the
            // Manage item opens. Opening setup on a running set offered to
            // configure rooms that were already configured.
            onOpenBreakoutRooms={() =>
              breakoutActive ? setBreakoutRoomsAssignmentOpen(true) : setBreakoutRoomsOpen(true)
            }
            activeBreakoutRooms={activeBreakoutRooms}
            currentBreakoutRoomId={currentBreakoutRoomId}
            onJoinBreakoutRoom={(roomId) => { void joinBreakoutRoom(roomId) }}
            // Leaving is a real move back to the main room, not just forgetting
            // which room we were in — the connection has to follow.
            onLeaveBreakoutRoom={() => { void leaveBreakoutRoom() }}
            onOpenBreakoutRoomsAssignment={() => setBreakoutRoomsAssignmentOpen(true)}
            canManageBreakout={isRoomModerator}
            breakoutStarted={breakoutActive}
            // Somewhere to go, and not there yet.
            //
            // Under self walk-in nobody is assigned, so waiting for an
            // assignment hid the entry from exactly the people who need it:
            // anyone who dismissed the prompt had no way back to the rooms.
            breakoutJoinPending={
              (!!myAssignedRoomId && myAssignedRoomId !== currentBreakoutRoomId) ||
              (breakoutAssignment === 'self' && !currentBreakoutRoomId)
            }
            breakoutAssignedRoomId={myAssignedRoomId}
            onEndBreakoutRooms={closeBreakoutRooms}
            onOpenBreakoutJoin={() => setInvitePromptOpen(true)}
            onBroadcastBreakoutMessage={broadcastBreakoutMessage}
            onOpenPolling={() => setPollsPanelOpen(true)}
            onOpenSurveys={() => setSurveysPanelOpen(true)}
            onOpenBranding={() => setBrandingPanelOpen(true)}
            onOpenDocumentShare={() => {
              if (shareNeedsPresenter) {
                if (presenterAsked) return
                setPresenterRequestFor('document')
                setMyPresenterOutcome(null)
                setPresenterRequestOpen(true)
                return
              }
              setDocumentSharePanelOpen(true)
            }}
            onOpenVideoShare={() => {
              if (shareNeedsPresenter) {
                if (presenterAsked) return
                setPresenterRequestFor('video')
                setMyPresenterOutcome(null)
                setPresenterRequestOpen(true)
                return
              }
              setVideoSharePanelOpen(true)
            }}
            onOpenLiveStreaming={isRoomHost ? handleOpenLiveStreaming : undefined}
            // An extra camera follows the camera control — off means none of
            // your video is sent — so adding one while the camera is off has
            // nothing to show. Hidden rather than offered and silently held.
            onOpenAddVideoInput={camOn ? () => setAddVideoInputOpen(true) : undefined}
            onOpenEngagementControls={() => setEngagementControlsOpen(true)}
            onOpenTranscription={
              transcriptionEnabledForSession
                ? () => setTranscriptionPanelOpen(true)
                : undefined
            }
            transcriptionActive={transcriptionEnabledForSession}
            onOpenBackgroundEffects={handleOpenBackgroundEffects}
            onEndWebinarForAll={handleEndWebinarForAll}
            onLeaveWebinar={handleLeaveWebinar}
            onEndMeetingForAll={handleEndMeetingForAll}
            onLeaveMeeting={handleLeaveMeeting}
            onStartWebinar={handleStartWebinar}
            onStopWebinar={handleStopWebinar}
            isWebinarLive={isWebinarLive}
            isWebinar={isWebinarSession}
            viewerCount={participants.length}
            onToggleDocumentPiP={handleManualPiP}
            meetingCode={meetingInfo.meetingCode.toLowerCase()}
            // Real title from the persisted record (meeting or webinar).
            // Falls back to whatever VcApp seeded into `meetingInfo`.
            meetingName={
              webinarRecord?.title ?? meetingRecord?.title ?? meetingInfo.meetingName
            }
            onVisibilityChange={setIsControlBarVisible}
            permanentBottomBar={permanentBottomBar}
            isMobileView={isMobileOrLandscape} // Show mobile layout (emoji, screen share) in landscape
            viewOnly={viewOnly}
            hostCanUseMicrophone={hostCanUseMicrophone}
            hostCanUseVideo={hostCanUseVideo}
            hostCanShareScreen={hostCanShareScreen}
            hostCanUseEmojis={hostCanUseEmojis}
            hostAllowChat={hostAllowChat}
            onHostBlocked={handleHostBlocked}
            hostCanRaiseHand={hostCanRaiseHand}
          />
        </div>

        {/* Live streaming — host only; see isRoomHost. */}
        {isRoomHost && (
        <LiveStreamingControls
          isStreaming={broadcasting.isLive}
          streamDuration={broadcasting.durationSeconds}
          showDialog={liveStreamingOpen}
          onShowDialogChange={setLiveStreamingOpen}
          onStartStream={(config) => {
            // One destination. The mixer publishes to a single RTMP endpoint,
            // and the dialog's second slot would silently do nothing — so take
            // the first enabled one rather than pretend both went live.
            const target = config.platforms[0]
            if (!target) return
            // Mapped explicitly. Anything unrecognised used to fall through to
            // YouTube, which meant picking Twitch published a Twitch key to
            // YouTube's ingest and failed with nothing explaining why. The
            // backend rejects a platform it has no ingest for and says so.
            const platform = target.platform.toLowerCase() as
              "youtube" | "facebook" | "custom" | "twitch" | "linkedin"

            void broadcasting.start({
              platform,
              streamKey: target.streamKey ?? "",
              rtmpUrl: target.streamUrl,
              title: config.title,
            }).then((ok) => {
              // Only dismiss on success: a rejected key or an unreachable mixer
              // should leave the host looking at what they typed.
              if (ok) setLiveStreamingOpen(false)
            })
          }}
          onStopStream={() => {
            void broadcasting.stop()
          }}
        />
        )}

        {/* Mobile/Landscape: Overlay Side Panel */}
        {isMobileOrLandscape && sidePanelOpen && !customPanel && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title={sidePanelDefaultTab === "chat" ? "Chat" : sidePanelDefaultTab === "people" ? "Participants" : sidePanelDefaultTab === "info" ? "Meeting Info" : "Files"}
            hasInputs={chatOpen}
          >
            <SidePanelTabs
              defaultTab={sidePanelDefaultTab}
              participants={participants}
              localParticipant={localParticipant}
              messages={chat.messages}
              unreadChat={chat.unreadCount}
              files={[]}
              lobbyUsers={lobbyUsers}
              meetingInfo={meetingInfoPanelOpen ? meetingInfo : undefined}
              typingUsers={chat.typingUsers}
              canChat={hostSendChat}
              canPrivateChat={hostPrivateChat}
              canSendFiles={hostSendFiles}
              canUseEmojis={hostCanUseEmojis}
              hideHeader
              onMuteAll={handleMuteAll}
              onStopAllVideos={handleStopAllVideos}
              onStopAllScreens={handleStopAllScreens}
              onEndMeeting={handleEndMeetingForAll}
              onRequestAudio={handleRequestAudio}
              onRequestVideo={handleRequestVideo}
              onAdmit={handleAdmitFromLobby}
              onReject={handleRejectFromLobby}
              onAdmitAll={handleAdmitAllFromLobby}
              onSpotlight={(id: string) => {
                handlePin(id)
              }}
              onPin={(id: string) => {
                handlePin(id)
              }}
              onClose={closeAllSidePanels}
              privateThreads={chat.privateThreads}
              unreadGroup={chat.unreadGroup}
              unreadPrivate={chat.unreadPrivate}
              onSendMessage={handleSendChat}
              onAttachChat={canAttachInChat ? handleAttachChat : undefined}
              panelVisible={sidePanelOpen}
              onActiveChatThreadChange={chat.setActiveThread}
              onClearChatThread={chat.clearThread}
              onMarkChatRead={chat.markAllRead}
              onClearChat={chat.clearAll}
              onMuteParticipant={handleMuteParticipant}
              onUnmuteParticipant={handleUnmuteParticipant}
              onStopVideo={handleStopVideo}
              onStartVideo={handleStartVideo}
              onStopScreen={handleStopScreen}
              onRemoveParticipant={handleRemoveParticipant}
              onChangeRole={handleChangeRole}
              canClear={localParticipant.role === "host" || localParticipant.role === "moderator"}
              onShare={handleOpenShare}
              onChat={handleOpenChatWithParticipant}
              onSelectParticipant={handleSelectParticipantFromPanel}
              selectedPrivateChat={selectedPrivateChat}
              onShowToast={handleShowChatToast}
              shareBlocked={shareBlocked}
              onAllowShare={handleAllowShare}
              onSetPresenter={handleSetPresenter}
              onSetAnnotator={handleSetAnnotator}
              permissionRequests={permissionRequests}
              onApproveRequest={approvePermissionRequest}
              onRejectRequest={rejectPermissionRequest}
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "polls" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Polls"
          >
            <PollsPanel
              polls={polls}
              canCreatePoll={canManagePolls}
              localParticipantId={localParticipant.id}
              votedPollIds={votedPollIds}
              onClose={() => setPollsPanelOpen(false)}
              hideHeader
              onCreatePoll={() => setCreatePollOpen(true)}
              onViewPoll={(pollId) => {
                setActivePollId(pollId)
                setPollResultsOpen(true)
              }}
              onVotePoll={(pollId) => {
                setActivePollId(pollId)
                setPollVotingOpen(true)
              }}
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "videoShare" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Video Share"
          >
            <VideoSharePanel
              videos={sharedVideos}
              onClose={() => setVideoSharePanelOpen(false)}
              hideHeader
              canShare={canShareFiles}
              onShareVideo={(video) => {
                // Published to the room, as the desktop panel does — sharing
                // from mobile reached nobody before.
                const next: SharedVideo = {
                  id: `vid-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                  ...video,
                  sharedBy: props.localPeerId ?? undefined,
                  sharedByName: meetingInfo.userName,
                }
                setSharedVideos((prev) => [next, ...prev])
                sharedMedia.publishLibraryItem('video', {
                  id: next.id, name: next.name, url: next.url,
                  kind: next.kind, size: next.size,
                  sharedBy: props.localPeerId ?? '',
                  sharedByName: meetingInfo.userName,
                })
              }}
              onRemoveVideo={(id) => {
                setSharedVideos((prev) => prev.filter((v) => v.id !== id))
                sharedMedia.unpublishLibraryItem('video', id)
              }}
              onPlayVideo={(video) => {
                // Present it, rather than opening a tab. This showed the video
                // to nobody — including the person who clicked, who just got a
                // new tab — while the desktop panel put it on the room's stage.
                setLocalPresenting({
                  kind: 'video',
                  id: video.id, name: video.name, url: video.url, videoKind: video.kind,
                })
                sharedMedia.start('video', {
                  id: video.id, name: video.name, url: video.url,
                  source: video.kind === 'embed' ? 'embed' : 'file',
                })
              }}
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "documentShare" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Document Share"
          >
            <DocumentSharePanel
              documents={sharedDocuments}
              onClose={() => setDocumentSharePanelOpen(false)}
              hideHeader
              canShare={canShareFiles}
              localPeerId={props.localPeerId ?? null}
              presentingDocumentId={presentingDocument?.id ?? null}
              // Presenting from a phone, as from a laptop.
              onPresentDocument={presentDocument}
              onShareDocument={(doc) => {
                // Publishes to the room, as the desktop panel does. Without
                // this a document shared from mobile reached nobody: it went
                // into this client's own list and was never broadcast, and it
                // carried no sharedBy, so even locally it looked like a file
                // this participant had uploaded rather than shared.
                const next: SharedDocument = {
                  ...doc,
                  id: `doc-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                  uploadedAt: new Date().toISOString(),
                  sharedBy: props.localPeerId ?? undefined,
                  sharedByName: meetingInfo.userName,
                }
                setSharedDocuments((prev) => [next, ...prev])
                sharedMedia.publishLibraryItem('document', {
                  id: next.id, name: next.name, url: next.url,
                  size: next.size, type: next.type,
                  sharedBy: props.localPeerId ?? '',
                  sharedByName: meetingInfo.userName,
                  ...(next.previewUrl ? { previewUrl: next.previewUrl } : {}),
                })
              }}
              onRemoveDocument={removeSharedDocument}
              onDownloadDocument={downloadDocument}
            />
          </MobileOverlayPanel>
        )}

        {/* Mobile Breakout Rooms Panel - Now handled via ControlBar only */}
        {/* {isMobileOrLandscape && customPanel === "breakoutRooms" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Breakout Rooms"
          >
            <BreakoutRoomsPanel
              rooms={activeBreakoutRooms}
              currentRoomId={currentBreakoutRoomId}
              isHost={localParticipant.role === "host"}
              onClose={() => setBreakoutRoomsPanelOpen(false)}
              hideHeader
              onJoinRoom={(roomId) => {
                setCurrentBreakoutRoomId(roomId)
              }}
              onBroadcastMessage={broadcastBreakoutMessage}
              onEndAllRooms={() => {
                setActiveBreakoutRooms([])
                setCurrentBreakoutRoomId(null)
                setBreakoutRoomsPanelOpen(false)
              }}
              onCloseRoom={(roomId) => {
                setActiveBreakoutRooms((prev) =>
                  prev.map((r) => (r.id === roomId ? { ...r, isActive: false } : r))
                )
              }}
              onReturnToMainRoom={() => {
                setCurrentBreakoutRoomId(null)
              }}
            />
          </MobileOverlayPanel>
        )} */}

        {isMobileOrLandscape && customPanel === "webinarUsers" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Webinar Users"
          >
            <WebinarUsersPanel
              onClose={() => setWebinarUsersOpen(false)}
              hideHeader
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "surveys" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Surveys"
          >
            <SurveysPanel
              surveys={surveys}
              onClose={() => setSurveysPanelOpen(false)}
              hideHeader
              onCreateSurvey={createSurvey}
              onCreateSurveyClick={() => setCreateSurveyOpen(true)}
              onDeleteSurvey={removeSurvey}
              onEditSurvey={handleEditSurvey}
              localParticipantName={meetingInfo.userName}
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "transcription" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Transcription"
          >
            <TranscriptionPanel
              onClose={() => setTranscriptionPanelOpen(false)}
              hideHeader={true}
            />
          </MobileOverlayPanel>
        )}

        {isMobileOrLandscape && customPanel === "branding" && (
          <MobileOverlayPanel
            open={sidePanelOpen}
            onClose={closeAllSidePanels}
            title="Branding"
          >
            <BrandingPanel
              logos={logos}
              backgrounds={backgrounds}
              overlays={overlays}
              videoClips={videoClips}
              audioClips={audioClips}
              onClose={() => setBrandingPanelOpen(false)}
              hideHeader
              onAddLogo={handleAddLogo}
              onAddBackground={handleAddBackground}
              onDeleteLogo={handleDeleteLogo}
              onDeleteBackground={handleDeleteBackground}
              onToggleOverlay={handleToggleOverlay}
              onAddVideoClip={handleAddVideoClip}
              onAddAudioClip={handleAddAudioClip}
              onDeleteVideoClip={handleDeleteVideoClip}
              onDeleteAudioClip={handleDeleteAudioClip}
            />
          </MobileOverlayPanel>
        )}
      </div>

      {/* Asking to present, and answering. Mounted here rather than in
          MeetingScreenDialogs because both need `moderation`, which arrives as
          a prop on this component. */}
      <ScreenShareRequestDialog
        open={presenterRequestOpen}
        onOpenChange={(open) => {
          setPresenterRequestOpen(open)
          // Closing clears the verdict, so re-opening starts a fresh ask
          // rather than replaying the last answer.
          if (!open) setMyPresenterOutcome(null)
        }}
        outcome={myPresenterOutcome}
        subject={presenterRequestFor}
        onRequest={() => {
          setMyPresenterOutcome(null)
          setPresenterAsked(true)
          moderation?.requestPresenter(presenterRequestFor)
          // The dialog closes once the ask is sent: waiting in the middle of
          // the screen kept the person out of their own meeting. The chip at
          // the top right of the video area takes over (MyRequestChip).
          setPresenterRequestOpen(false)
        }}
        // "Start Screen Share" has to actually start it. Closing the dialog on
        // an approval and leaving the user to find the button again is the kind
        // of dead end this whole flow exists to remove.
        // Approval has to resume the thing that was asked for. Always starting
        // a screen share here would answer a document request with the wrong
        // action — the same dead end this flow exists to remove.
        onApproved={() => {
          setMyPresenterOutcome(null)
          startApprovedShare()
        }}
        onRejected={() => setMyPresenterOutcome(null)}
      />

      <MeetingScreenDialogs
        isMobileOrLandscape={isMobileOrLandscape}
        captionsAvailable={transcriptionEnabledForSession}
        localParticipant={localParticipant}
        meetingUserName={meetingInfo.userName}
        onLeaveMeeting={onLeaveMeeting}
        breakoutAssignableParticipants={breakoutAssignableParticipants}
        onStartBreakoutRooms={openBreakoutRooms}
        onEndBreakoutRooms={closeBreakoutRooms}
        onMoveBreakoutParticipant={moveBreakoutParticipant}
        activeBreakoutRooms={activeBreakoutRooms}
        onBreakoutRoomFull={(roomId) =>
          breakoutBroadcastRef.current?.(
            `${activeBreakoutRooms.find((r) => r.id === roomId)?.name ?? 'That room'} is full.`,
            'Breakout rooms',
          )
        }
        breakoutStarted={breakoutActive}
        presentParticipantIds={presentParticipantIds}
        othersScreenSharing={remoteScreens.length > 0}
        othersSharingFiles={othersDocuments.length > 0}
        videoInputs={props.localVideoInputs}
        maxVideoInputs={MAX_ADDITIONAL_VIDEO_INPUTS}
        onAddVideoInput={props.publishVideoInput}
        onRemoveVideoInput={props.unpublishVideoInput}
        autoGridTiles={autoGridTiles}
        tiledGridTiles={tiledGridTiles}
      />


      {/* Share Dialog - Rendered at root level for center screen positioning */}
      {/* "You're the only one here" — only when the host is truly alone in
          the call. Auto-dismisses (via the existing handler) once a second
          participant joins, and stays dismissed for the rest of the session. */}
      {showJoinSharePopover && meetingShareUrl && participantCount <= 1 && (
        <div
          className={cn(
            "fixed bottom-4 left-4 z-[70] w-[calc(100vw-2rem)] max-w-sm transition-all duration-500 ease-out",
            isJoinSharePopoverVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <div className="rounded-2xl border border-border bg-card p-4 shadow-2xl backdrop-blur-md">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm dark:text-foreground font-semibold text-foreground">{t("meetingScreen.youreTheOnlyOneHere")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("meetingScreen.shareMeetingLink")}</p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0"
                onClick={handleCloseJoinSharePopover}
                aria-label="Close share popover"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3 rounded-lg border border-border bg-muted/60 px-3 py-2">
              <p className="truncate text-xs text-foreground">{meetingShareUrl}</p>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                onClick={handleCopyJoinShareLink}
              >
                {joinShareCopied ? <Check className="mr-1.5 h-4 w-4 text-green-500" /> : <Copy className="mr-1.5 h-4 w-4" />}
                {joinShareCopied ? t("meetingScreen.copied") : t("meetingScreen.copy")}
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-9"
                onClick={() => {
                  handleOpenShare(meetingShareUrl)
                  handleCloseJoinSharePopover()
                }}
              >
                <Share2 className="mr-1.5 h-4 w-4" />
                {t("meetingScreen.share")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        url={shareUrl}
      />

    </div>
  )
})

interface MeetingScreenProps {
  meetingInfo: MeetingInfo
  onLeaveMeeting: (reason?: "left" | "ended" | "removed") => void
  localParticipant?: Participant
  participants?: Participant[]
}

// Plays a remote peer's audio via a hidden, UNMUTED <audio> element.
// The <video> tiles are always muted (and unmount when off-page/paginated), so
// all remote audio flows through these dedicated players, rendered at the
// MeetingScreen level — off-page and camera-off peers stay audible for the
// whole meeting. Bound to the peer's audio tracks alone (useTrackStream), so it
// re-binds only when those change — not on mute toggles, not when video comes
// or goes.
type AudioElementWithSinkId = HTMLAudioElement & {
  setSinkId?: (sinkId: string) => Promise<void>
}

const RemoteAudioPlayer = memo(function RemoteAudioPlayer({
  peerId,
  stream,
}: { peerId: string; stream: MediaStream | null }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  // Only the audio tracks, re-bound only when THEY change. The peer's stream
  // also carries their camera, and binding to it dropped their voice every
  // time their video track came or went.
  const audioStream = useTrackStream(stream, 'audio')
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId)
  // Subscribed per peer, so turning one person down re-renders their player
  // and nobody else's.
  const volume = useParticipantVolumeStore(
    (s) => s.volumes[peerId] ?? DEFAULT_PARTICIPANT_VOLUME,
  )
  const incomingAudioPaused = useMeetingSessionStore((s) => s.areIncomingAudiosPaused)

  // Route the meeting to the chosen output.
  //
  // Selecting a speaker used to affect nothing but the lobby's TEST TONE — the
  // only `setSinkId` in the app was in speaker-test.service. So the test played
  // through your headset, proving the choice had taken, and then every voice in
  // the meeting came out of the laptop.
  //
  // Separate effect from the stream binding so changing output does not re-bind
  // the stream, and so it re-applies when a peer's player mounts later.
  useEffect(() => {
    const el = audioRef.current as AudioElementWithSinkId | null
    if (!el || !selectedSpeakerId) return
    // Absent in Firefox and in Safari, where output follows the OS and there is
    // nothing to do. Not an error, so it is not reported as one.
    if (typeof el.setSinkId !== 'function') return
    el.setSinkId(selectedSpeakerId).catch(() => {
      // The default sink still plays, which beats silence — but the user chose
      // a speaker and is not getting it, and saying nothing is how "everyone
      // comes out of the laptop" became something you had to ask about.
      //
      // Deduped inside the reporter: once per chosen device, not once per peer.
      notifySpeakerRoutingFailure(selectedSpeakerId)
    })
  }, [selectedSpeakerId, audioStream])

  // Personal mix, applied to the element rather than the track: it costs no
  // Web Audio graph, survives the track being replaced, and is invisible to
  // everyone else — the peer keeps sending exactly what they were sending.
  //
  // Its own effect, so changing a volume never re-binds the stream. Re-runs on
  // the bound stream too, because a player that mounts or re-binds later starts at the
  // element's default of 1 and would otherwise ignore a level set earlier.
  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.volume = volume
  }, [volume, audioStream])

  // "Pause all incoming audio", locally. The real pause is at the SFU
  // (useConsumers); this is the belt-and-braces half — silent the instant the
  // toggle flips rather than when the jitter buffer drains, and still silent if
  // a pause RPC was lost to a socket blip.
  //
  // `muted`, never `volume` (that is the personal mix above, and iOS ignores
  // it) and never pause() / clearing srcObject: re-starting would go back
  // through autoplay recovery. Unmuting only ever follows a click on the
  // toggle, so it has the user activation autoplay asks for.
  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.muted = incomingAudioPaused
  }, [incomingAudioPaused, audioStream])

  useEffect(() => {
    const el = audioRef.current
    if (!el || !audioStream) return
    el.srcObject = audioStream
    // Read, not subscribed: the effect above keeps it current, and making the
    // flag a dependency here would re-bind the stream on every toggle.
    el.muted = useMeetingSessionStore.getState().areIncomingAudiosPaused

    // Gateway peers (H.323 / SIP / mixer) are rare and are exactly the ones we
    // keep having to diagnose, so their audio path reports unconditionally —
    // no debug flag to remember, and no noise for ordinary peers. A swallowed
    // play() rejection here is indistinguishable from "no audio arrived", which
    // is the ambiguity this removes.
    const diag = /^(h323_|sip_)/.test(peerId) || peerId === 'mixer'
    if (diag) {
      const a = audioStream.getAudioTracks()
       
      console.info('[h323-audio] binding', {
        peerId,
        audioTracks: a.length,
        trackState: a.map((t) => ({ id: t.id.slice(0, 8), enabled: t.enabled, muted: t.muted, readyState: t.readyState })),
      })
    }

    // Every peer, not just the gateways. A refused play() is silence, and the
    // gateway branch's own comment — that a swallowed rejection is
    // indistinguishable from "no audio arrived" — was always true of everybody.
    playWithAutoplayRecovery(el, `peer ${peerId}`)
    return () => {
      // Drop it from the retry set first: a gesture arriving after this player
      // unmounts must not call play() on an element whose stream has gone.
      forgetBlockedAudio(el)
      el.pause()
      el.srcObject = null
    }
  }, [audioStream, peerId])
  return <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
})

function makeDefaultLocalParticipant(meetingInfo: MeetingInfo): Participant {
  return {
    id: "you",
    name: meetingInfo.userName,
    muted: false,
    videoOn: true,
    active: true,
    handRaised: false,
    role: "host",
    isLocal: true,
    isSpeaking: false,
    isScreenSharing: false,
  }
}

export function MeetingScreen(props: MeetingScreenProps) {
  const {
    connected, sfuReady, remotePeers, peerId, activeSpeakerId, speakingId, subscribe, sendEvent, request, channelJoined, error,
    roomId, localStream, publishScreenShare, unpublishScreenShare, screenSharePending,
    localVideoInputs, publishVideoInput, unpublishVideoInput, unpublishAllVideoInputs,
  } = useMeetingConnection()

  // Live, multi-peer, server-side captions — gated on the room channel's
  // existing broadcast relay, so it belongs next to useActiveSpeakers, the
  // other hook consuming a custom room-channel event. The outbound mic
  // upload this depends on is started separately, inside MeetingScreenInner,
  // which is where the meeting's own transcriptionEnabled setting lives.
  const liveCaptions = useLiveCaptions(subscribe)

  // An embedding application hears how the connections stand. No-op unframed.
  useEmbedConnectionEvents({ connected, channelJoined, sfuReady, error })

  // And so does the person in the meeting. Computed here, outside the memoized
  // MeetingScreenInner, so a connection flapping re-renders one banner rather
  // than the whole meeting.
  const reconnecting = useReconnectBanner({ roomId, channelJoined, sfuReady, error })

  // The screen stays on for the whole meeting. On a phone it used to lock after
  // the screen timeout whenever our camera was off — mic on or not.
  useScreenWakeLock()

  // Every `toast.*` call in the app (recording, hands, background status, and
  // the ~60 other call sites outside this feature) is redirected here while a
  // meeting is on screen, so its notices stack above the control bar instead
  // of sitting on top of it. Set once on mount and cleared on unmount: nothing
  // else should be able to leave the sink pointed at a screen that is gone,
  // which would otherwise send the dashboard's own toasts into a detached
  // store no one is reading.
  useEffect(() => {
    const sink: ToastSink = {
      push: (notice) => useMeetingNoticesStore.getState().pushOrdinary(notice),
      dismiss: (id) => {
        if (id === undefined) useMeetingNoticesStore.getState().clearOrdinary()
        else useMeetingNoticesStore.getState().dismissOrdinary(id)
      },
    }
    setToastSink(sink)
    return () => {
      setToastSink(null)
      // The store outlives the screen; the next meeting starts with none.
      useMeetingNoticesStore.getState().reset()
    }
  }, [])

  // Real roles from the signaling server. The local participant used to be
  // hardcoded as host, which is why every user saw "Host" under their own name
  // and every moderator gate was open to everyone.
  const roles = useRoomRoles({ peerId, subscribe, sendEvent, channelJoined })

  // Moderator actions on the wire, plus what to do when one lands on us.
  // Forcing micOn/camOn false is enough: useLocalMedia already pauses both the
  // local track and the SERVER producer off those flags, so remote peers
  // actually see the change (a local producer.pause() alone never reaches the SFU).
  // Filled in by MeetingScreenInner; see onForceStopScreenShare below.
  const stopLocalShareRef = useRef<(() => boolean) | null>(null)
  const setMicOn = useMeetingSessionStore((s) => s.setMicOn)
  const setCamOn = useMeetingSessionStore((s) => s.setCamOn)
  const t = useTranslation()
  // Read by the moderation handlers, which are built once and would otherwise
  // see the roster as it was on the first render.
  const remotePeersRef = useRef(remotePeers)
  remotePeersRef.current = remotePeers
  // Read the same way: how many extra cameras are live when a stop lands.
  const localVideoInputsRef = useRef(localVideoInputs)
  localVideoInputsRef.current = localVideoInputs
  // Each force command runs for everyone it reaches, but is only ANNOUNCED to
  // someone it changed something for — see forcedActionChanged. "Mute all"
  // reaches the whole room, and told people who were already muted that the
  // host had just muted them.
  const forcedState = (): ForcedMediaState => {
    const session = useMeetingSessionStore.getState()
    return {
      micOn: session.micOn,
      camOn: session.camOn,
      extraCameras: localVideoInputsRef.current.length,
      sharing: false,
    }
  }
  const moderationHandlers = useMemo(
    () => ({
      // A host muting this person individually also takes back any device
      // they had personally granted: the person is under the room's rule again.
      onForceMute: () => {
        const changed = forcedActionChanged('mute', forcedState())
        useDeviceGrantStore.getState().revoke('audio')
        setMicOn(false)
        if (changed) meetingAlert(t('meetingScreen.alertMutedByHost'), { personal: true })
        // Muting the mic silences a shared tab's audio too (useMeetingConnection).
      },
      // Stopping someone's video has to stop *all* of it. `camOn` only pauses
      // the primary camera producer, so any additional camera would have kept
      // streaming after a moderator turned the person's video off.
      onForceStopVideo: () => {
        const changed = forcedActionChanged('stop_video', forcedState())
        useDeviceGrantStore.getState().revoke('video')
        setCamOn(false)
        unpublishAllVideoInputs()
        if (changed) meetingAlert(t('meetingScreen.alertCameraStoppedByHost'), { personal: true })
      },
      // `unpublishScreenShare` only frees the SFU producers. The local capture
      // lives in the inner component's `useScreenShare` state, so stopping it
      // needs a handle the inner registers — otherwise `screenOn`/`screenStream`
      // stay live, the presenter keeps watching a share nobody receives, and
      // the view never falls back. The handle says whether a share was running.
      onForceStopScreenShare: () => {
        const sharing = stopLocalShareRef.current?.() ?? false
        unpublishScreenShare()
        if (forcedActionChanged('stop_screenshare', { ...forcedState(), sharing })) {
          meetingAlert(t('meetingScreen.alertShareStoppedByHost'), { personal: true })
        }
      },
      onMeetingEnded: () => props.onLeaveMeeting('ended'),
      onRemoved: () => props.onLeaveMeeting('removed'),
      // An ask, not a command. Turning a participant's mic or camera on
      // remotely is not a moderator's to make, so this prompts and leaves the
      // decision (and the action) with them.
      //
      // A dialog that waits for an answer (HostRequestDialog), not a toast: a
      // toast went after a few seconds, so the ask was easy to miss, and it was
      // English-only. A view-only attendee cannot publish at all, so they are
      // not offered a button that would do nothing.
      onMediaRequested: (kind: 'audio' | 'video', by: string | null) => {
        if (useMeetingSessionStore.getState().viewOnly) return
        setMediaRequest({ kind, by })
      },
      // Delivered to EVERY peer: the channel cannot address moderators only,
      // so it is queued regardless and the dialog that reads this
      // queue is rendered for moderators alone.
      onPresenterRequested: (
        requesterId: string,
        name: string,
        subject: PresenterRequestSubject,
        details: PresenterRequestDetails,
      ) => {
        usePresenterRequestsStore.getState().add({
          peerId: requesterId,
          name,
          subject,
          // Only for a share session, so every other ask is stored as it was.
          ...(details.shareOnly ? { shareOnly: true, email: details.email } : {}),
        })
      },
      onPresenterSubject: (requesterId: string, subject: PresenterRequestSubject) => {
        usePresenterRequestsStore.getState().noteSubject(requesterId, subject)
      },
      onPresenterDenied: () => {
        usePresenterRequestsStore.getState().setMyOutcome('rejected')
      },
      onPresenterAnswered: (answeredPeerId: string) => {
        usePresenterRequestsStore.getState().remove(answeredPeerId)
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMicOn, setCamOn, unpublishScreenShare, unpublishAllVideoInputs, props.onLeaveMeeting, t],
  )
  const moderation = useModeration({ peerId, sendEvent, subscribe, handlers: moderationHandlers })

  // The host's ask to unmute or turn the camera on, while it waits for an answer.
  const [mediaRequest, setMediaRequest] = useState<{ kind: 'audio' | 'video'; by: string | null } | null>(null)
  // Accepting is the host's say-so for this person's device, so the room's
  // mic and camera switches do not turn it straight back off (device grant).
  const acceptMediaRequest = useCallback(() => {
    const req = mediaRequest
    setMediaRequest(null)
    if (!req) return
    useDeviceGrantStore.getState().grant(req.kind)
    if (req.kind === 'audio') setMicOn(true)
    else setCamOn(true)
  }, [mediaRequest, setMicOn, setCamOn])
  const declineMediaRequest = useCallback(() => setMediaRequest(null), [])

  // A screen-share page's pending ask goes when the page does (contract §8).
  useShareAskerDepartures(remotePeers)

  // Stable, so MeetingScreenInner's memo holds: an inline arrow here was a new
  // prop on every render of this component and re-rendered the whole meeting.
  const registerStopScreenShare = useCallback((fn: () => boolean) => {
    stopLocalShareRef.current = fn
  }, [])
  const micOn = useMeetingSessionStore((s) => s.micOn)
  const camOn = useMeetingSessionStore((s) => s.camOn)
  const areIncomingVideosPaused = useMeetingSessionStore((s) => s.areIncomingVideosPaused)

  // An attendee who joins after the host has switched cameras off must not
  // arrive on camera.
  //
  // Closing the gate stops the cameras running at that moment (`stopAllVideos`
  // in MeetingScreenInner), but that is a one-off command. Someone joining later
  // still turned on the camera they picked on the preview, and only the button
  // knew the rule: it greyed out while their video kept flowing, until they
  // clicked it and were told the host had disabled the camera.
  //
  // Waits until the room has told us BOTH its controls and our role. The store
  // keeps the previous meeting's flags until `host:controls` lands, and everyone
  // reads as an attendee until `roles:updated` does, so acting any earlier could
  // switch off a host's camera.
  const hostAllowsVideo = useMeetingHostControlsStore((s) => s.useVideo)
  const [hostControlsHeard, setHostControlsHeard] = useState(false)
  useEffect(() => subscribe('host:controls', () => setHostControlsHeard(true)), [subscribe])
  // Heard at all, not "my id is in it": the room lists only hosts and
  // co-hosts, so waiting for an attendee's own entry meant this never ran for
  // the very people it is for, and a reload with the camera on beat the switch.
  const myRoleHeard = roles.heard
  // The host personally asked for this camera (an accepted Request Video), so
  // the room switch does not apply to it.
  const camGranted = useDeviceGrantStore((s) => s.video)
  // A new meeting starts under the room's rules; a grant from the last one
  // must not follow the person in.
  useEffect(() => { useDeviceGrantStore.getState().clear() }, [])
  useEffect(() => {
    if (!hostControlsHeard || !myRoleHeard) return
    if (roles.isModerator || hostAllowsVideo || camGranted) return
    if (!camOn && localVideoInputs.length === 0) return
    setCamOn(false)
    unpublishAllVideoInputs()
    // Extra cameras still listed here with the primary already off is the tail
    // of a stop that was just announced ("Stop all videos" lands first and
    // unpublishing them is async), not a second thing to tell anyone.
    if (camOn) toast.info({ title: t('controlBar.cameraDisabledByHost') })
  }, [
    hostControlsHeard, myRoleHeard, roles.isModerator, hostAllowsVideo, camGranted,
    camOn, localVideoInputs.length, setCamOn, unpublishAllVideoInputs, t,
  ])

  // The same rule for the microphone, which had no join check at all: someone
  // who joined (or reloaded) with the mic on stayed live while the host's
  // switch said nobody could be.
  const hostAllowsMicrophone = useMeetingHostControlsStore((s) => s.useMicrophone)
  const micGranted = useDeviceGrantStore((s) => s.audio)
  useEffect(() => {
    if (!hostControlsHeard || !myRoleHeard) return
    if (roles.isModerator || hostAllowsMicrophone || micGranted) return
    if (!micOn) return
    setMicOn(false)
    toast.info({ title: t('controlBar.microphoneDisabledByHost') })
  }, [
    hostControlsHeard, myRoleHeard, roles.isModerator, hostAllowsMicrophone, micGranted,
    micOn, setMicOn, t,
  ])

  // Room lock + who is waiting for admission. Host-only; for everyone else the
  // hook is inert (the endpoints are host-gated) and returns an empty list.
  // The lock is live session state, so it is read from the server rather than
  // from the meeting record — a fresh room is always unlocked.
  const lobby = useLobby({
    meetingId: props.meetingInfo.meetingCode || null,
    // Host AND co-hosts, and nobody when the roster has not resolved.
    //
    // This read `!props.localParticipant || role === 'host'`, which was wrong
    // in both directions: the first clause is TRUE before this client's roster
    // row arrives, so an ordinary attendee was handed the lobby feed, and
    // `=== 'host'` excluded co-hosts, who are supposed to be able to admit.
    isHost: canManageLobby(roles),
    subscribe,
  })

  // Every remote peer sharing right now. Several can share at once — the SFU
  // consumes each screen producer independently — and taking only the first
  // meant the rest were decoded and never viewable.
  const remoteScreens = useMemo(() => {
    const out: { peerId: string; name: string; stream: MediaStream }[] = []
    for (const p of remotePeers.values()) {
      if (p.screenStream) out.push({ peerId: p.peerId, name: p.name, stream: p.screenStream })
    }
    return out
  }, [remotePeers])

  // In-meeting chat rides the room channel that is already open for signaling —
  // no second socket, no SDK session, nothing shared with the app messenger.
  // Null until the channel joins, which keeps chat inert rather than pretending
  // to send into a connection that isn't there.
  const chatChannel = useMemo<ChatChannel | null>(
    () => (channelJoined ? { sendEvent, request, subscribe } : null),
    [channelJoined, sendEvent, request, subscribe],
  )

  // Kept for the layout paths that still ask "is anyone remote sharing"; the
  // stage itself selects across local + remote from `remoteScreens`.
  const remoteScreen = useMemo(
    () => (remoteScreens.length ? remoteScreens[0] : null),
    [remoteScreens],
  )

  // Build local participant from auth + session state
  const localParticipant = useMemo<Participant>(() => {
    if (props.localParticipant) return props.localParticipant
    return {
      id: peerId ?? 'you',
      name: props.meetingInfo.userName,
      muted: !micOn,
      videoOn: camOn,
      active: true,
      handRaised: false,
      role: roles.participantRole(peerId ?? undefined),
      isLocal: true,
      // Speaking means SPEAKING, not "the microphone is not muted".
      //
      // This was `micOn`, so your own tile counted as speaking for as long as
      // you were unmuted — which is most of a meeting. VideoTile turns that into
      // an infinite animation on the tile, so a single participant sitting in an
      // empty room ran a 2.5s loop forever, and any running animation pins the
      // whole window to a 60Hz composite loop: measured at ~52% of a core
      // (GPU 23%->6%, WindowServer 41%->12%) with animations disabled.
      //
      // `speakingId` is the SFU's AudioLevelObserver talking, debounced by
      // useActiveSpeakers, and it watches our own producer like everyone else's.
      // Not `activeSpeakerId`: that one stays on the last speaker through
      // silence, so the ring stayed lit after you had stopped talking.
      isSpeaking: micOn && !!peerId && speakingId === peerId,
      isScreenSharing: false,
      sfuPeerId: peerId ?? undefined,
    }
  }, [props.localParticipant, props.meetingInfo.userName, micOn, camOn, peerId, roles, speakingId])

  // Build remote participants from the Presence roster. Name comes from
  // Presence; the stream is attached once its SFU consumer arrives (until then
  // stream is undefined + videoOn false → the tile shows the avatar placeholder,
  // same as a local participant with the camera off).
  //
  // Not gated on the SFU. Presence is the roster and is known as soon as the
  // room channel joins; hiding it until media was ready drew you alone and then
  // dropped the whole room in at once, and emptied the grid on every SFU
  // reconnect. People appear as avatars and their video arrives into a tile
  // that is already in its final place.
  const participants = useMemo<Participant[]>(() => {
    if (props.participants) return props.participants
    return Array.from(remotePeers.values()).map((peer) => {
      // The peer's own report wins for mic and camera STATE; `videoOn` stays
      // what we receive, because that is what the tile can actually draw.
      const { muted, cameraOn } = rosterMedia(peer.reported, peer)
      return {
        id: peer.peerId,
        name: peer.name,
        muted,
        videoOn: peer.videoOn,
        cameraOn,
        active: !muted,
        // Was hardcoded false, so a raised hand was invisible to everyone but the
        // person who raised it. Comes from Presence now, which also means a late
        // joiner sees hands that went up before they arrived.
        handRaised: peer.handRaised,
        role: roles.participantRole(peer.peerId),
        ...(roles.hasAnnotatorGrant(peer.peerId) ? { annotationAccess: true } : {}),
        isLocal: false,
        // The same rule as your own tile: the active speaker while audible and
        // unmuted. `peer.isSpeaking` is set false when the peer is created and
        // nothing ever updates it, so a remote speaker's tile never lit up.
        isSpeaking: !muted && speakingId === peer.peerId,
        // Was hardcoded false, so a peer who was actually sharing never offered
        // "Stop screen share". The connection already tracks this per peer.
        isScreenSharing: peer.isSharingScreen,
        stream: peer.stream ?? undefined,
        sfuPeerId: peer.peerId,
        videoSourceType: peer.videoSourceType,
        // Only when true, so every other row is exactly what it was.
        ...(peer.shareOnly ? { shareOnly: true } : {}),
      }
    })
  }, [props.participants, remotePeers, roles, speakingId])

  // Extra cameras — mine and everyone else's — as their own tiles.
  //
  // These are NOT roster rows: they get a grid slot and nothing else, so the
  // People panel, participant counts, breakout assignment and moderation keep
  // seeing exactly one entry per human. `id` is namespaced by owner + producer
  // slot so it can never collide with a real peer id.
  const additionalSources = useMemo<Participant[]>(() => {
    const rows: Participant[] = []
    const makeRow = (
      owner: { id: string; name: string; role: Participant['role']; isLocal: boolean },
      src: { sourceId: string; label: string; stream: MediaStream | null; active?: boolean },
    ): Participant => ({
      id: `${owner.id}::${src.sourceId}`,
      name: owner.name,
      muted: true,
      // "Pause all incoming videos" covers extra cameras too — otherwise a
      // remote document cam would keep playing while every other tile went to
      // an avatar. Your own extra cameras are a local capture that costs no
      // inbound bandwidth, so they stay visible — while they are capturing.
      // An input added with the camera off, or suspended by turning it off,
      // has no stream, and a local tile with no stream of its own would fall
      // back to MediaStreamContext and show your PRIMARY camera; `active` is
      // what keeps it on the avatar.
      //
      // A remote input with no stream yet is one the SFU announced and nobody
      // has asked for (LAZY_CONSUME), or one reaped while off screen. Its tile
      // still has to exist: the tile retaining its key is the only thing that
      // makes lastN request the stream. Until it lands the tile is an avatar.
      videoOn: owner.isLocal ? !!src.active : !areIncomingVideosPaused,
      active: true,
      handRaised: false,
      role: owner.role,
      isLocal: owner.isLocal,
      isSpeaking: false,
      isScreenSharing: false,
      stream: src.stream ?? undefined,
      sourceKind: 'additional',
      sourceLabel: src.label,
      ownerId: owner.id,
      sourceId: src.sourceId,
    })

    const selfId = peerId ?? 'you'
    for (const input of localVideoInputs) {
      rows.push(makeRow(
        { id: selfId, name: props.meetingInfo.userName, role: roles.participantRole(peerId ?? undefined), isLocal: true },
        input,
      ))
    }
    for (const peer of remotePeers.values()) {
      for (const src of peer.aux) {
        rows.push(makeRow(
          { id: peer.peerId, name: peer.name, role: roles.participantRole(peer.peerId), isLocal: false },
          src,
        ))
      }
    }
    return rows
  }, [localVideoInputs, remotePeers, peerId, props.meetingInfo.userName, roles, areIncomingVideosPaused])

  // Everything whose audio belongs in a local recording: your mic plus every
  // remote peer. The old implementation captured display audio and so recorded
  // the meeting without the recorder's own voice.
  const recordingAudioStreams = useMemo<MediaStream[]>(() => {
    const out: MediaStream[] = []
    if (localStream) out.push(localStream)
    for (const peer of remotePeers.values()) {
      if (peer.stream) out.push(peer.stream)
    }
    return out
  }, [localStream, remotePeers])

  // Local recording never touches the server, so the room only learns about it
  // if the recorder says so. Nobody should be recorded without an indicator.
  const handleRecordingStateChange = useCallback((isRecording: boolean) => {
    if (!channelJoined) return
    sendEvent(
      isRecording ? 'media:recording_started' : 'media:recording_stopped',
      isRecording ? { kind: 'local', name: props.meetingInfo.userName } : { kind: 'local' },
    )
  }, [channelJoined, sendEvent, props.meetingInfo.userName])

  // Cloud recording lives on the server, but the room's REC indicator still
  // needs telling: a late joiner's status fetch will catch up eventually, but
  // a live start/stop should not wait on that alone.
  const handleCloudRecordingChanged = useCallback((active: boolean) => {
    if (!channelJoined) return
    sendEvent(
      active ? 'media:recording_started' : 'media:recording_stopped',
      active ? { kind: 'cloud', name: props.meetingInfo.userName } : { kind: 'cloud' },
    )
  }, [channelJoined, sendEvent, props.meetingInfo.userName])

  // Who in the room is recording. Local recording writes to one person's disk,
  // so nothing server-side knows about it — the indicator only exists because
  // the recorder announces it and everyone reflects it back.
  const [localIsRecording, setLocalIsRecording] = useState(false)
  // Bumped on every cloud start/stop announcement from someone else, so Inner
  // knows to re-check the server rather than trust a stale mount-time fetch.
  const [cloudSignal, setCloudSignal] = useState(0)
  const handleCloudChange = useCallback(() => {
    setCloudSignal((n) => n + 1)
  }, [])
  const recordingNotice = useRecordingNotice(subscribe, channelJoined, localIsRecording, handleCloudChange)

  // Virtual background changes mid-meeting take a moment (an image to fetch,
  // segmentation to warm) and used to happen in silence. One toast, updated in
  // place: "Applying…" if it is not instant, then the outcome.
  useEffect(() => {
    const id = 'virtual-background-status'
    let slow: ReturnType<typeof setTimeout> | null = null
    const off = onBackgroundStatus((status) => {
      if (slow) {
        clearTimeout(slow)
        slow = null
      }
      if (status === 'applying') {
        // A swap on a running pipeline finishes before this would show.
        slow = setTimeout(() => toast.pending({ id, title: t('dialogs.applyingBackground'), duration: Infinity }), 300)
        return
      }
      if (status === 'applied') toast.success({ id, title: t('dialogs.backgroundApplied'), duration: 2500 })
      else if (status === 'removed') toast.success({ id, title: t('dialogs.backgroundRemoved'), duration: 2500 })
      else toast.error({ id, title: t('dialogs.backgroundApplyFailed'), description: t('dialogs.backgroundApplyFailedDesc'), duration: 6000 })
    })
    return () => {
      off()
      if (slow) clearTimeout(slow)
    }
  }, [t])

  // Join / leave, and the room's other comings and goings — see the two hooks.
  // Hosts and co-hosts are always told a screen-share session joined.
  useParticipantAlerts(subscribe, peerId ?? null, roles.isModerator)
  useMeetingEventAlerts({
    subscribe,
    channelJoined,
    peers: remotePeers,
    myRole: roles.myRole,
    canPresent: roles.canPresent,
    isModerator: roles.isModerator,
    lobbyRequests: lobby.requests,
    locked: lobby.locked,
    recorderNames: recordingNotice.recorderNames,
    cloudRecording: recordingNotice.cloudAnnounced,
  })

  const handleRecordingStateChangeOuter = useCallback((isRecording: boolean) => {
    setLocalIsRecording(isRecording)
    handleRecordingStateChange(isRecording)
  }, [handleRecordingStateChange])

  // A recorder who starts before someone else joins never sends them the
  // original announcement: Phoenix broadcasts do not replay. Re-announce to
  // whoever just arrived so a local recording in progress is never silent for
  // a late joiner.
  const localIsRecordingRef = useRef(localIsRecording)
  localIsRecordingRef.current = localIsRecording
  // Same problem as local recording, but for cloud: a guest who joins after
  // the announcement went out (Phoenix broadcasts do not replay) has no other
  // way to learn the meeting is being recorded, since their status fetch has
  // no token to send. Mirrors ownCloudRecording from Inner without hoisting
  // Inner's whole cloud-recording state up here.
  const ownCloudActiveRef = useRef(false)
  const handleOwnCloudRecordingChange = useCallback((active: boolean) => {
    ownCloudActiveRef.current = active
  }, [])
  useEffect(() => {
    const off = subscribe('presence_diff', (payload) => {
      const diff = payload as { joins?: Record<string, unknown> }
      const joinKeys = Object.keys(diff.joins ?? {})
      const hasNewcomer = joinKeys.some((id) => id !== peerId)
      if (!hasNewcomer) return
      if (localIsRecordingRef.current) {
        sendEvent('media:recording_started', { kind: 'local', name: props.meetingInfo.userName })
      }
      if (ownCloudActiveRef.current) {
        sendEvent('media:recording_started', { kind: 'cloud', name: props.meetingInfo.userName })
      }
    })
    return off
  }, [subscribe, sendEvent, peerId, props.meetingInfo.userName])

  // Only recorders who were not recording before (newRecorders). Comparing the
  // whole list re-announced everyone already recording when one more started,
  // and announced the ones left as STARTING when another of them stopped.
  const announcedRecordersRef = useRef<readonly string[]>([])
  useEffect(() => {
    const started = newRecorders(announcedRecordersRef.current, recordingNotice.recorderNames)
    announcedRecordersRef.current = recordingNotice.recorderNames
    if (started.length > 0) {
      toast.info({
        title: 'This meeting is being recorded',
        description: `${started.join(', ')} started a local recording.`,
      })
    }
  }, [recordingNotice.recorderNames])

  // Debug overlay for connection state (dev-only)
  const showDebug = import.meta.env.DEV && !connected && !sfuReady

  usePrefetchPanels()

  return (
    <UIStateProvider>
      {/* Always-on hidden audio for every remote peer — lives outside VideoGrid
          so pagination / camera-off never silences anyone. */}
      {Array.from(remotePeers.values()).map((peer) => (
        <RemoteAudioPlayer key={peer.peerId} peerId={peer.peerId} stream={peer.stream} />
      ))}
      <ReconnectingBanner reason={reconnecting} />
      {showDebug && error && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg bg-destructive/90 px-4 py-2 text-sm text-white shadow-lg">
          SFU: {error}
        </div>
      )}
      <VideoInputProvider onRemoveVideoInput={unpublishVideoInput}>
      <SafeSuspense>
        <MeetingScreenInner
          registerStopScreenShare={registerStopScreenShare}
          meetingInfo={props.meetingInfo}
          onLeaveMeeting={props.onLeaveMeeting}
          liveCaptions={liveCaptions}
          localParticipant={localParticipant}
          participants={participants}
          additionalSources={additionalSources}
          activeSpeakerId={activeSpeakerId}
          roles={roles}
          moderation={moderation}
          lobby={lobby}
          remoteScreen={remoteScreen}
          remoteScreens={remoteScreens}
          publishScreenShare={publishScreenShare}
          unpublishScreenShare={unpublishScreenShare}
          screenSharePending={screenSharePending}
          recordingAudioStreams={recordingAudioStreams}
          onRecordingStateChange={handleRecordingStateChangeOuter}
          someoneElseIsRecording={recordingNotice.recorderNames.length > 0}
          onCloudRecordingChanged={handleCloudRecordingChanged}
          cloudRecordingSignal={cloudSignal}
          cloudAnnounced={recordingNotice.cloudAnnounced}
          cloudStartedBy={recordingNotice.cloudStartedBy}
          onOwnCloudRecordingChange={handleOwnCloudRecordingChange}
          localVideoInputs={localVideoInputs}
          publishVideoInput={publishVideoInput}
          unpublishVideoInput={unpublishVideoInput}
          chatChannel={chatChannel}
          localPeerId={peerId}
          localStream={localStream}
        />
      </SafeSuspense>
      </VideoInputProvider>
      <HostRequestDialog
        open={mediaRequest !== null}
        type={mediaRequest?.kind ?? 'audio'}
        hostName={(mediaRequest?.by && remotePeers.get(mediaRequest.by)?.name) || undefined}
        onAccept={acceptMediaRequest}
        onDecline={declineMediaRequest}
      />
    </UIStateProvider>
  )
}
